// ─── Locale types & config ────────────────────────────────
export type Locale = 'fr' | 'en-gb' | 'de' | 'es' | 'it';

export const SUPPORTED_LOCALES: Locale[] = ['fr', 'en-gb', 'de', 'es', 'it'];

export const LOCALE_META: Record<Locale, { label: string; flag: string; htmlLang: string; group: string }> = {
  fr:    { label: 'Français',  flag: '🇫🇷', htmlLang: 'fr',    group: 'Western Europe' },
  'en-gb': { label: 'English (UK)', flag: '🇬🇧', htmlLang: 'en-GB', group: 'Western Europe' },
  de:    { label: 'Deutsch',   flag: '🇩🇪', htmlLang: 'de',    group: 'Western Europe' },
  es:    { label: 'Español',   flag: '🇪🇸', htmlLang: 'es',    group: 'Southern Europe' },
  it:    { label: 'Italiano',  flag: '🇮🇹', htmlLang: 'it',    group: 'Southern Europe' },
};

export const DEFAULT_LOCALE: Locale = 'fr';
export const FALLBACK_CHAIN: Locale[] = ['en-gb', 'fr'];

// ─── Translation dictionary type ──────────────────────────
type TranslationDict = Record<string, string>;

// ─── FR (master locale) ──────────────────────────────────
const fr: TranslationDict = {
  // Nav
  'nav.shop': 'Shop',
  'nav.displays': 'Displays',
  'nav.boosters': 'Boosters',
  'nav.lots': 'Full set',
  'nav.case': 'Carton de display',
  'nav.decks': 'Decks',
  'nav.loyalty': 'Fidélité',
  'nav.promo': 'Promo',
  'promo.title': 'Promo',
  'promo.intro': 'Les offres du moment sélectionnées par Sabaody Island. Prix et disponibilités sont mis à jour en temps réel.',
  'promo.empty': 'Aucune offre pour le moment',
  'promo.empty_hint': 'Revenez bientôt, de nouvelles offres arrivent !',
  'promo.browse_shop': 'Voir le shop',
  'seo.promo_title': 'Promo One Piece TCG — Offres du moment | Sabaody Island',
  'seo.promo_desc': 'Découvrez les offres du moment sur les produits One Piece TCG scellés chez Sabaody Island.',
  'nav.search': 'Rechercher',
  'nav.cart': 'Panier',
  'nav.menu': 'Menu',

  // Search
  'search.placeholder': 'Rechercher une carte, un set, un personnage...',
  'search.card_placeholder': 'Rechercher une carte...',

  // Hero
  'hero.badge': 'One Piece TCG — Cartes vérifiées',
  'hero.title': 'Sabaody Island',
  'hero.subtitle': 'La référence One Piece TCG en Europe',
  'hero.subtitle2': '',
  'hero.cta': 'Shop maintenant',
  'hero.new': 'Nouveautés / Restocks',

  // Sections
  'section.new': 'Nouveautés',
  'section.new_desc': "Fraîchement arrivées sur l'île",
  'section.restock': 'Restock',
  'section.restock_desc': 'De retour en stock, ne les ratez pas !',
  'section.bestsellers': 'Best-sellers',
  'section.bestsellers_desc': "Les plus populaires de l'équipage",
  'section.deals': '🔥 Offres spéciales',
  'section.deals_desc': 'Découvrez nos meilleures affaires du moment. Stock limité, capitaine !',
  'section.view_all': 'Tout voir',

  // Loyalty
  'loyalty.title': 'Programme de fidélité 🏴‍☠️',
  'loyalty.subtitle': 'Chaque achat vous rapproche du trésor. Gagnez des points, montez en grade, débloquez des récompenses.',
  'loyalty.how': 'Comment ça marche ?',
  'loyalty.step1': 'Achetez',
  'loyalty.step1_desc': 'Gagnez des Berry à chaque commande',
  'loyalty.step2': 'Accumulez',
  'loyalty.step2_desc': "Vos Berry s'ajoutent automatiquement",
  'loyalty.step3': 'Profitez',
  'loyalty.step3_desc': 'Échangez vos Berry contre des récompenses',
  'loyalty.tiers': 'Les grades',
  'loyalty.your_current_rank': 'Votre rang actuel',
  'loyalty.rewards': 'Récompenses',
  'loyalty.cta': 'Commencer à accumuler des points',
  'loyalty.teaser_title': 'Programme de fidélité 🏴‍☠️',
  'loyalty.teaser_desc': "Gagnez des points à chaque achat et débloquez des récompenses exclusives. Rejoignez l'équipage !",
  'loyalty.teaser_cta': 'Découvrir le programme de fidélité',
  'loyalty.earn_rule': '1 € dépensé = 1 Berry',
  'loyalty.reward_250': 'Livraison offerte',
  'loyalty.reward_500': '3 boosters offerts',
  'loyalty.reward_1000': '6 boosters offerts',
  'loyalty.reward_2000': '1 Display cadeau',
  'loyalty.reward_5000': '2 Displays cadeaux',
  'loyalty.reward_10000': '3 Displays cadeaux',
  'loyalty.perk_5off': '-5 % sur les displays',
  'loyalty.perk_10off': '-10 % sur les displays',
  'loyalty.perk_early': 'Accès anticipé aux sorties',
  'loyalty.rules_title': 'Règles d\'utilisation',
  'loyalty.rule_one_per_order': 'Une seule récompense utilisable par commande',
  'loyalty.rule_no_expiry': 'Vos points n\'expirent jamais',
  'loyalty.rule_account_required': 'Compte client requis pour accumuler et utiliser des points',
  'loyalty.rule_next_order': 'Les récompenses sont utilisables dès votre prochaine commande',
  'loyalty.checkout_earn': 'Vous gagnerez {n} points avec cette commande',

  // Tiers
  'tier.moussaillon': 'Moussaillon',
  'tier.pirate': 'Pirate',
  'tier.captain': 'Capitaine',
  'tier.yonko': 'Yonko',
  'tier.tenryubito': 'Tenryubito',
  'tier.roi_des_pirates': 'Roi des Pirates',

  // Account
  'account.title': 'Mon compte',
  'account.rank': 'Votre rang',
  'account.rank_current': 'Rang actuel',
  'account.berry_current': 'Berry actuels',
  'account.next_rank': 'Prochain rang',
  'account.points_to_next': 'Plus que {count} Berry avant le rang supérieur !',
  'account.max_rank_reached': 'Vous avez atteint le rang le plus élevé.',
  'account.balance': 'Berry disponibles',
  'account.lifetime_earned': 'Berry gagnés au total',
  'account.history': 'Historique',
  'account.berry_cta': 'Relevé des Berry',
  'account.berry_cta_subtitle': 'Consultez vos gains, utilisations et récompenses.',
  'account.no_transactions': 'Aucune transaction pour le moment',
  'account.redeem': 'Utiliser',
  'account.redeem_success': 'Récompense utilisée !',
  'account.redeem_next_order': 'Elle sera appliquée à votre prochaine commande',
  'account.redeem_error': 'Erreur',
  'account.logout': 'Déconnexion',
  'account.loading': 'Chargement...',
  // Berry history
  'berry_history.title': 'Mouvements Berry',
  'berry_history.empty': 'Aucun mouvement Berry pour le moment.',
  'berry_history.filter_all': 'Tous',
  'berry_history.filter_earnings': 'Gains',
  'berry_history.filter_spendings': 'Utilisations',
  'berry_history.filter_rewards': 'Récompenses',
  'berry_history.filter_orders': 'Commandes',
  'berry_history.see_more': 'Voir plus',
  'berry_history.label_welcome_bonus': 'Bonus de bienvenue',
  'berry_history.label_newsletter': 'Bonus newsletter',
  'berry_history.label_social_instagram': 'Réseaux sociaux — Instagram',
  'berry_history.label_social_facebook': 'Réseaux sociaux — Facebook',
  'berry_history.label_social_tiktok': 'Réseaux sociaux — TikTok',
  'berry_history.label_social': 'Réseaux sociaux —',
  'berry_history.label_review': 'Avis validé',
  'berry_history.label_referral_out': 'Parrainage — filleul qualifié',
  'berry_history.label_referral_in': 'Bonus parrainage — bienvenue',
  'berry_history.label_free_delivery': 'Livraison offerte',
  'berry_history.label_reward_used': 'Récompense utilisée',
  'berry_history.label_reward_generic': 'Récompense',
  'berry_history.label_order_earn': 'Commande',
  'berry_history.order_suffix': 'Commande',
  'checkout.loyalty_title': 'Points fidélité',
  'checkout.loyalty_balance': 'Votre solde',
  'checkout.free_shipping_toggle': 'Utiliser 250 Berry : Livraison offerte',
  'checkout.free_shipping_helper': 'Disponible car vous avez atteint 250 Berry. Vous pouvez l\'utiliser maintenant ou la garder.',
  'checkout.free_shipping_applied': 'Livraison offerte (250 Berry)',
  'checkout.free_shipping_remaining': 'Après utilisation',
  'checkout.rewards_title': 'Récompenses Berry',
  'checkout.reward_none': 'Ne pas utiliser de récompense',
  'checkout.reward_free_shipping': 'Livraison offerte',
  'checkout.reward_free_boosters_3': '3 boosters offerts ajoutés à la commande',
  'checkout.reward_free_boosters_6': '6 boosters offerts ajoutés à la commande',
  'checkout.reward_free_display': '1 Display cadeau ajoutée à la commande',
  'checkout.reward_free_displays_2': '2 Displays cadeaux ajoutées à la commande',
  'checkout.reward_free_displays_3': '3 Displays cadeaux ajoutées à la commande',
  'checkout.reward_insufficient': 'Solde Berry insuffisant',
  'checkout.reward_needs_display': 'Votre panier doit contenir au moins une Display',
  'checkout.reward_pick_display': 'Choisissez la Display à réduire :',
  'checkout.reward_after_use': 'Après utilisation',
  'checkout.reward_gift': '(cadeau)',
  'checkout.reward_display_discount_label': 'Réduction Display -10 %',
  'checkout.reward_select_display': 'Sélectionnez la Display à réduire',
  'checkout.reward_discount_per_unit': '-{amount} sur 1 unité',
  'checkout.gift_pick_edition': 'Choisissez vos éditions',
  'checkout.gift_available': 'Disponible',
  'checkout.gift_out_of_stock': 'Rupture',
  'checkout.gift_no_stock': 'Stock insuffisant pour cette édition',
  'checkout.gift_incomplete': 'Sélectionnez toutes les éditions pour votre cadeau',
  'checkout.gift_remaining': 'Encore {count} à choisir',
  'loyalty.reward_available': 'Récompense disponible : Livraison offerte (prochaine commande)',
  'loyalty.reward_use_at_checkout': 'Utiliser lors du checkout',

  // Stock
  'stock.in_stock': 'En stock',
  'stock.preorder': 'Précommande',
  'stock.sold_out': 'Victime de son succès',
  'stock.out_of_stock': 'Rupture',
  'stock.preorder_available': 'Précommander',
  'stock.available': 'disponible',
  'stock.available_plural': 'disponibles',
  'stock.limited': 'Quantité limitée',
  'stock.shipped_24_48': 'En stock — Expédition sous 24-48h',
  'stock.out_label': 'Rupture de stock',
  'stock.eta': 'Livraison estimée :',
  'stock.eta_on': 'Livraison estimée le',

  // Badges
  'badge.new': 'Nouveau',
  'badge.restock': 'Restock',
  'badge.bestseller': 'Best-seller',
  'badge.sealed': 'Scellé',

  // Product
  'product.from': 'Dès',
  'price.coming_soon': 'Bientôt disponible',
  'product.languages': 'langues',
  'product.choose_language': 'Choisir la langue',
  'product.choose_binder_type': 'Choisir le type de classeur',
  'product.add_to_cart': 'Ajouter au panier',
  'product.preorder': 'Précommander',
  'product.notify': 'Me prévenir',
  'product.not_found': 'Produit introuvable',
  'product.back_to_shop': 'Page précédente',
  'product.you_may_like': 'Vous aimerez aussi',
  'product.click_to_unzoom': 'Cliquez pour dézoomer',
  'product.sorted': "Pré-trié dans l'ordre numérique",
  'product.complete_collection': 'Collection complète (détails sur demande)',
  'product.lot_contents': 'Contenu du lot',
  'product.total_cards': 'Total cartes',
  'product.super_rare': 'Super Rare',
  'product.uncommon': 'Non commune',
  'product.common': 'Commune',
  'product.limited_per_order': 'Limité à {n} par commande',
  'product.state': 'État:',
  'product.sorted_nm_jp': 'Pré-trié • NM/M • Japonais',
  'benefit.pre_sorted': 'Pré-trié dans l\'ordre numérique',
  'benefit.pack_fresh': 'État sortie de booster',
  'benefit.no_display': 'Sans acheter de display',

  // Filters
  'filter.all': 'Tout',
  'filter.filters': 'Filtres',
  'filter.reset': 'Réinitialiser',
  'filter.set': 'Set / Extension',
  'filter.rarity': 'Rareté',
  'filter.language': 'Langue',
  'filter.color': 'Couleur',
  'filter.all_option': 'Tous',
  'sort.label': 'Trier par',
  'sort.newest': 'Nouveautés',
  'sort.price_asc': 'Prix : croissant',
  'sort.price_desc': 'Prix : décroissant',
  'sort.popular': 'Popularité',
  'sort.release_desc': 'Date de sortie : récent → ancien',
  'sort.release_asc': 'Date de sortie : ancien → récent',
  'sort.edition_az': 'Édition : A → Z',
  'sort.edition_za': 'Édition : Z → A',
  'sort.code_desc': 'Code : décroissant',
  'sort.code_asc': 'Code : croissant',
  'sort.stock_first': 'En stock d\'abord',
  'sort.edition_op_first': 'Édition : OP d\'abord',
  'sort.edition_prb_first': 'Édition : PRB d\'abord',
  'sort.edition_eb_first': 'Édition : EB d\'abord',
  'sort.st_asc': 'Numéro ST : ST01 → ST29',
  'sort.st_desc': 'Numéro ST : ST29 → ST01',
  'sort.out_of_stock_first': 'Rupture d\'abord',
  'sort.apply': 'Appliquer',
  'sort.reset': 'Réinitialiser',
  'results': 'résultat',
  'results_plural': 'résultats',
  'no_results': 'Aucun résultat trouvé',
  'no_results_hint': 'Essayez de modifier vos filtres',

  // Category
  'category.back': 'Page précédente',
  'category.products': 'produit',
  'category.products_plural': 'produits',
  'category.empty': 'Aucun produit dans cette catégorie',
  'category.empty_hint': 'Revenez bientôt, capitaine !',
  'catalog.load_error': 'Le catalogue n\'a pas pu être chargé. Réessayez dans un instant.',
  'catalog.retry_aria': 'Réessayer de charger le catalogue',
  'catalog.retry': 'Réessayer',
  'cat.displays': 'Displays',
  'cat.boosters': 'Boosters',
  'cat.lots': 'Full set',
  'cat.case': 'Carton de display',
  'cat.decks': 'Decks préconstruits',
  'cat.binder': 'Classeur',
  'cat.sleeves': 'Protège-cartes',
  'cat.acrylic-protectors': 'Protections acryliques',
  'cat.storage-boxes': 'Boîtes de rangement',
  'nav.accessories': 'Accessoires',
  'nav.binder': 'Classeur',
  'nav.sleeves': 'Protège-cartes',
  'nav.acrylic-protectors': 'Protections acryliques',
  'nav.storage-boxes': 'Boîtes de rangement',
  'category.coming_soon': 'Bientôt disponible',
  'category.coming_soon_hint': 'Cette catégorie arrive prochainement. Restez à l\'écoute, capitaine !',

  // Cart
  'cart.title': 'Votre panier',
  'cart.empty': 'Votre panier est vide',
  'cart.explore': 'Découvrir nos trésors',
  'cart.clear': 'Vider le panier',
  'cart.crosssell_title': 'Suggestions d\'achat',
  'cart.crosssell_subtitle': 'Complète ta commande en 1 clic',
  'cart.crosssell_add': 'Ajouter',
  'cart.crosssell_coming_soon': 'Bientôt disponible',
  'cart.summary': 'Récapitulatif',
  'cart.subtotal': 'Sous-total',
  'cart.shipping': 'Livraison',
  'cart.shipping_next': "Calculée à l'étape suivante",
  'cart.total': 'Total',
  'cart.checkout': 'Commander',
  'cart.drawer_empty': 'Votre panier est vide',
  'cart.drawer_hint': 'Explorez nos trésors pour le remplir !',
  'cart.view_full': 'Voir le panier complet',

  // Checkout
  'checkout.title': 'Commande',
  'checkout.empty': 'Panier vide',
  'checkout.back': 'Retour au panier',
  'checkout.info': 'Informations',
  'checkout.firstname': 'Prénom',
  'checkout.lastname': 'Nom',
  'checkout.email': 'Email',
  'checkout.address': 'Adresse',
  'checkout.zip': 'Code postal',
  'checkout.city': 'Ville',
  'checkout.country': 'Pays',
  'checkout.shipping': 'Livraison',
  'checkout.shipping_eu': '🇪🇺 Livraison dans toute l\'Europe',
  'checkout.payment': 'Paiement sécurisé',
  'checkout.payment_hint': 'Le paiement sera configuré ici',
  'checkout.pay': 'Procéder au paiement',
  'checkout.days': 'jours',
  'checkout.redirecting': 'Redirection en cours...',
  'checkout.stripe_redirect_hint': 'Vous serez redirigé vers Stripe pour effectuer le paiement en toute sécurité.',
  'checkout.success_title': 'Commande confirmée !',
  'checkout.success_desc': 'Merci pour votre achat. Votre commande a bien été enregistrée et vous recevrez un email de confirmation.',
  'checkout.success_orders': 'Voir mes commandes',
  'checkout.success_continue': 'Continuer le shopping',
  'checkout.order_number': 'Numéro de commande',
  'checkout.berry_earned': 'gagnés',

  // Trust
  'trust.verified': 'Cartes vérifiées',
  'trust.verified_desc': 'Authenticité garantie',
  'trust.refund': 'Satisfait ou remboursé',
  'trust.refund_desc': 'Retour sous 14 jours',
  'trust.shipping': 'Livraison Europe',
  'trust.shipping_desc': 'Expédition rapide 🇪🇺',
  'detail.trust1': 'Cartes vérifiées et authentifiées',
  'detail.trust2': 'Remboursement si non satisfait (14 jours)',
  'detail.trust3': 'Livraison Europe — Mondial Relay, Chronopost, La Poste, FedEx',
  'detail.notify_back': 'Me prévenir du retour en stock',
  'binder.subtitle': 'Disponible en 9 pochettes, 12 pochettes et toploader.',
  'binder.available': 'Disponible',
  'binder.coming_soon': 'Bientôt disponible',
  'binder.out_of_stock': 'Rupture de stock',
  'binder.notify_cta': 'Me prévenir du retour en stock',
  'binder.notify_desc': 'Ce classeur sera bientôt disponible. Activez l\'alerte pour être prévenu.',
  'binder.desc_official': 'Classeur officiel pour l\'édition',
  'binder.desc_formats': 'Formats disponibles : 9 pochettes, 12 pochettes, toploader.',
  'binder.desc_protect': 'Conçu pour ranger et protéger votre collection.',
  'binder.desc_matching': 'Design assorti à l\'édition.',
  'binder.format_9pkt': '9 pochettes',
  'binder.format_12pkt': '12 pochettes',
  'binder.format_topload': 'Toploader',
  'binder.title_prefix': 'Classeur',
  'binder.trust1': 'Accessoire compatible One Piece TCG',
  'binder.trust2': 'Remboursement sous 14 jours',
  'binder.trust3': 'Livraison Europe',
  'binder.view_product': 'Voir le classeur',
  'binder.subtitle_short': '9 / 12 pochettes · Toploader',

  // Notify
  'notify.title': 'Me prévenir',
  'notify.subtitle': 'Recevez une alerte par email',
  'notify.desc': 'Ce produit est actuellement victime de son succès. Laissez votre email pour être notifié(e) de son retour.',
  'notify.submit': 'Me prévenir du retour en stock',
  'notify.success_title': "C'est noté !",
  'notify.success_desc': 'Vous serez prévenu(e) dès que ce produit sera de retour en stock.',
  'notify.error': 'Une erreur est survenue. Veuillez réessayer.',

  // Footer
  'footer.desc': 'Votre boutique de confiance pour le One Piece TCG. Cartes vérifiées, expédition rapide en Europe.',
  'footer.nav': 'Navigation',
  'footer.info': 'Informations',
  'footer.cgv': 'Conditions générales de vente',
  'footer.refund_policy': 'Politique de remboursement',
  'footer.privacy': 'Politique de confidentialité',
  'footer.contact': 'Contact',
  'footer.shipped_from': 'Expédition depuis la France 🇫🇷',
  'footer.rights': '© 2026 Sabaody Island. Tous droits réservés.',
  'footer.payment_methods': 'Méthodes de paiement',
  'footer.shipping_partners': 'Partenaires de livraison',

  // Shop
  'shop.title': 'Shop',
  'shop.browse_categories': 'Parcourir par catégorie',
  'product.description_heading': 'Description',
  'shop.desc': 'Toutes nos cartes One Piece TCG vérifiées',

  // Locale switcher
  'locale.switch': 'Langue',

  // BubbleNav
  'bubble.title': 'Explorez nos trésors',
  'bubble.swipe_hint': 'Glissez →',

  // 404
  'notfound.title': 'Page introuvable',
  'notfound.desc': 'Oops ! Cette page n\'existe pas.',
  'notfound.back': 'Retour à l\'accueil',

  // Notify placeholder
  'notify.email_placeholder': 'votre@email.com',

  // Loyalty
  'loyalty.points': 'Berry',
  'loyalty.no_img': 'Pas d\'image',

  // Misc
  'misc.no_img': 'Pas d\'image',

  // Product navigation
  'nav.prev': 'Précédent',
  'nav.next': 'Suivant',

  // Language names (for variant selectors)
  'lang.Japonais': 'Japonais',
  'lang.Anglais': 'Anglais',
  'lang.Français': 'Français',
  'lang.JP': 'Japonais',
  'lang.EN': 'Anglais',
  'lang.FR': 'Français',
  'lang.9-Pocket': '9 pochettes',
  'lang.12-Pocket': '12 pochettes',
  'lang.Toploader': 'Toploader',

  // Condition values
  'condition.Scellé': 'Scellé',
  'condition.Neuf': 'Neuf',
  'condition.NM': 'NM',
  'condition.EX': 'EX',
  'condition.GD': 'GD',
  'condition.LP': 'LP',
  'condition.MP': 'MP',

  // Badge values (mapping from product data)
  'badge_val.Nouveau': 'Nouveau',
  'badge_val.Best-seller': 'Best-seller',
  'badge_val.Restock': 'Restock',
  'badge_val.Perfect fit': 'Perfect fit',
  'badge_val.Full set': 'Full set',
  'badge_val.Lot': 'Lot',
  'badge_val.Précommande': 'Précommande',
  'badge_val.Accessoire': 'Accessoire',

  // Sealed trust
  'sealed.chip': 'Produits scellés non trackés, non pesés',
  'sealed.badge': 'Non tracké • Non pesé',
  'sealed.detail_title': 'Garantie scellé',
  'sealed.detail_desc': 'Aucune display/booster box n\'est trackée ou pesée.',
  'sealed.checkout': 'Scellés non trackés, non pesés — Politique de confiance Sabaody Island.',

  // Auth
  'auth.login': 'Se connecter',
  'auth.signup': 'Créer un compte',
  'auth.email': 'Email',
  'auth.password': 'Mot de passe',
  'auth.forgot_password': 'Mot de passe oublié ?',
  'auth.no_account': 'Pas encore de compte ?',
  'auth.has_account': 'Déjà un compte ?',
  'auth.continue_google': 'Continuer avec Google',
  'auth.continue_apple': 'Continuer avec Apple',
  'auth.or': 'ou',
  'auth.error': 'Erreur',
  'auth.password_min': '12 caractères minimum',
  'auth.password_policy': 'Le mot de passe doit contenir au minimum 12 caractères, avec au moins une majuscule, une minuscule, un chiffre et un caractère spécial.',
  'auth.first_name': 'Prénom',
  'auth.last_name': 'Nom',
  'auth.phone': 'Téléphone (optionnel)',
  'auth.address_line1': 'Adresse',
  'auth.address_line2': 'Complément (optionnel)',
  'auth.postal_code': 'Code postal',
  'auth.city': 'Ville',
  'auth.country': 'Pays',
  'auth.shipping_info': 'Adresse de livraison',
  'auth.check_email': 'Vérifiez votre email',
  'auth.check_email_desc': 'Un lien de confirmation vous a été envoyé. Cliquez dessus pour activer votre compte.',
  'auth.back_to_login': 'Retour à la connexion',
  'auth.confirmed_title': 'Email confirmé ✅',
  'auth.confirmed_desc': 'Merci ! Votre compte Sabaody Island est maintenant activé. Vous pouvez vous connecter ou continuer vos achats.',
  'auth.confirmed_shop': 'Continuer le shopping',
  'auth.confirmed_error_title': 'Lien invalide ou expiré',
  'auth.confirmed_error_desc': 'Ce lien de confirmation n\'est plus valide. Veuillez vous inscrire à nouveau ou contacter le support.',
  'auth.confirmed_resend': 'Réessayer l\'inscription',
  'auth.confirming': 'Vérification en cours…',
  'auth.forgot_desc': 'Entrez votre email pour recevoir un lien de réinitialisation.',
  'auth.send_reset': 'Envoyer le lien',
  'auth.reset_email_sent': 'Un email de réinitialisation vous a été envoyé.',
  'auth.reset_password': 'Nouveau mot de passe',
  'auth.new_password': 'Nouveau mot de passe',
  'auth.update_password': 'Mettre à jour',
  'auth.password_updated': 'Mot de passe mis à jour !',
  'auth.login_or_signup': 'Se connecter / Créer un compte',
  'auth.newsletter_optin': 'Je souhaite m\'abonner à la newsletter',
  'auth.newsletter_berry': '+25 Berry',
  'auth.newsletter_bonus_note': 'Bonus crédité après création du compte.',
  'auth.newsletter_privacy': 'Consultez notre',
  'auth.newsletter_privacy_link': 'politique de confidentialité',
  'auth.has_referral': 'J\'ai un code parrain',
  'auth.referral_placeholder': 'SAB-XXXX',
  'auth.referral_applied': 'Code appliqué ✅',

  // Account extra
  'account.loyalty': 'Fidélité',

  // Orders
  'orders.title': 'Mes commandes',
  'orders.empty': 'Aucune commande pour le moment',
  'orders.order': 'Commande',
  'orders.not_found': 'Commande introuvable',
  'orders.items': 'Articles',
  'orders.shipping': 'Livraison',
  'orders.address': 'Adresse',
  'orders.track': 'Suivre ma commande',
  'orders.track_copy': 'Copier le numéro de suivi',
  'orders.track_copied': 'Copié !',
  'orders.track_hint': 'Entrez votre numéro de suivi et votre code postal ci-dessous pour voir l\'état de votre colis.',
  'orders.track_open_tab': 'Ouvrir le suivi dans un nouvel onglet',
  'orders.track_no_tracking': 'Le suivi sera disponible une fois votre commande expédiée.',
  'orders.track_loading': 'Chargement du suivi…',
  'orders.track_postal': 'Code postal',

  // Order statuses (business)
  'order_status.draft': 'Brouillon',
  'order_status.pending_payment': 'En attente de paiement',
  'order_status.paid': 'Payée',
  'order_status.processing': 'En préparation',
  'order_status.in_transit': 'En transit',
  'order_status.delivered': 'Livrée',
  'order_status.dispute': 'En litige',
  'order_status.return_requested': 'Retour demandé',
  'order_status.fulfilled': 'Expédiée',
  'order_status.cancelled': 'Annulée',
  'order_status.refunded': 'Remboursée',

  // Addresses
  'addresses.title': 'Mes adresses',
  'addresses.add': 'Ajouter une adresse',
  'addresses.edit': 'Modifier l\'adresse',
  'addresses.empty': 'Aucune adresse enregistrée',
  'addresses.full_name': 'Nom complet',
  'addresses.line1': 'Adresse ligne 1',
  'addresses.line2': 'Adresse ligne 2 (optionnel)',
  'addresses.phone': 'Téléphone (optionnel)',
  'addresses.set_default': 'Adresse par défaut',
  'addresses.save': 'Enregistrer',
  'addresses.fill_required': 'Veuillez remplir les champs obligatoires',

  // Guest checkout
  'checkout.contact_title': 'Contact',
  'checkout.logged_in_as': 'Connecté(e) en tant que',
  'checkout.guest_email_label': 'Email pour recevoir le suivi',
  'checkout.create_account_cta': 'Créer un compte (optionnel)',
  'checkout.address_title': 'Adresse de livraison',
  'checkout.guest_create_account_title': 'Créer un compte pour retrouver vos commandes',
  'checkout.guest_create_account_desc': 'Retrouvez vos commandes, gagnez des Berry à chaque achat et débloquez des récompenses.',

  // Settings
  'settings.title': 'Paramètres',
  'settings.change_password': 'Changer le mot de passe',
  'settings.current_password': 'Mot de passe actuel',
  'settings.new_password': 'Nouveau mot de passe',
  'settings.confirm_password': 'Confirmer le nouveau mot de passe',
  'settings.save_password': 'Mettre à jour le mot de passe',
   'settings.passwords_mismatch': 'Les mots de passe ne correspondent pas',
   'settings.passwords_mismatch_new': 'Les nouveaux mots de passe ne correspondent pas',
   'settings.current_password_wrong': 'Le mot de passe actuel est incorrect',
   'settings.password_updated_success': 'Votre mot de passe a bien été mis à jour',
  'settings.change_email': 'Changer l\'adresse email',
  'settings.new_email': 'Nouvelle adresse email',
  'settings.save_email': 'Mettre à jour l\'email',
  'settings.email_change_sent': 'Un email de confirmation a été envoyé à ta nouvelle adresse. Vérifie ta boîte de réception pour confirmer le changement.',
   'settings.email_change_error': 'Impossible de changer l\'adresse email',
   'settings.email_change_sent_hint': 'Clique sur le lien dans l\'email envoyé à ta nouvelle adresse pour finaliser le changement.',
   'settings.email_change_logout_hint': 'Tu as été déconnecté(e) par sécurité. Confirme le changement via l\'email envoyé à ta nouvelle adresse, puis connecte-toi avec celle-ci.',
  'settings.security': 'Sécurité du compte',

  // Email changed confirmation page
  'auth.email_changed_title': 'Email mis à jour ✉️',
  'auth.email_changed_desc': 'Ton adresse email a bien été mise à jour. Tu peux maintenant te connecter avec ta nouvelle adresse.',
  'auth.email_changed_error_title': 'Lien expiré ou invalide',
  'auth.email_changed_error_desc': 'Le lien de confirmation a expiré ou est invalide. Réessaie depuis les paramètres de ton compte.',
  'auth.email_change_expired_title': 'Lien expiré',
  'auth.email_change_expired_desc': 'Ce lien de confirmation a expiré. Tu peux te reconnecter avec ton ancien email et refaire la demande.',
  'settings.email_change_warning': 'Tu seras déconnecté(e) immédiatement. Un email de confirmation sera envoyé à la nouvelle adresse.',

  // Stock hold
  'hold.created': 'Stock réservé pendant 15 minutes',
  'hold.failed': 'Stock insuffisant — impossible de réserver',
  'hold.expired': 'Votre réservation a expiré. Veuillez réessayer.',
  'hold.cancelled': 'Paiement annulé — le stock a été libéré.',
  'hold.item_error': 'Quantité demandée indisponible pour {sku} ({available} dispo)',
  'hold.retry_cta': 'Réessayer',

  // SEO
  'seo.home_title': 'Sabaody Island — One Piece TCG Europe | Scellés vérifiés',
  'seo.home_desc': 'Boutique de confiance One Piece TCG en Europe. Displays, boosters, decks scellés et vérifiés. Non pesés, non trackés. Expédition rapide dans toute l\'UE.',
  'seo.shop_title': 'One Piece TCG — Displays, Boosters, Decks | Sabaody',
  'seo.shop_desc': 'Parcourez toutes nos catégories One Piece TCG : displays, boosters, lots, decks préconstruits et accessoires. Produits scellés, livraison rapide en Europe.',
  'seo.cart_title': 'Panier — Sabaody Island',
  'seo.cart_desc': 'Vérifiez les articles One Piece TCG de votre panier avant le paiement sécurisé sur Sabaody Island.',
  'seo.account_title': 'Mon compte — Sabaody Island',
  'seo.account_desc': 'Gérez votre compte Sabaody Island : commandes, adresses, Berry et fidélité One Piece TCG.',
  'seo.checkout_title': 'Paiement sécurisé — Sabaody Island',
  'seo.checkout_desc': 'Finalisez votre commande One Piece TCG en toute sécurité avec livraison rapide en Europe.',
  'seo.category_desc': 'Achetez des {category} One Piece TCG scellés. Produits vérifiés, non pesés, prix compétitifs. Livraison rapide en Europe.',
  // Per-category SEO
  'seo.cat.displays_title': 'Displays One Piece TCG — Booster Box scellées | Sabaody',
  'seo.cat.displays_desc': 'Achetez des displays (booster box) One Piece TCG scellées, vérifiées et non pesées. JP, EN, FR disponibles. Livraison rapide en Europe.',
  'seo.cat.boosters_title': 'Boosters One Piece TCG — Packs scellés à l\'unité | Sabaody',
  'seo.cat.boosters_desc': 'Boosters One Piece TCG à l\'unité, scellés et non trackés. Toutes les éditions OP et EB disponibles. Expédition rapide en Europe.',
  'seo.cat.case_title': 'Cartons de Display One Piece TCG — Case scellés | Sabaody',
  'seo.cat.case_desc': 'Cartons complets (case) de displays One Piece TCG scellés. Idéal pour les collectionneurs et revendeurs. Livraison sécurisée en Europe.',
  'seo.cat.binder_title': 'Classeurs One Piece TCG — Rangement & Collection | Sabaody',
  'seo.cat.binder_desc': 'Classeurs et portfolios pour ranger votre collection One Piece TCG. Protégez vos cartes avec style. Livraison en Europe.',
  'seo.cat.acrylic-protectors_title': 'Protections Acryliques One Piece TCG | Sabaody',
  'seo.cat.acrylic-protectors_desc': 'Boîtiers acryliques pour protéger vos displays et booster box One Piece TCG. Qualité premium, livraison en Europe.',
  'seo.cat.decks_title': 'Decks Préconstruits One Piece TCG | Sabaody',
  'seo.cat.decks_desc': 'Decks préconstruits One Piece TCG scellés, prêts à jouer. Toutes les éditions disponibles. Livraison rapide en Europe.',
  'seo.cat.lots_title': 'Full Set One Piece TCG — Sets complets | Sabaody',
  'seo.cat.lots_desc': 'Full sets One Piece TCG : collections complètes pré-triées pour collectionner facilement. Livraison en Europe.',
  // Category SEO intro blocks (visible on page)
  'seo.cat.displays_intro': 'Sur <strong>Sabaody Island</strong>, retrouve les <strong>displays One Piece TCG</strong> (booster boxes) pour <strong>ouvrir</strong>, <strong>collectionner</strong> ou préparer un <strong>deck compétitif</strong>.<br/><ul><li><strong>Éditions</strong> : OP, EB, PRB</li><li><strong>Langues</strong> : FR / EN / JPN (selon disponibilité)</li><li><strong>Scellés</strong> : <strong>non trackés / non pesés</strong></li><li><strong>Stock</strong> : mis à jour en temps réel</li></ul>Que tu vises une extension précise (OP15, EB03…) ou que tu cherches simplement une <strong>booster box One Piece en Europe</strong>, tu es au bon endroit : sélection claire, variantes par langue, et options de livraison adaptées.<br/><br/>Bienvenue sur Sabaody 🏴‍☠️',
  'seo.cat.boosters_intro': 'Envie d\'ouvrir quelques packs sans prendre un display ?<br/><br/>Ici, tu peux acheter des <strong>boosters One Piece TCG</strong> à l\'unité. Choisis l\'édition et la langue <strong>FR / EN / JPN</strong> selon la disponibilité, puis complète ta collection au rythme qui te plaît.<br/><br/>Les produits scellés sont <strong>non trackés / non pesés</strong>, et le stock est mis à jour en continu.<br/><br/>Idéal pour tester une extension, compléter un deck, ou simplement se faire plaisir.<br/><br/>Bienvenue sur Sabaody 🏴\u200d☠️',
  'seo.cat.case_intro': 'Pour les collectionneurs et les ouvertures XXL, découvre les <strong>cartons de display One Piece TCG</strong>.<br/><br/>Choisis ta langue <strong>FR / EN / JPN</strong> quand elle est disponible.<br/><br/>Comme toujours : scellés <strong>non trackés / non pesés</strong>, et stock suivi en temps réel.<br/><br/>Idéal si tu veux ouvrir en gros, sécuriser ton stock, ou viser une extension précise.<br/><br/>Bienvenue sur Sabaody 🏴\u200d☠️',
  'seo.cat.decks_intro': 'Les <strong>decks préconstruits One Piece TCG</strong> sont parfaits pour jouer rapidement et apprendre.<br/><br/>Choisis ta langue <strong>FR / EN / JPN</strong> selon la disponibilité.<br/><br/>Que tu sois nouveau joueur ou compétiteur, c\'est le moyen le plus simple de mettre le pied à l\'étrier !<br/><br/>Bienvenue sur Sabaody 🏴\u200d☠️',
  'seo.cat.lots_intro': 'Envie de collectionner sans acheter des displays ?<br/><br/>Les <strong>full set One Piece TCG</strong> sont faits pour toi.<br/><br/>Les lots sont <strong>pré-triés</strong> pour démarrer une collection et viser un <strong>master set</strong> plus facilement.<br/><br/>Parfait si tu veux gagner du temps, compléter une extension, ou offrir un lot "clé en main".<br/><br/>Bienvenue sur Sabaody 🏴\u200d☠️',
  'seo.cat.binder_intro': 'Besoin de ranger ta collection proprement et de la faire grandir sereinement ?<br/><br/>Découvre nos <strong>classeurs One Piece TCG</strong> pensés pour les collectionneurs comme pour les joueurs.<br/><br/>Plusieurs formats seront disponibles selon tes besoins : 9 pochettes, 12 pochettes et format Top Loader pour protéger tes cartes les plus précieuses.<br/><br/>Chaque classeur est personnalité par édition afin de classer ta collection, comme un professionnel !<br/><br/>Bienvenue sur Sabaody 🏴\u200d☠️',
  'seo.cat.sleeves_intro': 'Besoin de protéger ton deck et tes cartes préférées ?<br/><br/>Découvre nos <strong>protège cartes One Piece TCG</strong> pour éviter l\'usure.<br/><br/>Tu retrouveras des sleeves tailles standard, finitions agréables, et une protection fiable au quotidien.<br/><br/>Indispensable pour jouer confortablement et conserver des cartes en parfait état.<br/><br/>Bienvenue sur Sabaody 🏴\u200d☠️',
  'seo.cat.acrylic-protectors_intro': 'Envie de mettre en valeur tes displays et tes decks scellés comme de vrais trésors ?<br/><br/>Retrouve nos <strong>protections acryliques</strong> pour exposer, sécuriser et préserver tes pièces importantes.<br/><br/>C\'est l\'option premium pour protéger contre les chocs, la poussière et les manipulations, tout en gardant un rendu propre et "collector".<br/><br/>Le petit détail sur mesure qui change tout pour une collection qui brille.<br/><br/>Bienvenue sur Sabaody 🏴\u200d☠️',
  'seo.cat.storage-boxes_title': 'Boîtes de Rangement One Piece TCG | Sabaody',
  'seo.cat.storage-boxes_desc': 'Deck boxes, boîtes de tri et de stockage pour vos cartes One Piece TCG. Organisation et protection au quotidien. Livraison en Europe.',
  'seo.cat.storage-boxes_intro': 'Besoin de ranger, trier ou transporter tes cartes ?<br/><br/>Découvre nos <strong>boîtes de rangement One Piece TCG</strong> : deck boxes, boîtes de tri et solutions de stockage pour organiser ta collection au quotidien.<br/><br/>Que tu cherches une deck box magnétique, une boîte de tri par extension ou un rangement longue durée, tu trouveras ce qu\'il te faut.<br/><br/>Bienvenue sur Sabaody 🏴\u200d☠️',
  // Reviews
  'reviews.title': 'Avis clients',
  'reviews.section_title': 'Nos avis clients',
  'reviews.section_subtitle': 'Transparence, qualité, et service — vos retours comptent.',
  'reviews.store_rating': 'Note de la boutique',
  'reviews.based_on': 'Basé sur {count} avis',
  'reviews.no_reviews': 'Aucun avis pour le moment.',
  'reviews.verified': 'Achat vérifié',
  'reviews.see_all': 'Voir tous les avis',
  'reviews.leave_review': 'Laisser un avis',
  'reviews.page_title': 'Avis clients — Sabaody Island',
  'reviews.page_desc': 'Découvrez les avis de nos clients. Transparence et qualité garanties.',
  'reviews.form_title': 'Laisser un avis',
  'reviews.form_name': 'Votre prénom',
  'reviews.form_name_placeholder': 'Ex: Luffy',
  'reviews.form_country': 'Pays',
  'reviews.form_comment': 'Votre avis',
  'reviews.form_comment_placeholder': 'Partagez votre expérience (min. 10 caractères)...',
  'reviews.form_submit': 'Envoyer mon avis',
  'reviews.form_success': 'Merci pour votre avis ! Il sera visible après modération.',
  'reviews.form_error': 'Une erreur est survenue. Veuillez réessayer.',
  'reviews.form_rate_limit': 'Vous avez déjà soumis un avis récemment. Réessayez dans 24h.',
  'reviews.form_login_required': 'Connectez-vous pour laisser un avis.',
  'reviews.login_to_review': 'Connectez-vous pour laisser un avis sur votre commande',
  'reviews.login_to_review_desc': 'Seuls les clients ayant déjà passé commande peuvent laisser un avis.',
  'reviews.login_cta': 'Se connecter',
  'reviews.no_eligible_orders': 'Aucune commande éligible',
  'reviews.no_eligible_orders_desc': 'Seuls les clients ayant déjà reçu une commande peuvent laisser un avis.',
  'reviews.all_orders_reviewed': 'Tous vos avis ont été envoyés !',
  'reviews.all_orders_reviewed_desc': 'Vous avez déjà laissé un avis pour toutes vos commandes éligibles.',
  'reviews.form_title_order': 'Laisser un avis sur votre commande',
  'reviews.select_order': 'Commande concernée',
  'reviews.select_order_placeholder': 'Sélectionnez une commande…',
  // Per-product-type SEO title templates
  'seo.product.booster_box': '{setCode} Booster Box ({lang}) — {productName} | Sabaody Island',
  'seo.product.booster_pack': '{setCode} Booster ({lang}) — {productName} | Sabaody Island',
  'seo.product.case': '{setCode} Case ({lang}) — {productName} | Sabaody Island',
  'seo.product.deck': '{setCode} Deck — {productName} | Sabaody Island',
  'seo.product.default': '{name} | Sabaody Island',
  // OP15 booster box — per-product override
  'seo.product.op15_adventure_kamis_island_booster_box_title': 'OP15 Booster Box (JPN/EN/FR) — Adventure on Kami\'s Island | Sabaody Island',
  'seo.product.op15_adventure_kamis_island_booster_box_desc': 'Booster Box OP15 « Adventure on Kami\'s Island » en JPN/EN/FR selon disponibilité. Scellé non tracké, stock Europe, livraison Europe.',
  // Per-product-type SEO desc templates
  'seo.product_desc.booster_box': 'Achetez la Booster Box {setCode} {productName} ({lang}), scellée et vérifiée. Non pesée. Livraison rapide en Europe.',
  'seo.product_desc.booster_pack': 'Booster individuel {setCode} {productName} ({lang}), scellé et non tracké. Expédition rapide en Europe.',
  'seo.product_desc.case': 'Carton complet {setCode} {productName} ({lang}), scellé. Idéal collectionneurs et revendeurs. Livraison sécurisée en Europe.',
  'seo.product_desc.deck': 'Starter Deck {setCode} {productName}, scellé et prêt à jouer. Livraison rapide en Europe.',
  'seo.loyalty_title': 'Programme de fidélité One Piece TCG — Berry & Récompenses | Sabaody Island',
  'seo.loyalty_desc': 'Gagnez des Berry à chaque achat et échangez-les contre boosters gratuits, livraison offerte et récompenses exclusives.',
  'seo.refund_title': 'Politique de remboursement — Sabaody Island',
  'seo.refund_desc': 'Consultez notre politique de remboursement et de retour. Retour sous 14 jours pour les produits scellés non ouverts.',
  'seo.privacy_title': 'Politique de confidentialité — Sabaody Island',
  'seo.privacy_desc': 'Découvrez comment Sabaody Island protège vos données personnelles.',
  'seo.terms_title': 'Conditions générales de vente — Sabaody Island',
  'seo.terms_desc': 'Consultez nos conditions générales de vente pour la boutique Sabaody Island.',

  // Admin
  'admin.orders': 'Commandes',
  'admin.inventory': 'Inventaire',
  'admin.catalog': 'Catalogue',
  'admin.newsletter': 'Newsletter',
  'admin.social_rewards': 'Réseaux sociaux',
  'admin.order_reviews': 'Avis commandes',
  'admin.reviews': 'Avis clients',
  'admin.email_center': 'Centre e-mails',
  'admin.new_member_banner': 'Bannière nouveaux membres',

  // Order reviews
  'order_review.title': 'Laisser un avis',
  'order_review.subtitle': 'Partagez votre expérience avec cette commande.',
  'order_review.reward_info': 'Recevez +10 Berry pour votre avis !',
  'order_review.rating': 'Note',
  'order_review.title_label': 'Titre (optionnel)',
  'order_review.title_placeholder': 'Résumez votre expérience',
  'order_review.content_label': 'Votre avis',
  'order_review.content_placeholder': 'Partagez votre expérience (min. 10 caractères)...',
  'order_review.display_mode': 'Publier en tant que',
  'order_review.pseudonym': 'Pseudonyme',
  'order_review.anonymous': 'Anonyme',
  'order_review.pseudonym_label': 'Votre pseudonyme',
  'order_review.pseudonym_placeholder': 'Ex: Luffy',
  'order_review.select_product': 'Quel produit souhaitez-vous évaluer ?',
  'order_review.submit': 'Envoyer mon avis',
  'order_review.success': 'Merci pour votre avis et votre confiance ! Vos +10 Berry ont été ajoutés à votre compte.',
  'order_review.error': 'Une erreur est survenue.',
  'order_review.already_submitted': 'Avis déjà envoyé',
  'order_review.status_pending': 'Votre avis a bien été reçu.',
  'order_review.status_approved': 'Votre avis est publié.',
  'order_review.status_rejected': 'Votre avis a été traité.',
  'order_review.reward_granted': 'Berry obtenus !',
  'order_review.cta': 'Laisser un avis (+10 Berry)',
  'order_review.available': 'Avis disponible',
  'order_review.submitted': 'Avis envoyé',
  'order_review.rewarded': 'Récompense obtenue',
  'order_review.already_title': 'Merci pour votre retour !',
  'order_review.already_body': 'Vous avez déjà laissé un avis pour la commande {order}. Votre avis est bien enregistré et sera pris en compte par notre équipe.',
  'order_review.already_rating_label': 'Votre note',
  'order_review.already_submitted_on': 'Envoyé le {date}',
  'order_review.already_view_order': 'Voir ma commande',
  'order_review.already_back_orders': 'Retour à mes commandes',
  'order_review.already_duplicate_error': 'Vous avez déjà laissé un avis pour cette commande.',

  'social.follow_us': 'Suivez-nous',
  'social.follow_desc': 'Suivez-nous sur les réseaux et gagnez',
  'social.per_platform': 'par plateforme !',
  'social.handle_instruction': 'Renseigne ton pseudo pour que nous puissions vérifier que tu suis bien notre page.',
  'social.available': 'Disponible',
  'social.pending': 'En attente',
  'social.approved': 'Validé ✅',
  'social.rejected': 'Refusé',
  'social.request_berry': 'Demander mes +5 Berry',
  'social.request_submitted': 'Ta demande est en attente de validation.',
  'social.request_error': 'Erreur lors de l\'envoi de la demande.',
  'social.handle_prompt': 'Renseigne ton pseudo pour que nous puissions vérifier que tu suis bien notre page.',
  'social.handle_label': 'Ton pseudo sur cette plateforme',
  'social.handle_placeholder': 'monpseudo',
  'social.cancel': 'Annuler',
  'social.submit_request': 'Envoyer ma demande',
  'social.login_required': 'Connectez-vous pour gagner des Berry',
  'social.newsletter_label': 'S\'abonner à la newsletter',
  'social.newsletter_updated': 'Préférence newsletter mise à jour',
  'social.newsletter_active': 'Newsletter activée ✅',
  'social.newsletter_unsubscribe_hint': 'Le désabonnement se fait depuis le lien présent dans les emails newsletter.',

  // Referral
  'referral.title': 'Parrainage',
  'referral.desc': '+50 Berry pour toi et +50 Berry pour ton ami(e) après sa 1ère commande payée de 50€ minimum.',
  'referral.copy': 'Copier',
  'referral.share': 'Partager le lien',
  'referral.copied': 'Code copié !',
  'referral.link_copied': 'Lien copié !',
  'referral.share_text': 'Rejoins Sabaody Island avec mon code parrain et gagne 50 Berry !',
  'referral.edit_code': 'Modifier mon code',
  'referral.save': 'Enregistrer',
  'referral.code_updated': 'Code mis à jour !',
  'referral.code_locked': 'Code déjà modifié',
  'referral.already_changed': 'Tu as déjà modifié ton code une fois.',
  'referral.invalid_format': 'Format invalide (4-16 caractères, lettres/chiffres/tirets).',
  'referral.code_taken': 'Ce code est déjà pris.',
  'referral.reward_rule': 'pour toi et ton ami(e) après sa 1ère commande payée ≥ 50€',
  'referral.pending': 'En attente',
  'referral.activated': 'Validés',
  'referral.total_earned': 'Total gagné',

  // Accessibility
  'a11y.skip_to_content': 'Aller au contenu',
  'a11y.close': 'Fermer',
  'a11y.remove_item': 'Retirer l\'article',
  'a11y.decrease_qty': 'Diminuer la quantité',
  'a11y.increase_qty': 'Augmenter la quantité',
  'a11y.show_password': 'Afficher le mot de passe',
  'a11y.hide_password': 'Masquer le mot de passe',
  'a11y.copy': 'Copier',
  'a11y.share': 'Partager',
  'a11y.cancel': 'Annuler',
  'a11y.search': 'Rechercher',
  'a11y.cart_drawer': 'Panier',
  'a11y.main_navigation': 'Navigation principale',
  'a11y.open_menu': 'Ouvrir le menu',
  'a11y.close_menu': 'Fermer le menu',
  'a11y.mobile_menu': 'Menu de navigation',
};

const enGb: TranslationDict = {
  'nav.shop': 'Shop',
  'nav.displays': 'Booster Boxes',
  'nav.boosters': 'Boosters',
  'nav.lots': 'Full sets',
  'nav.case': 'Case',
  'nav.decks': 'Decks',
  'nav.loyalty': 'Loyalty',
  'nav.promo': 'Offers',
  'promo.title': 'Offers',
  'promo.intro': 'Current offers selected by Sabaody Island. Prices and availability are updated in real time.',
  'promo.empty': 'No offers right now',
  'promo.empty_hint': 'Come back soon, new offers are on the way!',
  'promo.browse_shop': 'Browse the shop',
  'seo.promo_title': 'One Piece TCG Offers | Sabaody Island',
  'seo.promo_desc': 'Discover the current offers on sealed One Piece TCG products at Sabaody Island.',
  'nav.search': 'Search',
  'nav.cart': 'Cart',
  'nav.menu': 'Menu',
  'search.placeholder': 'Search for a card, set, character...',
  'search.card_placeholder': 'Search for a card...',
  'hero.badge': 'One Piece TCG — Verified Cards',
  'hero.title': 'Sabaody Island',
  'hero.subtitle': 'The One Piece TCG reference in Europe',
  'hero.subtitle2': '',
  'hero.cta': 'Shop now',
  'hero.new': 'New Arrivals / Restocks',
  'section.new': 'New Arrivals',
  'section.new_desc': 'Freshly arrived on the island',
  'section.restock': 'Restock',
  'section.restock_desc': 'Back in stock, don\'t miss out!',
  'section.bestsellers': 'Best Sellers',
  'section.bestsellers_desc': 'The crew\'s most popular picks',
  'section.deals': '🔥 Special Deals',
  'section.deals_desc': 'Discover our best deals of the moment. Limited stock, captain!',
  'section.view_all': 'View all',
  'loyalty.title': 'Loyalty Programme 🏴‍☠️',
  'loyalty.subtitle': 'Every purchase brings you closer to the treasure. Earn points, rank up, unlock rewards.',
  'loyalty.how': 'How does it work?',
  'loyalty.step1': 'Buy',
  'loyalty.step1_desc': 'Earn Berry with every order',
  'loyalty.step2': 'Accumulate',
  'loyalty.step2_desc': 'Your Berry are added automatically',
  'loyalty.step3': 'Enjoy',
  'loyalty.step3_desc': 'Redeem your Berry for rewards',
  'loyalty.tiers': 'Ranks',
  'loyalty.your_current_rank': 'Your current rank',
  'loyalty.rewards': 'Rewards',
  'loyalty.cta': 'Start earning points',
  'loyalty.teaser_title': 'Loyalty Programme 🏴‍☠️',
  'loyalty.teaser_desc': 'Earn points with every purchase and unlock exclusive rewards. Join the crew!',
  'loyalty.teaser_cta': 'Explore loyalty rewards',
  'loyalty.earn_rule': '€1 spent = 1 Berry',
  'loyalty.reward_250': 'Free delivery',
  'loyalty.reward_500': '3 free booster packs',
  'loyalty.reward_1000': '6 free booster packs',
  'loyalty.reward_2000': '1 free Display',
  'loyalty.reward_5000': '2 free Displays',
  'loyalty.reward_10000': '3 free Displays',
  'loyalty.perk_5off': '-5% on booster boxes',
  'loyalty.perk_10off': '-10% on booster boxes',
  'loyalty.perk_early': 'Early access to new releases',
  'loyalty.rules_title': 'Redemption rules',
  'loyalty.rule_one_per_order': 'One reward per order',
  'loyalty.rule_no_expiry': 'Your points never expire',
  'loyalty.rule_account_required': 'Account required to earn and redeem points',
  'loyalty.rule_next_order': 'Rewards can be used on your next order',
  'loyalty.checkout_earn': 'You will earn {n} points with this order',
  'tier.moussaillon': 'Cabin Boy',
  'tier.pirate': 'Pirate',
  'tier.captain': 'Captain',
  'tier.yonko': 'Yonko',
  'tier.tenryubito': 'Tenryubito',
  'tier.roi_des_pirates': 'Pirate King',
  'account.title': 'My Account',
  'account.rank': 'Your rank',
  'account.rank_current': 'Current rank',
  'account.berry_current': 'Current Berry',
  'account.next_rank': 'Next rank',
  'account.points_to_next': 'Only {count} Berry to go before the next rank!',
  'account.max_rank_reached': 'You have reached the highest rank.',
  'account.balance': 'Available Berry',
  'account.lifetime_earned': 'Total Berry earned',
  'account.history': 'History',
  'account.berry_cta': 'Berry statement',
  'account.berry_cta_subtitle': 'See your earnings, spendings and rewards.',
  'account.no_transactions': 'No transactions yet',
  'account.redeem': 'Redeem',
  'account.redeem_success': 'Reward redeemed!',
  'account.redeem_next_order': 'It will be applied to your next order',
  'account.redeem_error': 'Error',
  'account.logout': 'Log out',
  'account.loading': 'Loading...',
  'berry_history.title': 'Berry movements',
  'berry_history.empty': 'No Berry movements yet.',
  'berry_history.filter_all': 'All',
  'berry_history.filter_earnings': 'Earnings',
  'berry_history.filter_spendings': 'Spendings',
  'berry_history.filter_rewards': 'Rewards',
  'berry_history.filter_orders': 'Orders',
  'berry_history.see_more': 'See more',
  'berry_history.label_welcome_bonus': 'Welcome bonus',
  'berry_history.label_newsletter': 'Newsletter bonus',
  'berry_history.label_social_instagram': 'Social follow — Instagram',
  'berry_history.label_social_facebook': 'Social follow — Facebook',
  'berry_history.label_social_tiktok': 'Social follow — TikTok',
  'berry_history.label_social': 'Social follow —',
  'berry_history.label_review': 'Review approved',
  'berry_history.label_referral_out': 'Referral — qualified friend',
  'berry_history.label_referral_in': 'Referral bonus — welcome',
  'berry_history.label_free_delivery': 'Free delivery',
  'berry_history.label_reward_used': 'Reward used',
  'berry_history.label_reward_generic': 'Reward',
  'berry_history.label_order_earn': 'Order',
  'berry_history.order_suffix': 'Order',
  'checkout.loyalty_title': 'Loyalty Points',
  'checkout.loyalty_balance': 'Your balance',
  'checkout.free_shipping_toggle': 'Use 250 Berry: Free delivery',
  'checkout.free_shipping_helper': 'Available because you have 250+ Berry. Use it now or save it.',
  'checkout.free_shipping_applied': 'Free delivery (250 Berry)',
  'checkout.free_shipping_remaining': 'After use',
  'checkout.rewards_title': 'Berry Rewards',
  'checkout.reward_none': 'Don\'t use a reward',
  'checkout.reward_free_shipping': 'Free delivery',
  'checkout.reward_free_boosters_3': '3 free booster packs added to order',
  'checkout.reward_free_boosters_6': '6 free booster packs added to order',
  'checkout.reward_free_display': '1 free Display added to order',
  'checkout.reward_free_displays_2': '2 free Displays added to order',
  'checkout.reward_free_displays_3': '3 free Displays added to order',
  'checkout.reward_insufficient': 'Insufficient Berry balance',
  'checkout.reward_needs_display': 'Your cart must contain at least one Display',
  'checkout.reward_pick_display': 'Choose the Display to discount:',
  'checkout.reward_after_use': 'After use',
  'checkout.reward_gift': '(gift)',
  'checkout.reward_display_discount_label': 'Display discount -10%',
  'checkout.reward_select_display': 'Select the Display to discount',
  'checkout.reward_discount_per_unit': '-{amount} on 1 unit',
  'checkout.gift_pick_edition': 'Choose your editions',
  'checkout.gift_available': 'Available',
  'checkout.gift_out_of_stock': 'Out of stock',
  'checkout.gift_no_stock': 'Insufficient stock for this edition',
  'checkout.gift_incomplete': 'Select all editions for your gift',
  'checkout.gift_remaining': '{count} more to choose',
  'loyalty.reward_available': 'Reward available: Free delivery (next order)',
  'loyalty.reward_use_at_checkout': 'Use at checkout',
  'stock.in_stock': 'In stock',
  'stock.preorder': 'Pre-order',
  'stock.sold_out': 'Sold out',
  'stock.out_of_stock': 'Out of stock',
  'stock.preorder_available': 'Pre-order',
  'stock.available': 'available',
  'stock.available_plural': 'available',
  'stock.limited': 'Limited quantity',
  'stock.shipped_24_48': 'In stock — Dispatched within 24–48h',
  'stock.out_label': 'Out of stock',
  'stock.eta': 'Estimated delivery:',
  'stock.eta_on': 'Estimated delivery on',
  'badge.new': 'New',
  'badge.restock': 'Restock',
  'badge.bestseller': 'Best Seller',
  'badge.sealed': 'Sealed',
  'product.from': 'From',
  'price.coming_soon': 'Coming soon',
  'product.languages': 'languages',
  'product.choose_language': 'Choose language',
  'product.choose_binder_type': 'Choose binder type',
  'product.add_to_cart': 'Add to cart',
  'product.preorder': 'Pre-order',
  'product.notify': 'Notify me',
  'product.not_found': 'Product not found',
  'product.back_to_shop': 'Previous page',
  'product.you_may_like': 'You may also like',
  'product.click_to_unzoom': 'Click to zoom out',
  'product.sorted': 'Pre-sorted in numerical order',
  'product.complete_collection': 'Complete collection (details on request)',
  'product.lot_contents': 'Bundle contents',
  'product.total_cards': 'Total cards',
  'product.super_rare': 'Super Rare',
  'product.uncommon': 'Uncommon',
  'product.common': 'Common',
  'product.limited_per_order': 'Limited to {n} per order',
  'product.state': 'Condition:',
  'product.sorted_nm_jp': 'Pre-sorted • NM/M • Japanese',
  'benefit.pre_sorted': 'Pre-sorted in numerical order',
  'benefit.pack_fresh': 'Pack-fresh condition',
  'benefit.no_display': 'Without buying a display',
  'filter.all': 'All',
  'filter.filters': 'Filters',
  'filter.reset': 'Reset',
  'filter.set': 'Set / Extension',
  'filter.rarity': 'Rarity',
  'filter.language': 'Language',
  'filter.color': 'Colour',
  'filter.all_option': 'All',
  'sort.label': 'Sort by',
  'sort.newest': 'Newest',
  'sort.price_asc': 'Price: low to high',
  'sort.price_desc': 'Price: high to low',
  'sort.popular': 'Popularity',
  'sort.release_desc': 'Release date: newest → oldest',
  'sort.release_asc': 'Release date: oldest → newest',
  'sort.edition_az': 'Edition: A → Z',
  'sort.edition_za': 'Edition: Z → A',
  'sort.code_desc': 'Code: descending',
  'sort.code_asc': 'Code: ascending',
  'sort.stock_first': 'In stock first',
  'sort.out_of_stock_first': 'Sold out first',
  'sort.edition_op_first': 'Edition: OP first',
  'sort.edition_prb_first': 'Edition: PRB first',
  'sort.edition_eb_first': 'Edition: EB first',
  'sort.st_asc': 'ST number: ST01 → ST29',
  'sort.st_desc': 'ST number: ST29 → ST01',
  'sort.apply': 'Apply',
  'sort.reset': 'Reset',
  'results': 'result',
  'results_plural': 'results',
  'no_results': 'No results found',
  'no_results_hint': 'Try adjusting your filters',
  'category.back': 'Previous page',
  'category.products': 'product',
  'category.products_plural': 'products',
  'category.empty': 'No products in this category',
  'category.empty_hint': 'Come back soon, captain!',
  'catalog.load_error': 'The catalogue could not be loaded. Please try again in a moment.',
  'catalog.retry_aria': 'Retry loading the catalogue',
  'catalog.retry': 'Retry',
  'cat.displays': 'Booster Boxes',
  'cat.boosters': 'Boosters',
  'cat.lots': 'Full sets',
  'cat.case': 'Case',
  'cat.decks': 'Preconstructed decks',
  'cat.binder': 'Binders',
  'cat.sleeves': 'Sleeves',
  'cat.acrylic-protectors': 'Acrylic Protectors',
  'cat.storage-boxes': 'Storage Boxes',
  'nav.accessories': 'Accessories',
  'nav.binder': 'Binders',
  'nav.sleeves': 'Sleeves',
  'nav.acrylic-protectors': 'Acrylic Protectors',
  'nav.storage-boxes': 'Storage Boxes',
  'category.coming_soon': 'Coming soon',
  'category.coming_soon_hint': 'This category is coming soon. Stay tuned, captain!',
  'cart.title': 'Your cart',
  'cart.empty': 'Your cart is empty',
  'cart.explore': 'Discover our treasures',
  'cart.clear': 'Clear cart',
  'cart.crosssell_title': 'You might also like',
  'cart.crosssell_subtitle': 'Complete your order in 1 click',
  'cart.crosssell_add': 'Add',
  'cart.crosssell_coming_soon': 'Coming soon',
  'cart.summary': 'Summary',
  'cart.subtotal': 'Subtotal',
  'cart.shipping': 'Delivery',
  'cart.shipping_next': 'Calculated at next step',
  'cart.total': 'Total',
  'cart.checkout': 'Checkout',
  'cart.drawer_empty': 'Your cart is empty',
  'cart.drawer_hint': 'Explore our treasures to fill it up!',
  'cart.view_full': 'View full cart',
  'checkout.title': 'Order',
  'checkout.empty': 'Cart is empty',
  'checkout.back': 'Back to cart',
  'checkout.info': 'Information',
  'checkout.firstname': 'First name',
  'checkout.lastname': 'Last name',
  'checkout.email': 'Email',
  'checkout.address': 'Address',
  'checkout.zip': 'Postcode',
  'checkout.city': 'City',
  'checkout.country': 'Country',
  'checkout.shipping': 'Delivery method',
  'checkout.shipping_eu': '🇪🇺 EU delivery across Europe',
  'checkout.payment': 'Secure payment',
  'checkout.payment_hint': 'Payment will be configured here',
  'checkout.pay': 'Proceed to payment',
  'checkout.days': 'days',
  'checkout.redirecting': 'Redirecting...',
  'checkout.stripe_redirect_hint': 'You will be redirected to Stripe to complete your payment securely.',
  'checkout.success_title': 'Order confirmed!',
  'checkout.success_desc': 'Thank you for your purchase. Your order has been placed and you will receive a confirmation email.',
  'checkout.success_orders': 'View my orders',
  'checkout.success_continue': 'Continue shopping',
  'checkout.order_number': 'Order number',
  'checkout.berry_earned': 'earned',
  'trust.verified': 'Verified cards',
  'trust.verified_desc': 'Authenticity guaranteed',
  'trust.refund': 'Money-back guarantee',
  'trust.refund_desc': '14-day returns',
  'trust.shipping': 'EU delivery',
  'trust.shipping_desc': 'Fast delivery 🇪🇺',
  'detail.trust1': 'Verified and authenticated cards',
  'detail.trust2': 'Money-back if not satisfied (14 days)',
  'detail.trust3': 'EU Delivery — Mondial Relay, Chronopost, La Poste, FedEx',
  'detail.notify_back': 'Notify me when back in stock',
  'binder.subtitle': 'Available in 9-pocket, 12-pocket and toploader formats.',
  'binder.available': 'Available',
  'binder.coming_soon': 'Coming soon',
  'binder.out_of_stock': 'Out of stock',
  'binder.notify_cta': 'Notify me when back in stock',
  'binder.notify_desc': 'This binder will be available soon. Set up an alert to be notified.',
  'binder.desc_official': 'Official binder for edition',
  'binder.desc_formats': 'Available formats: 9-pocket, 12-pocket, toploader.',
  'binder.desc_protect': 'Designed to store and protect your collection.',
  'binder.desc_matching': 'Matching visual style for the edition.',
  'binder.format_9pkt': '9-Pocket',
  'binder.format_12pkt': '12-Pocket',
  'binder.format_topload': 'Toploader',
  'binder.title_prefix': 'Binder',
  'binder.trust1': 'Compatible with One Piece TCG',
  'binder.trust2': 'Money-back within 14 days',
  'binder.trust3': 'EU Delivery',
  'binder.view_product': 'View binder',
  'binder.subtitle_short': '9 / 12 pocket · Toploader',
  'notify.title': 'Notify me',
  'notify.subtitle': 'Get an email alert',
  'notify.desc': 'This product is currently sold out. Leave your email to be notified when it\'s back.',
  'notify.submit': 'Notify me when back in stock',
  'notify.success_title': 'Noted!',
  'notify.success_desc': 'You\'ll be notified as soon as this product is back in stock.',
  'notify.error': 'An error occurred. Please try again.',
  'footer.desc': 'Your trusted shop for One Piece TCG. Verified cards, fast delivery across Europe.',
  'footer.nav': 'Navigation',
  'footer.info': 'Information',
  'footer.cgv': 'Terms of service',
  'footer.refund_policy': 'Refund policy',
  'footer.privacy': 'Privacy policy',
  'footer.contact': 'Contact',
  'footer.shipped_from': 'Dispatched from France 🇫🇷',
  'footer.rights': '© 2026 Sabaody Island. All rights reserved.',
  'footer.payment_methods': 'Payment methods',
  'footer.shipping_partners': 'Shipping partners',
  'shop.title': 'Shop',
  'shop.browse_categories': 'Browse by category',
  'product.description_heading': 'Description',
  'shop.desc': 'All our verified One Piece TCG cards',
  'locale.switch': 'Language',
  'bubble.title': 'Explore our treasures',
  'bubble.swipe_hint': 'Swipe →',
  'notfound.title': 'Page not found',
  'notfound.desc': 'Oops! This page doesn\'t exist.',
  'notfound.back': 'Return to home',
  'notify.email_placeholder': 'your@email.com',
  'loyalty.points': 'Berry',
  'loyalty.no_img': 'No image',
  'misc.no_img': 'No image',
  'nav.prev': 'Previous',
  'nav.next': 'Next',

  // Language names
  'lang.Japonais': 'Japanese',
  'lang.Anglais': 'English',
  'lang.Français': 'French',
  'lang.JP': 'Japanese',
  'lang.EN': 'English',
  'lang.FR': 'French',
  'lang.9-Pocket': '9-Pocket',
  'lang.12-Pocket': '12-Pocket',
  'lang.Toploader': 'Toploader',

  // Condition values
  'condition.Scellé': 'Sealed',
  'condition.Neuf': 'New',
  'condition.NM': 'NM',
  'condition.EX': 'EX',
  'condition.GD': 'GD',
  'condition.LP': 'LP',
  'condition.MP': 'MP',

  // Badge values
  'badge_val.Nouveau': 'New',
  'badge_val.Best-seller': 'Best Seller',
  'badge_val.Restock': 'Restock',
  'badge_val.Perfect fit': 'Perfect fit',
  'badge_val.Full set': 'Full set',
  'badge_val.Lot': 'Bundle',
  'badge_val.Précommande': 'Pre-order',
  'badge_val.Accessoire': 'Accessory',

  // Sealed trust
  'sealed.chip': 'Sealed products: not tracked, not weighed',
  'sealed.badge': 'Not tracked • Not weighed',
  'sealed.detail_title': 'Sealed guarantee',
  'sealed.detail_desc': 'No booster boxes are tracked or weighed. This is our store policy.',
  'sealed.checkout': 'Sealed products: not tracked, not weighed — Sabaody Island trust policy.',

  // Deck filters
  'filter.deck_type': 'Deck type',
  'filter.availability': 'Availability',
  'filter.price': 'Price',
  'deck_type.starter': 'Starter Deck',
  'deck_type.ultimate': 'Ultimate Deck',
  'deck_type.ultra': 'Ultra Deck',
  'deck_type.learn_together': 'Learn Together Deck',
  'deck_type.all': 'All types',
  'availability.all': 'All',
  'availability.in_stock': 'In stock',
  'availability.preorder': 'Pre-order',

  // Auth
  'auth.login': 'Log in',
  'auth.signup': 'Create account',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.forgot_password': 'Forgot password?',
  'auth.no_account': 'Don\'t have an account?',
  'auth.has_account': 'Already have an account?',
  'auth.continue_google': 'Continue with Google',
  'auth.continue_apple': 'Continue with Apple',
  'auth.or': 'or',
  'auth.error': 'Error',
  'auth.password_min': '12 characters minimum',
  'auth.password_policy': 'Password must be at least 12 characters, with at least one uppercase letter, one lowercase letter, one number, and one special character.',
  'auth.first_name': 'First name',
  'auth.last_name': 'Last name',
  'auth.phone': 'Phone (optional)',
  'auth.address_line1': 'Address',
  'auth.address_line2': 'Address line 2 (optional)',
  'auth.postal_code': 'Postal code',
  'auth.city': 'City',
  'auth.country': 'Country',
  'auth.shipping_info': 'Shipping address',
  'auth.check_email': 'Check your email',
  'auth.check_email_desc': 'A confirmation link has been sent. Click it to activate your account.',
  'auth.back_to_login': 'Back to login',
  'auth.confirmed_title': 'Email confirmed ✅',
  'auth.confirmed_desc': 'Thank you! Your Sabaody Island account is now activated. You can log in or continue shopping.',
  'auth.confirmed_shop': 'Continue shopping',
  'auth.confirmed_error_title': 'Invalid or expired link',
  'auth.confirmed_error_desc': 'This confirmation link is no longer valid. Please sign up again or contact support.',
  'auth.confirmed_resend': 'Try signing up again',
  'auth.confirming': 'Verifying…',
  'auth.forgot_desc': 'Enter your email to receive a reset link.',
  'auth.send_reset': 'Send reset link',
  'auth.reset_email_sent': 'A reset email has been sent.',
  'auth.reset_password': 'New password',
  'auth.new_password': 'New password',
  'auth.update_password': 'Update',
  'auth.password_updated': 'Password updated!',
  'auth.login_or_signup': 'Log in / Create account',
  'auth.newsletter_optin': 'Subscribe to the newsletter',
  'auth.newsletter_berry': '+25 Berry',
  'auth.newsletter_bonus_note': 'Bonus credited after account creation.',
  'auth.newsletter_privacy': 'See our',
  'auth.newsletter_privacy_link': 'privacy policy',
  'auth.has_referral': 'I have a referral code',
  'auth.referral_placeholder': 'SAB-XXXX',
  'auth.referral_applied': 'Code applied ✅',
  'account.loyalty': 'Loyalty',
  'orders.title': 'My orders',
  'orders.empty': 'No orders yet',
  'orders.order': 'Order',
  'orders.not_found': 'Order not found',
  'orders.items': 'Items',
  'orders.shipping': 'Shipping',
  'orders.address': 'Address',
  'orders.track': 'Track my order',
  'orders.track_copy': 'Copy tracking number',
  'orders.track_copied': 'Copied!',
  'orders.track_hint': 'Enter your tracking number and postal code below to see your parcel status.',
  'orders.track_open_tab': 'Open tracking in a new tab',
  'orders.track_no_tracking': 'Tracking will be available once your order has shipped.',
  'orders.track_loading': 'Loading tracking…',
  'orders.track_postal': 'Postal code',
  'order_status.draft': 'Draft',
  'order_status.pending_payment': 'Pending payment',
  'order_status.paid': 'Paid',
  'order_status.processing': 'Processing',
  'order_status.in_transit': 'In transit',
  'order_status.delivered': 'Delivered',
  'order_status.dispute': 'Dispute',
  'order_status.return_requested': 'Return requested',
  'order_status.fulfilled': 'Shipped',
  'order_status.cancelled': 'Cancelled',
  'order_status.refunded': 'Refunded',
  'addresses.title': 'My addresses',
  'addresses.add': 'Add address',
  'addresses.edit': 'Edit address',
  'addresses.empty': 'No saved addresses',
  'addresses.full_name': 'Full name',
  'addresses.line1': 'Address line 1',
  'addresses.line2': 'Address line 2 (optional)',
  'addresses.phone': 'Phone (optional)',
  'addresses.set_default': 'Default address',
  'addresses.save': 'Save',
  'addresses.fill_required': 'Please fill in required fields',

  // Guest checkout
  'checkout.contact_title': 'Contact',
  'checkout.logged_in_as': 'Logged in as',
  'checkout.guest_email_label': 'Email to receive order tracking',
  'checkout.create_account_cta': 'Create an account (optional)',
  'checkout.address_title': 'Shipping address',
  'checkout.guest_create_account_title': 'Create an account to track your orders',
  'checkout.guest_create_account_desc': 'Track your orders, earn Berry with every purchase and unlock rewards.',

  // Settings
  'settings.title': 'Settings',
  'settings.change_password': 'Change password',
  'settings.current_password': 'Current password',
  'settings.new_password': 'New password',
  'settings.confirm_password': 'Confirm new password',
  'settings.save_password': 'Update password',
   'settings.passwords_mismatch': 'Passwords do not match',
   'settings.passwords_mismatch_new': 'New passwords do not match',
   'settings.current_password_wrong': 'Current password is incorrect',
   'settings.password_updated_success': 'Your password has been updated successfully',
  'settings.change_email': 'Change email address',
  'settings.new_email': 'New email address',
  'settings.save_email': 'Update email',
  'settings.email_change_sent': 'A confirmation email has been sent to your new address. Check your inbox to confirm the change.',
   'settings.email_change_error': 'Unable to change email address',
   'settings.email_change_sent_hint': 'Click the link in the email sent to your new address to finalize the change.',
   'settings.email_change_logout_hint': 'You have been signed out for security. Confirm the change via the email sent to your new address, then sign in with it.',
  'settings.security': 'Account security',

  'auth.email_changed_title': 'Email updated ✉️',
  'auth.email_changed_desc': 'Your email address has been updated. You can now sign in with your new address.',
  'auth.email_changed_error_title': 'Expired or invalid link',
  'auth.email_changed_error_desc': 'The confirmation link has expired or is invalid. Try again from your account settings.',
  'auth.email_change_expired_title': 'Link expired',
  'auth.email_change_expired_desc': 'This confirmation link has expired. You can sign in with your old email and request the change again.',
  'settings.email_change_warning': 'You will be logged out immediately. A confirmation email will be sent to the new address.',

  // Stock hold
  'hold.created': 'Stock reserved for 15 minutes',
  'hold.failed': 'Insufficient stock — unable to reserve',
  'hold.expired': 'Your reservation has expired. Please try again.',
  'hold.cancelled': 'Payment cancelled — stock has been released.',
  'hold.item_error': 'Requested quantity unavailable for {sku} ({available} available)',
  'hold.retry_cta': 'Try again',

  // SEO
  'seo.home_title': 'Sabaody Island — One Piece TCG Europe | Verified Sealed',
  'seo.home_desc': 'Your trusted One Piece TCG shop in Europe. Sealed displays, boosters, decks. Verified, non-weighed, non-tracked. Fast EU-wide shipping.',
  'seo.shop_title': 'One Piece TCG Shop — Displays, Boosters, Decks | Sabaody',
  'seo.shop_desc': 'Browse all One Piece TCG categories: displays, booster boxes, bundles, starter decks and accessories. Sealed products, fast European delivery.',
  'seo.cart_title': 'Cart — Sabaody Island',
  'seo.cart_desc': 'Review the One Piece TCG items in your cart before secure checkout on Sabaody Island.',
  'seo.account_title': 'My account — Sabaody Island',
  'seo.account_desc': 'Manage your Sabaody Island account: orders, addresses, Berry balance and One Piece TCG loyalty.',
  'seo.checkout_title': 'Secure checkout — Sabaody Island',
  'seo.checkout_desc': 'Complete your One Piece TCG order securely with fast shipping across Europe.',
  'seo.category_desc': 'Buy sealed One Piece TCG {category}. Verified products, non-weighed, competitive prices. Fast shipping across Europe.',
  // Per-category SEO
  'seo.cat.displays_title': 'One Piece TCG Booster Boxes — Sealed Displays | Sabaody',
  'seo.cat.displays_desc': 'Buy sealed One Piece TCG booster boxes (displays). Verified, non-weighed. JP, EN, FR available. Fast shipping across Europe.',
  'seo.cat.boosters_title': 'One Piece TCG Booster Packs — Sealed Singles | Sabaody',
  'seo.cat.boosters_desc': 'One Piece TCG single booster packs, sealed and non-tracked. All OP and EB editions available. Fast EU shipping.',
  'seo.cat.case_title': 'One Piece TCG Cases — Sealed Display Cases | Sabaody',
  'seo.cat.case_desc': 'Full sealed cases of One Piece TCG booster boxes. Ideal for collectors and resellers. Secure shipping across Europe.',
  'seo.cat.binder_title': 'One Piece TCG Binders — Card Storage & Collection | Sabaody',
  'seo.cat.binder_desc': 'Binders and portfolios for your One Piece TCG collection. Protect your cards in style. Shipping across Europe.',
  'seo.cat.acrylic-protectors_title': 'One Piece TCG Acrylic Protectors — Display Cases | Sabaody',
  'seo.cat.acrylic-protectors_desc': 'Acrylic display cases to protect your One Piece TCG booster boxes. Premium quality, shipped across Europe.',
  'seo.cat.decks_title': 'One Piece TCG Starter Decks — Sealed | Sabaody',
  'seo.cat.decks_desc': 'Sealed One Piece TCG starter decks, ready to play. All editions available. Fast shipping across Europe.',
  'seo.cat.lots_title': 'One Piece TCG Full Sets — Complete Collections | Sabaody',
  'seo.cat.lots_desc': 'One Piece TCG full sets: pre-sorted complete collections to build your binder fast. Shipped across Europe.',
  'seo.cat.displays_intro': 'On <strong>Sabaody Island</strong>, shop <strong>One Piece TCG booster boxes (displays)</strong> to <strong>open packs</strong>, <strong>collect</strong>, or build a <strong>competitive deck</strong>.<br/><ul><li><strong>Sets</strong>: OP, EB, PRB</li><li><strong>Languages</strong>: EN / FR / JPN (subject to availability)</li><li><strong>Sealed</strong>: <strong>not tracked / not weighed</strong></li><li><strong>Stock</strong>: updated in real time</li></ul>Whether you\'re targeting a specific release (OP15, EB03…) or simply looking for a <strong>One Piece booster box in Europe</strong>, you\'re in the right place: clear selection, language variants, and EU delivery options.<br/>Set sail for Sabaody 🏴\u200d☠️',
  'seo.cat.boosters_intro': 'Want to open a few packs without buying a full booster box?<br/><br/>Here you can buy <strong>One Piece TCG booster packs</strong> individually. Choose the set and language <strong>EN / FR / JPN</strong> (subject to availability), then build your collection at your own pace.<br/><br/>Sealed products are <strong>not tracked / not weighed</strong>, and stock is updated continuously.<br/><br/>Perfect to try a set, upgrade a deck, or simply treat yourself.<br/><br/>Welcome to Sabaody 🏴\u200d☠️',
  'seo.cat.case_intro': 'For collectors and XXL openings, explore <strong>One Piece TCG sealed cases</strong>.<br/><br/>Choose your language <strong>EN / FR / JPN</strong> when available.<br/><br/>As always: sealed <strong>not tracked / not weighed</strong>, with stock tracked in real time.<br/><br/>Perfect if you want to open big, secure sealed stock, or target a specific set.<br/><br/>Welcome to Sabaody 🏴\u200d☠️',
  'seo.cat.decks_intro': '<strong>One Piece TCG starter decks</strong> are perfect to start playing quickly and learn the game.<br/><br/>Choose your language <strong>EN / FR / JPN</strong> depending on availability.<br/><br/>Whether you\'re new or competitive, it\'s the easiest way to get started!<br/><br/>Welcome to Sabaody 🏴\u200d☠️',
  'seo.cat.lots_intro': 'Want to collect without buying booster boxes?<br/><br/>Our <strong>One Piece TCG full sets</strong> are made for you.<br/><br/>They\'re <strong>pre-sorted</strong> to kick-start a collection and reach a <strong>master set</strong> more easily.<br/><br/>Perfect if you want to save time, complete a set, or gift a "ready-to-go" bundle.<br/><br/>Welcome to Sabaody 🏴\u200d☠️',
  'seo.cat.binder_intro': 'Need to store your collection neatly and help it grow with peace of mind?<br/><br/>Discover our <strong>One Piece TCG binders</strong> made for collectors and players alike.<br/><br/>Several formats will be available to fit your needs: 9-pocket, 12-pocket, and Top Loader format to protect your most valuable cards.<br/><br/>Each binder is personalised by set/edition so you can organise your collection like a pro!<br/><br/>Welcome to Sabaody 🏴\u200d☠️',
  'seo.cat.sleeves_intro': 'Need to protect your deck and your favourite cards?<br/><br/>Discover our <strong>One Piece TCG sleeves</strong> to prevent wear and tear.<br/><br/>You\'ll find standard-size sleeves, smooth finishes, and reliable everyday protection.<br/><br/>Essential for comfortable play and keeping cards in perfect condition.<br/><br/>Welcome to Sabaody 🏴\u200d☠️',
  'seo.cat.acrylic-protectors_intro': 'Want to showcase your sealed booster boxes and decks like real treasures?<br/><br/>Check out our <strong>acrylic protectors</strong> to display, secure and preserve your important pieces.<br/><br/>It\'s the premium option to protect against knocks, dust and handling, while keeping a clean "collector" look.<br/><br/>That custom touch that makes your collection truly shine.<br/><br/>Welcome to Sabaody 🏴\u200d☠️',
  'seo.cat.storage-boxes_title': 'Storage Boxes One Piece TCG | Sabaody',
  'seo.cat.storage-boxes_desc': 'Deck boxes, sorting boxes and card storage solutions for your One Piece TCG collection. Stay organised. Shipped across Europe.',
  'seo.cat.storage-boxes_intro': 'Need to store, sort or carry your cards?<br/><br/>Discover our <strong>One Piece TCG storage boxes</strong>: deck boxes, sorting boxes and storage solutions to keep your collection organised every day.<br/><br/>Whether you\'re looking for a magnetic deck box, a sorting box by set, or long-term storage, you\'ll find what you need.<br/><br/>Welcome to Sabaody 🏴\u200d☠️',
  'reviews.title': 'Customer Reviews',
  'reviews.section_title': 'Customer Reviews',
  'reviews.section_subtitle': 'Transparency, quality, and service — your feedback matters.',
  'reviews.store_rating': 'Store Rating',
  'reviews.based_on': 'Based on {count} reviews',
  'reviews.no_reviews': 'No reviews yet.',
  'reviews.verified': 'Verified purchase',
  'reviews.see_all': 'See all reviews',
  'reviews.leave_review': 'Leave a review',
  'reviews.page_title': 'Customer Reviews — Sabaody Island',
  'reviews.page_desc': 'Read customer reviews. Transparency and quality guaranteed.',
  'reviews.form_title': 'Leave a review',
  'reviews.form_name': 'Your first name',
  'reviews.form_name_placeholder': 'e.g. Luffy',
  'reviews.form_country': 'Country',
  'reviews.form_comment': 'Your review',
  'reviews.form_comment_placeholder': 'Share your experience (min. 10 characters)...',
  'reviews.form_submit': 'Submit my review',
  'reviews.form_success': 'Thank you for your review! It will be visible after moderation.',
  'reviews.form_error': 'An error occurred. Please try again.',
  'reviews.form_rate_limit': 'You have already submitted a review recently. Try again in 24h.',
  'reviews.form_login_required': 'Log in to leave a review.',
  'reviews.login_to_review': 'Log in to review your order',
  'reviews.login_to_review_desc': 'Only customers who have placed an order can leave a review.',
  'reviews.login_cta': 'Log in',
  'reviews.no_eligible_orders': 'No eligible orders',
  'reviews.no_eligible_orders_desc': 'Only customers who have received an order can leave a review.',
  'reviews.all_orders_reviewed': 'All reviews submitted!',
  'reviews.all_orders_reviewed_desc': 'You have already reviewed all your eligible orders.',
  'reviews.form_title_order': 'Review your order',
  'reviews.select_order': 'Select an order',
  'reviews.select_order_placeholder': 'Choose an order…',
  'seo.product.booster_box': '{setCode} Booster Box ({lang}) — {productName} | Sabaody Island',
  'seo.product.booster_pack': '{setCode} Booster Pack ({lang}) — {productName} | Sabaody Island',
  'seo.product.case': '{setCode} Case ({lang}) — {productName} | Sabaody Island',
  'seo.product.deck': '{setCode} Deck — {productName} | Sabaody Island',
  'seo.product.default': '{name} | Sabaody Island',
  'seo.product.op15_adventure_kamis_island_booster_box_title': 'OP15 Booster Box (JPN/EN/FR) — Adventure on Kami\'s Island | Sabaody Island',
  'seo.product.op15_adventure_kamis_island_booster_box_desc': 'OP15 "Adventure on Kami\'s Island" booster box in JPN/EN/FR (subject to availability). Sealed not tracked, EU stock, EU delivery.',
  'seo.product_desc.booster_box': 'Buy the {setCode} {productName} Booster Box ({lang}), sealed and verified. Non-weighed. Fast shipping across Europe.',
  'seo.product_desc.booster_pack': 'Single {setCode} {productName} booster pack ({lang}), sealed and non-tracked. Fast EU shipping.',
  'seo.product_desc.case': 'Full sealed {setCode} {productName} case ({lang}). Ideal for collectors and resellers. Secure shipping across Europe.',
  'seo.product_desc.deck': '{setCode} {productName} Starter Deck, sealed and ready to play. Fast shipping across Europe.',
  'seo.loyalty_title': 'One Piece TCG Loyalty Programme — Berry & Rewards | Sabaody Island',
  'seo.loyalty_desc': 'Earn Berry with every purchase and redeem them for free boosters, free shipping and exclusive rewards.',
  'seo.refund_title': 'Refund Policy — Sabaody Island',
  'seo.refund_desc': 'Read our refund and returns policy. 14-day returns on unopened sealed products.',
  'seo.privacy_title': 'Privacy Policy — Sabaody Island',
  'seo.privacy_desc': 'Learn how Sabaody Island protects your personal data and privacy across the EU, in line with GDPR.',
  'seo.terms_title': 'Terms & Conditions — Sabaody Island',
  'seo.terms_desc': 'Read our terms and conditions for the Sabaody Island shop.',

  // Admin
  'admin.orders': 'Orders',
  'admin.inventory': 'Inventory',
  'admin.catalog': 'Catalog',
  'admin.newsletter': 'Newsletter',
  'admin.social_rewards': 'Social rewards',
  'admin.order_reviews': 'Order reviews',
  'admin.reviews': 'Reviews',
  'admin.email_center': 'Email Center',
  'admin.new_member_banner': 'New member banner',

  // Order reviews
  'order_review.title': 'Leave a review',
  'order_review.subtitle': 'Share your experience with this order.',
  'order_review.reward_info': 'Earn +10 Berry for your review!',
  'order_review.rating': 'Rating',
  'order_review.title_label': 'Title (optional)',
  'order_review.title_placeholder': 'Summarise your experience',
  'order_review.content_label': 'Your review',
  'order_review.content_placeholder': 'Share your experience (min. 10 characters)...',
  'order_review.display_mode': 'Publish as',
  'order_review.pseudonym': 'Pseudonym',
  'order_review.anonymous': 'Anonymous',
  'order_review.pseudonym_label': 'Your pseudonym',
  'order_review.pseudonym_placeholder': 'e.g. Luffy',
  'order_review.select_product': 'Which product would you like to review?',
  'order_review.submit': 'Submit my review',
  'order_review.success': 'Thank you for your review! Your +10 Berry have been added to your account.',
  'order_review.error': 'An error occurred.',
  'order_review.already_submitted': 'Review already submitted',
  'order_review.status_pending': 'Your review has been received.',
  'order_review.status_approved': 'Your review is published.',
  'order_review.status_rejected': 'Your review has been processed.',
  'order_review.reward_granted': 'Berry earned!',
  'order_review.cta': 'Leave a review (+10 Berry)',
  'order_review.available': 'Review available',
  'order_review.submitted': 'Review submitted',
  'order_review.rewarded': 'Reward earned',
  'order_review.already_title': 'Thank you for your feedback!',
  'order_review.already_body': 'You have already left a review for order {order}. Your review has been saved and will be reviewed by our team.',
  'order_review.already_rating_label': 'Your rating',
  'order_review.already_submitted_on': 'Submitted on {date}',
  'order_review.already_view_order': 'View my order',
  'order_review.already_back_orders': 'Back to my orders',
  'order_review.already_duplicate_error': 'You have already left a review for this order.',

  'social.follow_us': 'Follow us',
  'social.follow_desc': 'Follow us on social media and earn',
  'social.per_platform': 'per platform!',
  'social.handle_instruction': 'Enter your handle so we can verify you follow our page.',
  'social.available': 'Available',
  'social.pending': 'Pending',
  'social.approved': 'Approved ✅',
  'social.rejected': 'Rejected',
  'social.request_berry': 'Request my +5 Berry',
  'social.request_submitted': 'Your request is pending review.',
  'social.request_error': 'Error submitting request.',
  'social.handle_prompt': 'Enter your handle so we can verify you follow our page.',
  'social.handle_label': 'Your handle on this platform',
  'social.handle_placeholder': 'myhandle',
  'social.cancel': 'Cancel',
  'social.submit_request': 'Submit request',
  'social.login_required': 'Log in to earn Berry',
  'social.newsletter_label': 'Subscribe to newsletter',
  'social.newsletter_updated': 'Newsletter preference updated',
  'social.newsletter_active': 'Newsletter active ✅',
  'social.newsletter_unsubscribe_hint': 'To unsubscribe, use the link at the bottom of newsletter emails.',

  // Referral
  'referral.title': 'Referral',
  'referral.desc': '+50 Berry for you and +50 Berry for your friend after their first paid order of £50 or more.',
  'referral.copy': 'Copy',
  'referral.share': 'Share link',
  'referral.copied': 'Code copied!',
  'referral.link_copied': 'Link copied!',
  'referral.share_text': 'Join Sabaody Island with my referral code and earn 50 Berry!',
  'referral.edit_code': 'Edit my code',
  'referral.save': 'Save',
  'referral.code_updated': 'Code updated!',
  'referral.code_locked': 'Code already modified',
  'referral.already_changed': 'You have already changed your code once.',
  'referral.invalid_format': 'Invalid format (4-16 characters, letters/numbers/hyphens).',
  'referral.code_taken': 'This code is already taken.',
  'referral.reward_rule': 'for you and your friend after their 1st paid order ≥ £50',
  'referral.pending': 'Pending',
  'referral.activated': 'Activated',
  'referral.total_earned': 'Total earned',

  // Accessibility
  'a11y.skip_to_content': 'Skip to content',
  'a11y.close': 'Close',
  'a11y.remove_item': 'Remove item',
  'a11y.decrease_qty': 'Decrease quantity',
  'a11y.increase_qty': 'Increase quantity',
  'a11y.show_password': 'Show password',
  'a11y.hide_password': 'Hide password',
  'a11y.copy': 'Copy',
  'a11y.share': 'Share',
  'a11y.cancel': 'Cancel',
  'a11y.search': 'Search',
  'a11y.cart_drawer': 'Cart',
  'a11y.main_navigation': 'Main navigation',
  'a11y.open_menu': 'Open menu',
  'a11y.close_menu': 'Close menu',
  'a11y.mobile_menu': 'Navigation menu',
};


// ─── DE (German) ─────────────────────────────────────────
const de: TranslationDict = {
  'nav.shop': 'Shop',
  'nav.displays': 'Booster-Boxen',
  'nav.boosters': 'Boosters',
  'nav.lots': 'Full Sets',
  'nav.case': 'Karton (Case)',
  'nav.decks': 'Decks',
  'nav.loyalty': 'Treueprogramm',
  'nav.promo': 'Angebote',
  'promo.title': 'Angebote',
  'promo.intro': 'Aktuelle Angebote, ausgewählt von Sabaody Island. Preise und Verfügbarkeit werden in Echtzeit aktualisiert.',
  'promo.empty': 'Derzeit keine Angebote',
  'promo.empty_hint': 'Komm bald wieder, neue Angebote sind unterwegs!',
  'promo.browse_shop': 'Zum Shop',
  'seo.promo_title': 'One Piece TCG Angebote | Sabaody Island',
  'seo.promo_desc': 'Entdecke die aktuellen Angebote für versiegelte One Piece TCG Produkte bei Sabaody Island.',
  'nav.search': 'Suchen',
  'nav.cart': 'Warenkorb',
  'nav.menu': 'Menü',
  'search.placeholder': 'Karte, Set oder Charakter suchen...',
  'search.card_placeholder': 'Karte suchen...',
  'hero.badge': 'One Piece TCG — Geprüfte Karten',
  'hero.title': 'Sabaody Island',
  'hero.subtitle': 'Die One Piece TCG-Referenz in Europa',
  'hero.subtitle2': '',
  'hero.cta': 'Jetzt shoppen',
  'hero.new': 'Neuheiten / Nachschub',
  'section.new': 'Neuheiten',
  'section.new_desc': 'Frisch auf der Insel angekommen',
  'section.restock': 'Nachschub',
  'section.restock_desc': 'Wieder auf Lager, nicht verpassen!',
  'section.bestsellers': 'Bestseller',
  'section.bestsellers_desc': 'Die beliebtesten Artikel der Crew',
  'section.deals': '🔥 Sonderangebote',
  'section.deals_desc': 'Entdecke unsere besten Angebote. Begrenzter Vorrat, Kapitän!',
  'section.view_all': 'Alle anzeigen',
  'loyalty.title': 'Treueprogramm 🏴‍☠️',
  'loyalty.subtitle': 'Jeder Kauf bringt dich dem Schatz näher. Sammle Punkte, steige im Rang auf, schalte Belohnungen frei.',
  'loyalty.how': 'Wie funktioniert es?',
  'loyalty.step1': 'Kaufen',
  'loyalty.step1_desc': 'Sammle Berry bei jeder Bestellung',
  'loyalty.step2': 'Sammeln',
  'loyalty.step2_desc': 'Deine Berry werden automatisch gutgeschrieben',
  'loyalty.step3': 'Profitieren',
  'loyalty.step3_desc': 'Tausche deine Berry gegen Belohnungen ein',
  'loyalty.tiers': 'Ränge',
  'loyalty.your_current_rank': 'Ihr aktueller Rang',
  'loyalty.rewards': 'Belohnungen',
  'loyalty.cta': 'Punkte sammeln starten',
  'loyalty.teaser_title': 'Treueprogramm 🏴‍☠️',
  'loyalty.teaser_desc': 'Sammle Punkte bei jedem Kauf und schalte exklusive Belohnungen frei. Komm an Bord!',
  'loyalty.teaser_cta': 'Treueprogramm entdecken',
  'loyalty.earn_rule': '1 € ausgegeben = 1 Berry',
  'loyalty.reward_250': 'Kostenlose Lieferung',
  'loyalty.reward_500': '3 Gratis-Booster',
  'loyalty.reward_1000': '6 Gratis-Booster',
  'loyalty.reward_2000': '1 Display geschenkt',
  'loyalty.reward_5000': '2 Displays geschenkt',
  'loyalty.reward_10000': '3 Displays geschenkt',
  'loyalty.perk_5off': '-5 % auf Booster-Boxen',
  'loyalty.perk_10off': '-10 % auf Booster-Boxen',
  'loyalty.perk_early': 'Früher Zugang zu Neuheiten',
  'loyalty.rules_title': 'Einlöseregeln',
  'loyalty.rule_one_per_order': 'Eine Belohnung pro Bestellung',
  'loyalty.rule_no_expiry': 'Deine Punkte verfallen nie',
  'loyalty.rule_account_required': 'Konto erforderlich zum Sammeln und Einlösen',
  'loyalty.rule_next_order': 'Prämien können bei der nächsten Bestellung eingelöst werden',
  'loyalty.checkout_earn': 'Du wirst {n} Punkte mit dieser Bestellung verdienen',
  'tier.moussaillon': 'Schiffsjunge',
  'tier.pirate': 'Pirat',
  'tier.captain': 'Kapitän',
  'tier.yonko': 'Yonko',
  'tier.tenryubito': 'Tenryubito',
  'tier.roi_des_pirates': 'Piratenkönig',
  'account.title': 'Mein Konto',
  'account.rank': 'Ihr Rang',
  'account.rank_current': 'Aktueller Rang',
  'account.berry_current': 'Aktuelle Berry',
  'account.next_rank': 'Nächster Rang',
  'account.points_to_next': 'Nur noch {count} Berry bis zum nächsten Rang!',
  'account.max_rank_reached': 'Sie haben den höchsten Rang erreicht.',
  'account.balance': 'Verfügbare Berry',
  'account.lifetime_earned': 'Insgesamt verdiente Berry',
  'account.history': 'Verlauf',
  'account.berry_cta': 'Berry-Kontoauszug',
  'account.berry_cta_subtitle': 'Sehen Sie Ihre Einnahmen, Ausgaben und Prämien.',
  'account.no_transactions': 'Noch keine Transaktionen',
  'account.redeem': 'Einlösen',
  'account.redeem_success': 'Prämie eingelöst!',
  'account.redeem_next_order': 'Sie wird bei Ihrer nächsten Bestellung angewendet',
  'account.redeem_error': 'Fehler',
  'account.logout': 'Abmelden',
  'account.loading': 'Laden...',
  'berry_history.title': 'Berry-Bewegungen',
  'berry_history.empty': 'Noch keine Berry-Bewegungen.',
  'berry_history.filter_all': 'Alle',
  'berry_history.filter_earnings': 'Einnahmen',
  'berry_history.filter_spendings': 'Ausgaben',
  'berry_history.filter_rewards': 'Prämien',
  'berry_history.filter_orders': 'Bestellungen',
  'berry_history.see_more': 'Mehr anzeigen',
  'berry_history.label_welcome_bonus': 'Willkommensbonus',
  'berry_history.label_newsletter': 'Newsletter-Bonus',
  'berry_history.label_social_instagram': 'Social Media — Instagram',
  'berry_history.label_social_facebook': 'Social Media — Facebook',
  'berry_history.label_social_tiktok': 'Social Media — TikTok',
  'berry_history.label_social': 'Social Media —',
  'berry_history.label_review': 'Bewertung bestätigt',
  'berry_history.label_referral_out': 'Empfehlung — qualifizierter Freund',
  'berry_history.label_referral_in': 'Empfehlungsbonus — Willkommen',
  'berry_history.label_free_delivery': 'Kostenloser Versand',
  'berry_history.label_reward_used': 'Prämie eingelöst',
  'berry_history.label_reward_generic': 'Prämie',
  'berry_history.label_order_earn': 'Bestellung',
  'berry_history.order_suffix': 'Bestellung',
  'checkout.loyalty_title': 'Treuepunkte',
  'checkout.loyalty_balance': 'Ihr Guthaben',
  'checkout.free_shipping_toggle': '250 Berry nutzen: Kostenlose Lieferung',
  'checkout.free_shipping_helper': 'Verfügbar, weil Sie 250+ Berry haben. Jetzt nutzen oder aufheben.',
  'checkout.free_shipping_applied': 'Kostenlose Lieferung (250 Berry)',
  'checkout.free_shipping_remaining': 'Nach Einlösung',
  'checkout.rewards_title': 'Berry-Belohnungen',
  'checkout.reward_none': 'Keine Belohnung verwenden',
  'checkout.reward_free_shipping': 'Kostenlose Lieferung',
  'checkout.reward_free_boosters_3': '3 Gratis-Booster zur Bestellung hinzugefügt',
  'checkout.reward_free_boosters_6': '6 Gratis-Booster zur Bestellung hinzugefügt',
  'checkout.reward_free_display': '1 Gratis-Display zur Bestellung hinzugefügt',
  'checkout.reward_free_displays_2': '2 Gratis-Displays zur Bestellung hinzugefügt',
  'checkout.reward_free_displays_3': '3 Gratis-Displays zur Bestellung hinzugefügt',
  'checkout.reward_insufficient': 'Nicht genug Berry',
  'checkout.reward_needs_display': 'Dein Warenkorb muss mindestens eine Display enthalten',
  'checkout.reward_pick_display': 'Wähle die Display für den Rabatt:',
  'checkout.reward_after_use': 'Nach Einlösung',
  'checkout.reward_gift': '(Geschenk)',
  'checkout.reward_display_discount_label': 'Display-Rabatt -10 %',
  'checkout.reward_select_display': 'Wähle die Display für den Rabatt',
  'checkout.reward_discount_per_unit': '-{amount} auf 1 Einheit',
  'checkout.gift_pick_edition': 'Wähle deine Editionen',
  'checkout.gift_available': 'Verfügbar',
  'checkout.gift_out_of_stock': 'Ausverkauft',
  'checkout.gift_no_stock': 'Nicht genug Bestand für diese Edition',
  'checkout.gift_incomplete': 'Wähle alle Editionen für dein Geschenk',
  'checkout.gift_remaining': 'Noch {count} auszuwählen',
  'loyalty.reward_available': 'Belohnung verfügbar: Kostenlose Lieferung (nächste Bestellung)',
  'loyalty.reward_use_at_checkout': 'Beim Checkout einlösen',
  'stock.in_stock': 'Auf Lager',
  'stock.preorder': 'Vorbestellung',
  'stock.sold_out': 'Ausverkauft',
  'stock.out_of_stock': 'Nicht verfügbar',
  'stock.preorder_available': 'Vorbestellen',
  'stock.available': 'verfügbar',
  'stock.available_plural': 'verfügbar',
  'stock.limited': 'Begrenzte Menge',
  'stock.shipped_24_48': 'Auf Lager — Versand innerhalb 24–48 Std.',
  'stock.out_label': 'Nicht auf Lager',
  'stock.eta': 'Voraussichtliche Lieferung:',
  'stock.eta_on': 'Voraussichtliche Lieferung am',
  'badge.new': 'Neu',
  'badge.restock': 'Nachschub',
  'badge.bestseller': 'Bestseller',
  'badge.sealed': 'Versiegelt',
  'product.from': 'Ab',
  'price.coming_soon': 'Bald verfügbar',
  'product.languages': 'Sprachen',
  'product.choose_language': 'Sprache wählen',
  'product.choose_binder_type': 'Ordnertyp wählen',
  'product.add_to_cart': 'In den Warenkorb',
  'product.preorder': 'Vorbestellen',
  'product.notify': 'Benachrichtigen',
  'product.not_found': 'Produkt nicht gefunden',
  'product.back_to_shop': 'Vorherige Seite',
  'product.you_may_like': 'Das könnte dir auch gefallen',
  'product.click_to_unzoom': 'Klicken zum Herauszoomen',
  'product.sorted': 'Vorsortiert in numerischer Reihenfolge',
  'product.complete_collection': 'Komplette Sammlung (Details auf Anfrage)',
  'product.lot_contents': 'Bundle-Inhalt',
  'product.total_cards': 'Karten gesamt',
  'product.super_rare': 'Super Rare',
  'product.uncommon': 'Uncommon',
  'product.common': 'Common',
  'product.limited_per_order': 'Auf {n} pro Bestellung begrenzt',
  'product.state': 'Zustand:',
  'product.sorted_nm_jp': 'Vorsortiert • NM/M • Japanisch',
  'benefit.pre_sorted': 'Vorsortiert in numerischer Reihenfolge',
  'benefit.pack_fresh': 'Zustand frisch aus dem Booster',
  'benefit.no_display': 'Ohne eine Display zu kaufen',
  'filter.all': 'Alle',
  'filter.filters': 'Filter',
  'filter.reset': 'Zurücksetzen',
  'filter.set': 'Set / Erweiterung',
  'filter.rarity': 'Seltenheit',
  'filter.language': 'Sprache',
  'filter.color': 'Farbe',
  'filter.all_option': 'Alle',
  'sort.label': 'Sortieren nach',
  'sort.newest': 'Neueste',
  'sort.price_asc': 'Preis: aufsteigend',
  'sort.price_desc': 'Preis: absteigend',
  'sort.popular': 'Beliebtheit',
  'sort.release_desc': 'Erscheinungsdatum: neu → alt',
  'sort.release_asc': 'Erscheinungsdatum: alt → neu',
  'sort.edition_az': 'Edition: A → Z',
  'sort.edition_za': 'Edition: Z → A',
  'sort.code_desc': 'Code: absteigend',
  'sort.code_asc': 'Code: aufsteigend',
  'sort.stock_first': 'Verfügbar zuerst',
  'sort.out_of_stock_first': 'Ausverkauft zuerst',
  'sort.edition_op_first': 'Edition: OP zuerst',
  'sort.edition_prb_first': 'Edition: PRB zuerst',
  'sort.edition_eb_first': 'Edition: EB zuerst',
  'sort.st_asc': 'ST-Nummer: ST01 → ST29',
  'sort.st_desc': 'ST-Nummer: ST29 → ST01',
  'sort.apply': 'Anwenden',
  'sort.reset': 'Zurücksetzen',
  'results': 'Ergebnis',
  'results_plural': 'Ergebnisse',
  'no_results': 'Keine Ergebnisse gefunden',
  'no_results_hint': 'Versuche die Filter anzupassen',
  'category.back': 'Vorherige Seite',
  'category.products': 'Produkt',
  'category.products_plural': 'Produkte',
  'category.empty': 'Keine Produkte in dieser Kategorie',
  'category.empty_hint': 'Komm bald wieder, Kapitän!',
  'catalog.load_error': 'Der Katalog konnte nicht geladen werden. Bitte versuche es gleich erneut.',
  'catalog.retry_aria': 'Katalog erneut laden',
  'catalog.retry': 'Erneut versuchen',
  'cat.displays': 'Booster-Boxen',
  'cat.boosters': 'Boosters',
  'cat.lots': 'Full Sets',
  'cat.case': 'Karton (Case)',
  'cat.decks': 'Vorkonstruierte Decks',
  'cat.binder': 'Ordner',
  'cat.sleeves': 'Kartenhüllen',
  'cat.acrylic-protectors': 'Acryl-Schutzhüllen',
  'cat.storage-boxes': 'Aufbewahrungsboxen',
  'nav.accessories': 'Zubehör',
  'nav.binder': 'Ordner',
  'nav.sleeves': 'Kartenhüllen',
  'nav.acrylic-protectors': 'Acryl-Schutzhüllen',
  'nav.storage-boxes': 'Aufbewahrungsboxen',
  'category.coming_soon': 'Bald verfügbar',
  'category.coming_soon_hint': 'Diese Kategorie kommt bald. Bleib dran, Kapitän!',
  'cart.title': 'Dein Warenkorb',
  'cart.empty': 'Dein Warenkorb ist leer',
  'cart.explore': 'Entdecke unsere Schätze',
  'cart.clear': 'Warenkorb leeren',
  'cart.crosssell_title': 'Das könnte dir auch gefallen',
  'cart.crosssell_subtitle': 'Vervollständige deine Bestellung mit 1 Klick',
  'cart.crosssell_add': 'Hinzufügen',
  'cart.crosssell_coming_soon': 'Demnächst verfügbar',
  'cart.summary': 'Zusammenfassung',
  'cart.subtotal': 'Zwischensumme',
  'cart.shipping': 'Versand',
  'cart.shipping_next': 'Wird im nächsten Schritt berechnet',
  'cart.total': 'Gesamt',
  'cart.checkout': 'Bestellen',
  'cart.drawer_empty': 'Dein Warenkorb ist leer',
  'cart.drawer_hint': 'Erkunde unsere Schätze und fülle ihn!',
  'cart.view_full': 'Warenkorb anzeigen',
  'checkout.title': 'Bestellung',
  'checkout.empty': 'Warenkorb ist leer',
  'checkout.back': 'Zurück zum Warenkorb',
  'checkout.info': 'Informationen',
  'checkout.firstname': 'Vorname',
  'checkout.lastname': 'Nachname',
  'checkout.email': 'E-Mail',
  'checkout.address': 'Adresse',
  'checkout.zip': 'PLZ',
  'checkout.city': 'Stadt',
  'checkout.country': 'Land',
  'checkout.shipping': 'Versandart',
  'checkout.shipping_eu': '🇪🇺 EU-Lieferung europaweit',
  'checkout.payment': 'Sichere Zahlung',
  'checkout.payment_hint': 'Zahlung wird hier eingerichtet',
  'checkout.pay': 'Zur Zahlung',
  'checkout.days': 'Tage',
  'checkout.redirecting': 'Weiterleitung...',
  'checkout.stripe_redirect_hint': 'Sie werden zu Stripe weitergeleitet, um die Zahlung sicher abzuschließen. Lieferadresse und E-Mail werden beim Bezahlen erfasst.',
  'checkout.success_title': 'Bestellung bestätigt!',
  'checkout.success_desc': 'Vielen Dank für Ihren Einkauf. Ihre Bestellung wurde aufgenommen und Sie erhalten eine Bestätigungs-E-Mail.',
  'checkout.success_orders': 'Meine Bestellungen',
  'checkout.success_continue': 'Weiter einkaufen',
  'checkout.order_number': 'Bestellnummer',
  'checkout.berry_earned': 'verdient',
  'trust.verified': 'Geprüfte Karten',
  'trust.verified_desc': 'Echtheit garantiert',
  'trust.refund': 'Geld-zurück-Garantie',
  'trust.refund_desc': '14 Tage Rückgabe',
  'trust.shipping': 'EU-Lieferung',
  'trust.shipping_desc': 'Schneller Versand 🇪🇺',
  'detail.trust1': 'Geprüfte und authentifizierte Karten',
  'detail.trust2': 'Geld zurück bei Unzufriedenheit (14 Tage)',
  'detail.trust3': 'EU-Lieferung — Mondial Relay, Chronopost, La Poste, FedEx',
  'detail.notify_back': 'Benachrichtige mich, wenn wieder verfügbar',
  'binder.subtitle': 'Erhältlich als 9-Fach, 12-Fach und Toploader.',
  'binder.available': 'Verfügbar',
  'binder.coming_soon': 'Bald verfügbar',
  'binder.out_of_stock': 'Ausverkauft',
  'binder.notify_cta': 'Benachrichtige mich bei Verfügbarkeit',
  'binder.notify_desc': 'Dieser Ordner wird bald verfügbar sein. Richte eine Benachrichtigung ein.',
  'binder.desc_official': 'Offizieller Ordner für die Edition',
  'binder.desc_formats': 'Verfügbare Formate: 9-Fach, 12-Fach, Toploader.',
  'binder.desc_protect': 'Entwickelt zum Aufbewahren und Schützen deiner Sammlung.',
  'binder.desc_matching': 'Passendes Design zur Edition.',
  'binder.format_9pkt': '9-Fach',
  'binder.format_12pkt': '12-Fach',
  'binder.format_topload': 'Toploader',
  'binder.title_prefix': 'Ordner',
  'binder.trust1': 'Kompatibel mit One Piece TCG',
  'binder.trust2': 'Rückerstattung innerhalb von 14 Tagen',
  'binder.trust3': 'EU-Lieferung',
  'binder.view_product': 'Ordner ansehen',
  'binder.subtitle_short': '9 / 12 Fach · Toploader',
  'notify.title': 'Benachrichtigung',
  'notify.subtitle': 'E-Mail-Benachrichtigung erhalten',
  'notify.desc': 'Dieses Produkt ist derzeit ausverkauft. Hinterlasse deine E-Mail, um benachrichtigt zu werden.',
  'notify.submit': 'Benachrichtige mich bei Verfügbarkeit',
  'notify.success_title': 'Notiert!',
  'notify.success_desc': 'Du wirst benachrichtigt, sobald das Produkt wieder verfügbar ist.',
  'notify.error': 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
  'footer.desc': 'Dein vertrauenswürdiger Shop für One Piece TCG. Geprüfte Karten, schneller Versand in Europa.',
  'footer.nav': 'Navigation',
  'footer.info': 'Informationen',
  'footer.cgv': 'AGB',
  'footer.refund_policy': 'Widerrufsbelehrung',
  'footer.privacy': 'Datenschutz',
  'footer.contact': 'Kontakt',
  'footer.shipped_from': 'Versand aus Frankreich 🇫🇷',
  'footer.rights': '© 2026 Sabaody Island. Alle Rechte vorbehalten.',
  'footer.payment_methods': 'Zahlungsmethoden',
  'footer.shipping_partners': 'Versandpartner',
  'shop.title': 'Shop',
  'shop.browse_categories': 'Nach Kategorie stöbern',
  'product.description_heading': 'Beschreibung',
  'shop.desc': 'Alle unsere geprüften One Piece TCG-Karten',
  'locale.switch': 'Sprache',
  'bubble.title': 'Entdecke unsere Schätze',
  'bubble.swipe_hint': 'Wischen →',
  'notfound.title': 'Seite nicht gefunden',
  'notfound.desc': 'Oops! Diese Seite existiert nicht.',
  'notfound.back': 'Zurück zur Startseite',
  'notify.email_placeholder': 'deine@email.de',
  'loyalty.points': 'Berry',
  'loyalty.no_img': 'Kein Bild',
  'misc.no_img': 'Kein Bild',
  'nav.prev': 'Vorherige',
  'nav.next': 'Nächste',

  // Language names
  'lang.Japonais': 'Japanisch',
  'lang.Anglais': 'Englisch',
  'lang.Français': 'Französisch',
  'lang.JP': 'Japanisch',
  'lang.EN': 'Englisch',
  'lang.FR': 'Französisch',
  'lang.9-Pocket': '9-Pocket',
  'lang.12-Pocket': '12-Pocket',
  'lang.Toploader': 'Toploader',

  // Condition values
  'condition.Scellé': 'Versiegelt',
  'condition.Neuf': 'Neu',
  'condition.NM': 'NM',
  'condition.EX': 'EX',
  'condition.GD': 'GD',
  'condition.LP': 'LP',
  'condition.MP': 'MP',

  // Badge values
  'badge_val.Nouveau': 'Neu',
  'badge_val.Best-seller': 'Bestseller',
  'badge_val.Restock': 'Nachschub',
  'badge_val.Perfect fit': 'Perfect fit',
  'badge_val.Full set': 'Full set',
  'badge_val.Lot': 'Bundle',
  'badge_val.Précommande': 'Vorbestellung',
  'badge_val.Accessoire': 'Zubehör',

  // Sealed trust
  'sealed.chip': 'Versiegelt: nicht getrackt, nicht gewogen',
  'sealed.badge': 'Nicht getrackt • Nicht gewogen',
  'sealed.detail_title': 'Versiegelungsgarantie',
  'sealed.detail_desc': 'Keine Booster-Boxen werden getrackt oder gewogen. Das ist unsere Geschäftsrichtlinie.',
  'sealed.checkout': 'Versiegelt: nicht getrackt, nicht gewogen — Vertrauensrichtlinie Sabaody Island.',

  // Deck filters
  'filter.deck_type': 'Deck-Typ',
  'filter.availability': 'Verfügbarkeit',
  'filter.price': 'Preis',
  'deck_type.starter': 'Starter Deck',
  'deck_type.ultimate': 'Ultimate Deck',
  'deck_type.ultra': 'Ultra Deck',
  'deck_type.learn_together': 'Learn Together Deck',
  'deck_type.all': 'Alle Typen',
  'availability.all': 'Alle',
  'availability.in_stock': 'Auf Lager',
  'availability.preorder': 'Vorbestellung',

  // Auth
  'auth.login': 'Anmelden',
  'auth.signup': 'Konto erstellen',
  'auth.email': 'E-Mail',
  'auth.password': 'Passwort',
  'auth.forgot_password': 'Passwort vergessen?',
  'auth.no_account': 'Noch kein Konto?',
  'auth.has_account': 'Bereits ein Konto?',
  'auth.continue_google': 'Weiter mit Google',
  'auth.continue_apple': 'Weiter mit Apple',
  'auth.or': 'oder',
  'auth.error': 'Fehler',
  'auth.password_min': 'Mindestens 12 Zeichen',
  'auth.password_policy': 'Das Passwort muss mindestens 12 Zeichen lang sein und mindestens einen Großbuchstaben, einen Kleinbuchstaben, eine Zahl und ein Sonderzeichen enthalten.',
  'auth.first_name': 'Vorname',
  'auth.last_name': 'Nachname',
  'auth.phone': 'Telefon (optional)',
  'auth.address_line1': 'Adresse',
  'auth.address_line2': 'Adresszusatz (optional)',
  'auth.postal_code': 'Postleitzahl',
  'auth.city': 'Stadt',
  'auth.country': 'Land',
  'auth.shipping_info': 'Lieferadresse',
  'auth.check_email': 'Prüfe dein E-Mail-Postfach',
  'auth.check_email_desc': 'Ein Bestätigungslink wurde gesendet. Klicke darauf, um dein Konto zu aktivieren.',
  'auth.back_to_login': 'Zurück zur Anmeldung',
  'auth.confirmed_title': 'E-Mail bestätigt ✅',
  'auth.confirmed_desc': 'Danke! Dein Sabaody Island-Konto ist jetzt aktiviert. Du kannst dich anmelden oder weiter einkaufen.',
  'auth.confirmed_shop': 'Weiter einkaufen',
  'auth.confirmed_error_title': 'Ungültiger oder abgelaufener Link',
  'auth.confirmed_error_desc': 'Dieser Bestätigungslink ist nicht mehr gültig. Bitte registriere dich erneut oder kontaktiere den Support.',
  'auth.confirmed_resend': 'Erneut registrieren',
  'auth.confirming': 'Überprüfung…',
  'auth.forgot_desc': 'Gib deine E-Mail ein, um einen Zurücksetzungslink zu erhalten.',
  'auth.send_reset': 'Link senden',
  'auth.reset_email_sent': 'Eine E-Mail zur Zurücksetzung wurde gesendet.',
  'auth.reset_password': 'Neues Passwort',
  'auth.new_password': 'Neues Passwort',
  'auth.update_password': 'Aktualisieren',
  'auth.password_updated': 'Passwort aktualisiert!',
  'auth.login_or_signup': 'Anmelden / Konto erstellen',
  'auth.newsletter_optin': 'Newsletter abonnieren',
  'auth.newsletter_berry': '+25 Berry',
  'auth.newsletter_bonus_note': 'Bonus wird nach Kontoerstellung gutgeschrieben.',
  'auth.newsletter_privacy': 'Siehe unsere',
  'auth.newsletter_privacy_link': 'Datenschutzrichtlinie',
  'auth.has_referral': 'Ich habe einen Empfehlungscode',
  'auth.referral_placeholder': 'SAB-XXXX',
  'auth.referral_applied': 'Code angewendet ✅',
  'account.loyalty': 'Treueprogramm',
  'orders.title': 'Meine Bestellungen',
  'orders.empty': 'Noch keine Bestellungen',
  'orders.order': 'Bestellung',
  'orders.not_found': 'Bestellung nicht gefunden',
  'orders.items': 'Artikel',
  'orders.shipping': 'Versand',
  'orders.address': 'Adresse',
  'orders.track': 'Meine Sendung verfolgen',
  'orders.track_copy': 'Sendungsnummer kopieren',
  'orders.track_copied': 'Kopiert!',
  'orders.track_hint': 'Geben Sie Ihre Sendungsnummer und Postleitzahl ein, um den Status Ihres Pakets zu sehen.',
  'orders.track_open_tab': 'Sendungsverfolgung in neuem Tab öffnen',
  'orders.track_no_tracking': 'Die Sendungsverfolgung ist verfügbar, sobald Ihre Bestellung versandt wurde.',
  'orders.track_loading': 'Sendungsverfolgung wird geladen…',
  'orders.track_postal': 'Postleitzahl',
  'order_status.draft': 'Entwurf',
  'order_status.pending_payment': 'Zahlung ausstehend',
  'order_status.paid': 'Bezahlt',
  'order_status.processing': 'In Bearbeitung',
  'order_status.in_transit': 'Unterwegs',
  'order_status.delivered': 'Zugestellt',
  'order_status.dispute': 'Streitfall',
  'order_status.return_requested': 'Rücksendung',
  'order_status.fulfilled': 'Versendet',
  'order_status.cancelled': 'Storniert',
  'order_status.refunded': 'Erstattet',
  'addresses.title': 'Meine Adressen',
  'addresses.add': 'Adresse hinzufügen',
  'addresses.edit': 'Adresse bearbeiten',
  'addresses.empty': 'Keine gespeicherten Adressen',
  'addresses.full_name': 'Vollständiger Name',
  'addresses.line1': 'Adresszeile 1',
  'addresses.line2': 'Adresszeile 2 (optional)',
  'addresses.phone': 'Telefon (optional)',
  'addresses.set_default': 'Standardadresse',
  'addresses.save': 'Speichern',
  'addresses.fill_required': 'Bitte füllen Sie die Pflichtfelder aus',

  // Guest checkout
  'checkout.contact_title': 'Kontakt',
  'checkout.logged_in_as': 'Angemeldet als',
  'checkout.guest_email_label': 'E-Mail für die Sendungsverfolgung',
  'checkout.create_account_cta': 'Konto erstellen (optional)',
  'checkout.address_title': 'Lieferadresse',
  'checkout.guest_create_account_title': 'Erstelle ein Konto, um deine Bestellungen zu verfolgen',
  'checkout.guest_create_account_desc': 'Verfolge deine Bestellungen, sammle Berry bei jedem Kauf und schalte Belohnungen frei.',

  // Settings
  'settings.title': 'Einstellungen',
  'settings.change_password': 'Passwort ändern',
  'settings.current_password': 'Aktuelles Passwort',
  'settings.new_password': 'Neues Passwort',
  'settings.confirm_password': 'Neues Passwort bestätigen',
  'settings.save_password': 'Passwort aktualisieren',
   'settings.passwords_mismatch': 'Passwörter stimmen nicht überein',
   'settings.passwords_mismatch_new': 'Die neuen Passwörter stimmen nicht überein',
   'settings.current_password_wrong': 'Das aktuelle Passwort ist falsch',
   'settings.password_updated_success': 'Ihr Passwort wurde erfolgreich aktualisiert',
  'settings.change_email': 'E-Mail-Adresse ändern',
  'settings.new_email': 'Neue E-Mail-Adresse',
  'settings.save_email': 'E-Mail aktualisieren',
  'settings.email_change_sent': 'Eine Bestätigungs-E-Mail wurde an deine neue Adresse gesendet. Überprüfe deinen Posteingang, um die Änderung zu bestätigen.',
   'settings.email_change_error': 'E-Mail-Adresse konnte nicht geändert werden',
   'settings.email_change_sent_hint': 'Klicke auf den Link in der E-Mail an deine neue Adresse, um die Änderung abzuschließen.',
   'settings.email_change_logout_hint': 'Du wurdest aus Sicherheitsgründen abgemeldet. Bestätige die Änderung über die E-Mail an deine neue Adresse und melde dich dann damit an.',
  'settings.security': 'Kontosicherheit',

  'auth.email_changed_title': 'E-Mail aktualisiert ✉️',
  'auth.email_changed_desc': 'Deine E-Mail-Adresse wurde aktualisiert. Du kannst dich jetzt mit deiner neuen Adresse anmelden.',
  'auth.email_changed_error_title': 'Link abgelaufen oder ungültig',
  'auth.email_changed_error_desc': 'Der Bestätigungslink ist abgelaufen oder ungültig. Versuche es erneut in deinen Kontoeinstellungen.',
  'auth.email_change_expired_title': 'Link abgelaufen',
  'auth.email_change_expired_desc': 'Dieser Bestätigungslink ist abgelaufen. Du kannst dich mit deiner alten E-Mail anmelden und die Änderung erneut anfordern.',
  'settings.email_change_warning': 'Du wirst sofort abgemeldet. Eine Bestätigungs-E-Mail wird an die neue Adresse gesendet.',

  // Stock hold
  'hold.created': 'Bestand für 15 Minuten reserviert',
  'hold.failed': 'Nicht genügend Bestand — Reservierung nicht möglich',
  'hold.expired': 'Ihre Reservierung ist abgelaufen. Bitte versuchen Sie es erneut.',
  'hold.cancelled': 'Zahlung abgebrochen — Bestand wurde freigegeben.',
  'hold.item_error': 'Angeforderte Menge für {sku} nicht verfügbar ({available} verfügbar)',
  'hold.retry_cta': 'Erneut versuchen',

  // SEO
  'seo.home_title': 'Sabaody Island — One Piece TCG Europa | Versiegelt geprüft',
  'seo.home_desc': 'Ihr vertrauenswürdiger One Piece TCG Shop in Europa. Versiegelte Displays, Booster, Decks. Geprüft, nicht gewogen. Schneller EU-Versand.',
  'seo.shop_title': 'One Piece TCG Shop — Displays, Booster, Decks | Sabaody',
  'seo.shop_desc': 'Alle One Piece TCG Kategorien: Displays, Booster Boxen, Sets, Starter Decks und Zubehör. Versiegelte Produkte, schnelle Lieferung in Europa.',
  'seo.cart_title': 'Warenkorb — Sabaody Island',
  'seo.cart_desc': 'Überprüfen Sie Ihre One Piece TCG Artikel vor dem sicheren Checkout bei Sabaody Island.',
  'seo.account_title': 'Mein Konto — Sabaody Island',
  'seo.account_desc': 'Verwalten Sie Ihr Sabaody Island Konto: Bestellungen, Adressen, Berry und Treueprogramm.',
  'seo.checkout_title': 'Sicherer Checkout — Sabaody Island',
  'seo.checkout_desc': 'Schließen Sie Ihre One Piece TCG Bestellung sicher ab — schneller Versand in Europa.',
  'seo.category_desc': 'Kaufen Sie versiegelte One Piece TCG {category}. Geprüfte Produkte, nicht gewogen, günstige Preise. Schneller Versand in Europa.',
  // Per-category SEO
  'seo.cat.displays_title': 'One Piece TCG Displays — Versiegelte Booster Boxen | Sabaody',
  'seo.cat.displays_desc': 'Versiegelte One Piece TCG Displays (Booster Boxen) kaufen. Geprüft, nicht gewogen. JP, EN, FR verfügbar. Schneller Versand in Europa.',
  'seo.cat.boosters_title': 'One Piece TCG Booster — Versiegelte Einzelpacks | Sabaody',
  'seo.cat.boosters_desc': 'One Piece TCG Booster-Packs einzeln, versiegelt und nicht getrackt. Alle OP- und EB-Editionen. Schneller EU-Versand.',
  'seo.cat.case_title': 'One Piece TCG Cases — Versiegelte Display-Kartons | Sabaody',
  'seo.cat.case_desc': 'Komplette versiegelte Cases mit One Piece TCG Displays. Ideal für Sammler und Händler. Sicherer Versand in Europa.',
  'seo.cat.binder_title': 'One Piece TCG Ordner — Kartenspeicher & Sammlung | Sabaody',
  'seo.cat.binder_desc': 'Ordner und Portfolios für Ihre One Piece TCG Sammlung. Schützen Sie Ihre Karten stilvoll. Versand in ganz Europa.',
  'seo.cat.acrylic-protectors_title': 'One Piece TCG Acrylschutz — Schutzhüllen | Sabaody',
  'seo.cat.acrylic-protectors_desc': 'Acryl-Schutzhüllen für Ihre One Piece TCG Displays und Booster Boxen. Premium-Qualität, Versand in Europa.',
  'seo.cat.decks_title': 'One Piece TCG Starter Decks — Versiegelte Decks | Sabaody',
  'seo.cat.decks_desc': 'Versiegelte One Piece TCG Starter Decks, spielbereit. Alle Editionen verfügbar. Schneller Versand in Europa.',
  'seo.cat.lots_title': 'One Piece TCG Full Sets — Komplettsets | Sabaody',
  'seo.cat.lots_desc': 'One Piece TCG Full Sets: vorsortierte Komplettsammlungen. Günstig sammeln, Versand in Europa.',
  'seo.cat.displays_intro': 'Auf <strong>Sabaody Island</strong> findest du <strong>One Piece TCG Displays (Booster Boxen)</strong> zum <strong>Öffnen</strong>, <strong>Sammeln</strong> oder für ein <strong>kompetitives Deck</strong>.<br/><ul><li><strong>Editionen</strong>: OP, EB, PRB</li><li><strong>Sprachen</strong>: EN / FR / JPN (je nach Verfügbarkeit)</li><li><strong>Versiegelt</strong>: <strong>nicht getrackt / nicht gewogen</strong></li><li><strong>Bestand</strong>: in Echtzeit aktualisiert</li></ul>Ob du OP15, EB03… suchst oder einfach eine <strong>One Piece Booster Box in Europa</strong> willst: klare Auswahl, Sprachvarianten und EU-Versandoptionen.<br/>Kurs auf Sabaody 🏴\u200d☠️',
  'seo.cat.boosters_intro': 'Lust, ein paar Packs zu öffnen, ohne gleich ein Display zu kaufen?<br/><br/>Hier kannst du <strong>One Piece TCG Boosterpacks</strong> einzeln kaufen. Wähle Edition und Sprache <strong>EN / FR / JPN</strong> je nach Verfügbarkeit und ergänze deine Sammlung in deinem Tempo.<br/><br/>Versiegelte Produkte sind <strong>nicht getrackt / nicht gewogen</strong>, und der Bestand wird laufend aktualisiert.<br/><br/>Ideal, um eine Edition zu testen, ein Deck zu verbessern oder dir einfach etwas zu gönnen.<br/><br/>Willkommen auf Sabaody 🏴\u200d☠️',
  'seo.cat.case_intro': 'Für Sammler und XXL-Openings: Entdecke <strong>One Piece TCG Cases</strong>.<br/><br/>Wähle <strong>EN / FR / JPN</strong>, wenn verfügbar.<br/><br/>Wie immer: versiegelt <strong>nicht getrackt / nicht gewogen</strong>, Bestand in Echtzeit.<br/><br/>Ideal für große Openings, versiegelten Vorrat oder eine bestimmte Edition.<br/><br/>Willkommen auf Sabaody 🏴\u200d☠️',
  'seo.cat.decks_intro': '<strong>One Piece TCG Starter Decks</strong> sind perfekt, um schnell zu spielen und das Spiel zu lernen.<br/><br/>Wähle <strong>EN / FR / JPN</strong> je nach Verfügbarkeit.<br/><br/>Egal ob Einsteiger oder kompetitiv: der einfachste Einstieg!<br/><br/>Willkommen auf Sabaody 🏴\u200d☠️',
  'seo.cat.lots_intro': 'Du willst sammeln, ohne Displays zu kaufen?<br/><br/>Unsere <strong>One Piece TCG Full Sets</strong> sind genau dafür da.<br/><br/>Sie sind <strong>vorsortiert</strong>, damit du leichter in eine Sammlung startest und ein <strong>Master Set</strong> erreichst.<br/><br/>Perfekt, wenn du Zeit sparen, eine Edition vervollständigen oder ein „fertiges" Bundle verschenken willst.<br/><br/>Willkommen auf Sabaody 🏴\u200d☠️',
  'seo.cat.binder_intro': 'Möchtest du deine Sammlung ordentlich aufbewahren und sie entspannt wachsen lassen?<br/><br/>Entdecke unsere <strong>One Piece TCG Ordner</strong>, gemacht für Sammler und Spieler.<br/><br/>Mehrere Formate sind je nach Bedarf verfügbar: 9-Pocket, 12-Pocket und Top-Loader-Format, um deine wertvollsten Karten zu schützen.<br/><br/>Jeder Ordner ist nach Edition gestaltet, damit du deine Sammlung wie ein Profi sortieren kannst!<br/><br/>Willkommen auf Sabaody 🏴\u200d☠️',
  'seo.cat.sleeves_intro': 'Möchtest du dein Deck und deine Lieblingskarten schützen?<br/><br/>Entdecke unsere <strong>One Piece TCG Kartenhüllen</strong>, um Abnutzung zu vermeiden.<br/><br/>Du findest Standardgröße, angenehme Haptik und zuverlässigen Schutz für den Alltag.<br/><br/>Unverzichtbar für entspanntes Spielen und Karten in perfektem Zustand.<br/><br/>Willkommen auf Sabaody 🏴\u200d☠️',
  'seo.cat.acrylic-protectors_intro': 'Möchtest du deine versiegelten Displays und Decks wie echte Schätze präsentieren?<br/><br/>Entdecke unsere <strong>Acryl-Protektoren</strong>, um wichtige Stücke zu präsentieren, zu sichern und zu schützen.<br/><br/>Die Premium-Option gegen Stöße, Staub und häufiges Anfassen – mit einem sauberen „Collector"-Look.<br/><br/>Das maßgeschneiderte Detail, das deine Sammlung zum Strahlen bringt.<br/><br/>Willkommen auf Sabaody 🏴\u200d☠️',
  'seo.cat.storage-boxes_title': 'Aufbewahrungsboxen One Piece TCG | Sabaody',
  'seo.cat.storage-boxes_desc': 'Deck Boxes, Sortierboxen und Aufbewahrungslösungen für deine One Piece TCG Karten. Organisation und Schutz im Alltag. Versand in Europa.',
  'seo.cat.storage-boxes_intro': 'Du brauchst eine Lösung zum Aufbewahren, Sortieren oder Transportieren deiner Karten?<br/><br/>Entdecke unsere <strong>One Piece TCG Aufbewahrungsboxen</strong>: Deck Boxes, Sortierboxen und Lagerlösungen, um deine Sammlung im Alltag zu organisieren.<br/><br/>Ob magnetische Deck Box, Sortierbox nach Edition oder langfristige Aufbewahrung – hier findest du, was du brauchst.<br/><br/>Willkommen auf Sabaody 🏴\u200d☠️',
  'reviews.title': 'Kundenbewertungen',
  'reviews.section_title': 'Kundenbewertungen',
  'reviews.section_subtitle': 'Transparenz, Qualität und Service — Ihr Feedback zählt.',
  'reviews.store_rating': 'Shop-Bewertung',
  'reviews.based_on': 'Basierend auf {count} Bewertungen',
  'reviews.no_reviews': 'Noch keine Bewertungen.',
  'reviews.verified': 'Verifizierter Kauf',
  'reviews.see_all': 'Alle Bewertungen ansehen',
  'reviews.leave_review': 'Bewertung abgeben',
  'reviews.page_title': 'Kundenbewertungen — Sabaody Island',
  'reviews.page_desc': 'Lesen Sie Kundenbewertungen. Transparenz und Qualität garantiert.',
  'reviews.form_title': 'Bewertung abgeben',
  'reviews.form_name': 'Ihr Vorname',
  'reviews.form_name_placeholder': 'z.B. Luffy',
  'reviews.form_country': 'Land',
  'reviews.form_comment': 'Ihre Bewertung',
  'reviews.form_comment_placeholder': 'Teilen Sie Ihre Erfahrung (mind. 10 Zeichen)...',
  'reviews.form_submit': 'Bewertung absenden',
  'reviews.form_success': 'Vielen Dank! Ihre Bewertung wird nach Moderation sichtbar.',
  'reviews.form_error': 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
  'reviews.form_rate_limit': 'Sie haben kürzlich bereits eine Bewertung abgegeben. Versuchen Sie es in 24h erneut.',
  'reviews.form_login_required': 'Melden Sie sich an, um eine Bewertung abzugeben.',
  'reviews.login_to_review': 'Melden Sie sich an, um Ihre Bestellung zu bewerten',
  'reviews.login_to_review_desc': 'Nur Kunden mit einer Bestellung können eine Bewertung abgeben.',
  'reviews.login_cta': 'Anmelden',
  'reviews.no_eligible_orders': 'Keine berechtigten Bestellungen',
  'reviews.no_eligible_orders_desc': 'Nur Kunden, die eine Bestellung erhalten haben, können eine Bewertung abgeben.',
  'reviews.all_orders_reviewed': 'Alle Bewertungen abgegeben!',
  'reviews.all_orders_reviewed_desc': 'Sie haben bereits alle berechtigten Bestellungen bewertet.',
  'reviews.form_title_order': 'Bewerten Sie Ihre Bestellung',
  'reviews.select_order': 'Bestellung auswählen',
  'reviews.select_order_placeholder': 'Bestellung wählen…',
  'seo.product.booster_box': '{setCode} Booster Box ({lang}) — {productName} | Sabaody Island',
  'seo.product.booster_pack': '{setCode} Booster Pack ({lang}) — {productName} | Sabaody Island',
  'seo.product.case': '{setCode} Case ({lang}) — {productName} | Sabaody Island',
  'seo.product.deck': '{setCode} Deck — {productName} | Sabaody Island',
  'seo.product.default': '{name} | Sabaody Island',
  'seo.product.op15_adventure_kamis_island_booster_box_title': 'OP15 Booster Box (JPN/EN/FR) — Adventure on Kami\'s Island | Sabaody Island',
  'seo.product.op15_adventure_kamis_island_booster_box_desc': 'OP15 „Adventure on Kami\'s Island" Booster Box in JPN/EN/FR (je nach Verfügbarkeit). Versiegelt, nicht getrackt, EU-Bestand, EU-Versand.',
  'seo.product_desc.booster_box': 'Kaufen Sie die {setCode} {productName} Booster Box ({lang}), versiegelt und geprüft. Nicht gewogen. Schneller Versand in Europa.',
  'seo.product_desc.booster_pack': 'Einzelner {setCode} {productName} Booster ({lang}), versiegelt und nicht getrackt. Schneller EU-Versand.',
  'seo.product_desc.case': 'Kompletter versiegelter {setCode} {productName} Case ({lang}). Ideal für Sammler und Händler. Sicherer Versand in Europa.',
  'seo.product_desc.deck': '{setCode} {productName} Starter Deck, versiegelt und spielbereit. Schneller Versand in Europa.',
  'seo.loyalty_title': 'One Piece TCG Treueprogramm — Berry & Prämien | Sabaody Island',
  'seo.loyalty_desc': 'Verdienen Sie Berry bei jedem Einkauf und tauschen Sie sie gegen kostenlose Booster, Gratisversand und exklusive Prämien ein.',
  'seo.refund_title': 'Rückerstattungsrichtlinie — Sabaody Island',
  'seo.refund_desc': 'Lesen Sie unsere Rückerstattungs- und Rückgaberichtlinie. 14-Tage-Rückgabe für ungeöffnete versiegelte Produkte.',
  'seo.privacy_title': 'Datenschutzrichtlinie — Sabaody Island',
  'seo.privacy_desc': 'Erfahren Sie, wie Sabaody Island Ihre persönlichen Daten schützt.',
  'seo.terms_title': 'AGB — Sabaody Island',
  'seo.terms_desc': 'Lesen Sie unsere Allgemeinen Geschäftsbedingungen für den Sabaody Island Shop.',
  'seo.product_title': '{name} | Sabaody Island',

  // Admin
  'admin.orders': 'Bestellungen',
  'admin.inventory': 'Inventar',
  'admin.catalog': 'Katalog',
  'admin.newsletter': 'Newsletter',
  'admin.social_rewards': 'Social Rewards',
  'admin.reviews': 'Bewertungen',
  'admin.email_center': 'E-Mail-Center',
  'admin.new_member_banner': 'Banner für neue Mitglieder',

  'social.follow_us': 'Folgt uns',
  'social.follow_desc': 'Folgt uns in den sozialen Medien und verdient',
  'social.per_platform': 'pro Plattform!',
  'social.handle_instruction': 'Gib deinen Benutzernamen ein, damit wir überprüfen können, dass du uns folgst.',
  'social.available': 'Verfügbar',
  'social.pending': 'Ausstehend',
  'social.approved': 'Genehmigt ✅',
  'social.rejected': 'Abgelehnt',
  'social.request_berry': 'Meine +5 Berry anfordern',
  'social.request_submitted': 'Deine Anfrage wird geprüft.',
  'social.request_error': 'Fehler beim Senden der Anfrage.',
  'social.handle_prompt': 'Gib deinen Benutzernamen ein, damit wir überprüfen können, dass du uns folgst.',
  'social.handle_label': 'Dein Benutzername auf dieser Plattform',
  'social.handle_placeholder': 'meinname',
  'social.cancel': 'Abbrechen',
  'social.submit_request': 'Anfrage senden',
  'social.login_required': 'Melde dich an, um Berry zu verdienen',
  'social.newsletter_label': 'Newsletter abonnieren',
  'social.newsletter_updated': 'Newsletter-Einstellung aktualisiert',
  'social.newsletter_active': 'Newsletter aktiviert ✅',
  'social.newsletter_unsubscribe_hint': 'Zum Abbestellen nutzen Sie den Link am Ende der Newsletter-E-Mails.',

  // Referral
  'referral.title': 'Empfehlung',
  'referral.desc': '+50 Berry für dich und +50 Berry für deinen Freund nach seiner ersten bezahlten Bestellung ab 50€.',
  'referral.copy': 'Kopieren',
  'referral.share': 'Link teilen',
  'referral.copied': 'Code kopiert!',
  'referral.link_copied': 'Link kopiert!',
  'referral.share_text': 'Tritt Sabaody Island mit meinem Empfehlungscode bei und verdiene 50 Berry!',
  'referral.edit_code': 'Meinen Code ändern',
  'referral.save': 'Speichern',
  'referral.code_updated': 'Code aktualisiert!',
  'referral.code_locked': 'Code bereits geändert',
  'referral.already_changed': 'Du hast deinen Code bereits einmal geändert.',
  'referral.invalid_format': 'Ungültiges Format (4-16 Zeichen, Buchstaben/Zahlen/Bindestriche).',
  'referral.code_taken': 'Dieser Code ist bereits vergeben.',
  'referral.reward_rule': 'für dich und deinen Freund nach seiner 1. bezahlten Bestellung ≥ 50€',
  'referral.pending': 'Ausstehend',
  'referral.activated': 'Aktiviert',
  'referral.total_earned': 'Insgesamt verdient',

  // Accessibility
  'a11y.skip_to_content': 'Zum Inhalt springen',
  'a11y.close': 'Schließen',
  'a11y.remove_item': 'Artikel entfernen',
  'a11y.decrease_qty': 'Menge verringern',
  'a11y.increase_qty': 'Menge erhöhen',
  'a11y.show_password': 'Passwort anzeigen',
  'a11y.hide_password': 'Passwort ausblenden',
  'a11y.copy': 'Kopieren',
  'a11y.share': 'Teilen',
  'a11y.cancel': 'Abbrechen',
  'a11y.search': 'Suchen',
  'a11y.cart_drawer': 'Warenkorb',
  'a11y.main_navigation': 'Hauptnavigation',
  'a11y.open_menu': 'Menü öffnen',
  'a11y.close_menu': 'Menü schließen',
  'a11y.mobile_menu': 'Navigationsmenü',
};


// ─── ES (Spanish) ────────────────────────────────────────
const es: TranslationDict = {
  'nav.shop': 'Tienda',
  'nav.displays': 'Cajas de sobres',
  'nav.boosters': 'Boosters',
  'nav.lots': 'Full sets',
  'nav.case': 'Caja (Case)',
  'nav.decks': 'Mazos',
  'nav.loyalty': 'Fidelidad',
  'nav.promo': 'Ofertas',
  'promo.title': 'Ofertas',
  'promo.intro': 'Ofertas actuales seleccionadas por Sabaody Island. Los precios y la disponibilidad se actualizan en tiempo real.',
  'promo.empty': 'No hay ofertas por ahora',
  'promo.empty_hint': '¡Vuelve pronto, llegan nuevas ofertas!',
  'promo.browse_shop': 'Ver la tienda',
  'seo.promo_title': 'Ofertas One Piece TCG | Sabaody Island',
  'seo.promo_desc': 'Descubre las ofertas actuales de productos sellados One Piece TCG en Sabaody Island.',
  'nav.search': 'Buscar',
  'nav.cart': 'Carrito',
  'nav.menu': 'Menú',
  'search.placeholder': 'Buscar una carta, set, personaje...',
  'search.card_placeholder': 'Buscar una carta...',
  'hero.badge': 'One Piece TCG — Cartas verificadas',
  'hero.title': 'Sabaody Island',
  'hero.subtitle': 'La referencia One Piece TCG en Europa',
  'hero.subtitle2': '',
  'hero.cta': 'Comprar ahora',
  'hero.new': 'Novedades / Reposiciones',
  'section.new': 'Novedades',
  'section.new_desc': 'Recién llegadas a la isla',
  'section.restock': 'Reposición',
  'section.restock_desc': 'De vuelta en stock, ¡no te las pierdas!',
  'section.bestsellers': 'Los más vendidos',
  'section.bestsellers_desc': 'Los favoritos de la tripulación',
  'section.deals': '🔥 Ofertas especiales',
  'section.deals_desc': 'Descubre nuestras mejores ofertas. ¡Stock limitado, capitán!',
  'section.view_all': 'Ver todo',
  'loyalty.title': 'Programa de fidelidad 🏴‍☠️',
  'loyalty.subtitle': 'Cada compra te acerca al tesoro. Gana puntos, sube de rango, desbloquea recompensas.',
  'loyalty.how': '¿Cómo funciona?',
  'loyalty.step1': 'Compra',
  'loyalty.step1_desc': 'Gana Berry con cada pedido',
  'loyalty.step2': 'Acumula',
  'loyalty.step2_desc': 'Tus Berry se añaden automáticamente',
  'loyalty.step3': 'Disfruta',
  'loyalty.step3_desc': 'Canjea tus Berry por recompensas',
  'loyalty.tiers': 'Rangos',
  'loyalty.your_current_rank': 'Tu rango actual',
  'loyalty.rewards': 'Recompensas',
  'loyalty.cta': 'Empezar a acumular puntos',
  'loyalty.teaser_title': 'Programa de fidelidad 🏴‍☠️',
  'loyalty.teaser_desc': 'Gana puntos con cada compra y desbloquea recompensas exclusivas. ¡Únete a la tripulación!',
  'loyalty.teaser_cta': 'Descubrir el programa de fidelidad',
  'loyalty.earn_rule': '1 € gastado = 1 Berry',
  'loyalty.reward_250': 'Envío gratis',
  'loyalty.reward_500': '3 sobres de regalo',
  'loyalty.reward_1000': '6 sobres de regalo',
  'loyalty.reward_2000': '1 Display de regalo',
  'loyalty.reward_5000': '2 Displays de regalo',
  'loyalty.reward_10000': '3 Displays de regalo',
  'loyalty.perk_5off': '-5 % en cajas de sobres',
  'loyalty.perk_10off': '-10 % en cajas de sobres',
  'loyalty.perk_early': 'Acceso anticipado a novedades',
  'loyalty.rules_title': 'Reglas de canje',
  'loyalty.rule_one_per_order': 'Una recompensa por pedido',
  'loyalty.rule_no_expiry': 'Tus puntos no caducan',
  'loyalty.rule_account_required': 'Cuenta necesaria para acumular y canjear puntos',
  'loyalty.rule_next_order': 'Las recompensas se pueden usar en tu próximo pedido',
  'loyalty.checkout_earn': 'Ganarás {n} puntos con este pedido',
  'tier.moussaillon': 'Grumete',
  'tier.pirate': 'Pirata',
  'tier.captain': 'Capitán',
  'tier.yonko': 'Yonko',
  'tier.tenryubito': 'Tenryubito',
  'tier.roi_des_pirates': 'Rey de los Piratas',
  'account.title': 'Mi cuenta',
  'account.rank': 'Tu rango',
  'account.rank_current': 'Rango actual',
  'account.berry_current': 'Berry actuales',
  'account.next_rank': 'Próximo rango',
  'account.points_to_next': '¡Solo {count} Berry más antes del siguiente rango!',
  'account.max_rank_reached': 'Has alcanzado el rango más alto.',
  'account.balance': 'Berry disponibles',
  'account.lifetime_earned': 'Berry ganados en total',
  'account.history': 'Historial',
  'account.berry_cta': 'Extracto de Berry',
  'account.berry_cta_subtitle': 'Consulta tus ganancias, usos y recompensas.',
  'account.no_transactions': 'Sin transacciones aún',
  'account.redeem': 'Canjear',
  'account.redeem_success': '¡Recompensa canjeada!',
  'account.redeem_next_order': 'Se aplicará en tu próximo pedido',
  'account.redeem_error': 'Error',
  'account.logout': 'Cerrar sesión',
  'account.loading': 'Cargando...',
  'berry_history.title': 'Movimientos Berry',
  'berry_history.empty': 'Aún no hay movimientos Berry.',
  'berry_history.filter_all': 'Todos',
  'berry_history.filter_earnings': 'Ganancias',
  'berry_history.filter_spendings': 'Usos',
  'berry_history.filter_rewards': 'Recompensas',
  'berry_history.filter_orders': 'Pedidos',
  'berry_history.see_more': 'Ver más',
  'berry_history.label_welcome_bonus': 'Bonus de bienvenida',
  'berry_history.label_newsletter': 'Bonus newsletter',
  'berry_history.label_social_instagram': 'Redes sociales — Instagram',
  'berry_history.label_social_facebook': 'Redes sociales — Facebook',
  'berry_history.label_social_tiktok': 'Redes sociales — TikTok',
  'berry_history.label_social': 'Redes sociales —',
  'berry_history.label_review': 'Opinión validada',
  'berry_history.label_referral_out': 'Recomendación — amigo cualificado',
  'berry_history.label_referral_in': 'Bonus recomendación — bienvenida',
  'berry_history.label_free_delivery': 'Envío gratuito',
  'berry_history.label_reward_used': 'Recompensa utilizada',
  'berry_history.label_reward_generic': 'Recompensa',
  'berry_history.label_order_earn': 'Pedido',
  'berry_history.order_suffix': 'Pedido',
  'checkout.loyalty_title': 'Puntos de fidelidad',
  'checkout.loyalty_balance': 'Tu saldo',
  'checkout.free_shipping_toggle': 'Usar 250 Berry: Envío gratis',
  'checkout.free_shipping_helper': 'Disponible porque tienes 250+ Berry. Úsalo ahora o guárdalo.',
  'checkout.free_shipping_applied': 'Envío gratis (250 Berry)',
  'checkout.free_shipping_remaining': 'Después del uso',
  'checkout.rewards_title': 'Recompensas Berry',
  'checkout.reward_none': 'No usar recompensa',
  'checkout.reward_free_shipping': 'Envío gratis',
  'checkout.reward_free_boosters_3': '3 sobres de regalo añadidos al pedido',
  'checkout.reward_free_boosters_6': '6 sobres de regalo añadidos al pedido',
  'checkout.reward_free_display': '1 Display de regalo añadida al pedido',
  'checkout.reward_free_displays_2': '2 Displays de regalo añadidos al pedido',
  'checkout.reward_free_displays_3': '3 Displays de regalo añadidos al pedido',
  'checkout.reward_insufficient': 'Saldo Berry insuficiente',
  'checkout.reward_needs_display': 'Tu carrito debe contener al menos una Display',
  'checkout.reward_pick_display': 'Elige la Display a descontar:',
  'checkout.reward_after_use': 'Después del uso',
  'checkout.reward_gift': '(regalo)',
  'checkout.reward_display_discount_label': 'Descuento Display -10 %',
  'checkout.reward_select_display': 'Selecciona la Display a descontar',
  'checkout.reward_discount_per_unit': '-{amount} en 1 unidad',
  'checkout.gift_pick_edition': 'Elige tus ediciones',
  'checkout.gift_available': 'Disponible',
  'checkout.gift_out_of_stock': 'Agotado',
  'checkout.gift_no_stock': 'Stock insuficiente para esta edición',
  'checkout.gift_incomplete': 'Selecciona todas las ediciones para tu regalo',
  'checkout.gift_remaining': '{count} más por elegir',
  'loyalty.reward_available': 'Recompensa disponible: Envío gratis (próximo pedido)',
  'loyalty.reward_use_at_checkout': 'Usar en el checkout',
  'stock.in_stock': 'En stock',
  'stock.preorder': 'Prepedido',
  'stock.sold_out': 'Agotado',
  'stock.out_of_stock': 'Sin stock',
  'stock.preorder_available': 'Hacer prepedido',
  'stock.available': 'disponible',
  'stock.available_plural': 'disponibles',
  'stock.limited': 'Cantidad limitada',
  'stock.shipped_24_48': 'En stock — Envío en 24–48h',
  'stock.out_label': 'Sin stock',
  'stock.eta': 'Entrega estimada:',
  'stock.eta_on': 'Entrega estimada el',
  'badge.new': 'Nuevo',
  'badge.restock': 'Reposición',
  'badge.bestseller': 'Más vendido',
  'badge.sealed': 'Sellado',
  'product.from': 'Desde',
  'price.coming_soon': 'Próximamente',
  'product.languages': 'idiomas',
  'product.choose_language': 'Elegir idioma',
  'product.choose_binder_type': 'Elegir tipo de carpeta',
  'product.add_to_cart': 'Añadir al carrito',
  'product.preorder': 'Hacer prepedido',
  'product.notify': 'Avisarme',
  'product.not_found': 'Producto no encontrado',
  'product.back_to_shop': 'Página anterior',
  'product.you_may_like': 'También te puede gustar',
  'product.click_to_unzoom': 'Clic para alejar',
  'product.sorted': 'Preordenado numéricamente',
  'product.complete_collection': 'Colección completa (detalles bajo petición)',
  'product.lot_contents': 'Contenido del lote',
  'product.total_cards': 'Total de cartas',
  'product.super_rare': 'Super Rare',
  'product.uncommon': 'Uncommon',
  'product.common': 'Common',
  'product.limited_per_order': 'Limitado a {n} por pedido',
  'product.state': 'Estado:',
  'product.sorted_nm_jp': 'Preordenado • NM/M • Japonés',
  'benefit.pre_sorted': 'Preordenado numéricamente',
  'benefit.pack_fresh': 'Estado recién abierto',
  'benefit.no_display': 'Sin comprar una display',
  'filter.all': 'Todo',
  'filter.filters': 'Filtros',
  'filter.reset': 'Restablecer',
  'filter.set': 'Set / Extensión',
  'filter.rarity': 'Rareza',
  'filter.language': 'Idioma',
  'filter.color': 'Color',
  'filter.all_option': 'Todos',
  'sort.label': 'Ordenar por',
  'sort.newest': 'Novedades',
  'sort.price_asc': 'Precio: de menor a mayor',
  'sort.price_desc': 'Precio: de mayor a menor',
  'sort.popular': 'Popularidad',
  'sort.release_desc': 'Fecha de lanzamiento: más reciente → más antiguo',
  'sort.release_asc': 'Fecha de lanzamiento: más antiguo → más reciente',
  'sort.edition_az': 'Edición: A → Z',
  'sort.edition_za': 'Edición: Z → A',
  'sort.code_desc': 'Código: descendente',
  'sort.code_asc': 'Código: ascendente',
  'sort.stock_first': 'En stock primero',
  'sort.out_of_stock_first': 'Agotado primero',
  'sort.edition_op_first': 'Edición: OP primero',
  'sort.edition_prb_first': 'Edición: PRB primero',
  'sort.edition_eb_first': 'Edición: EB primero',
  'sort.st_asc': 'Número ST: ST01 → ST29',
  'sort.st_desc': 'Número ST: ST29 → ST01',
  'sort.apply': 'Aplicar',
  'sort.reset': 'Restablecer',
  'results': 'resultado',
  'results_plural': 'resultados',
  'no_results': 'Sin resultados',
  'no_results_hint': 'Intenta ajustar los filtros',
  'category.back': 'Página anterior',
  'category.products': 'producto',
  'category.products_plural': 'productos',
  'category.empty': 'No hay productos en esta categoría',
  'category.empty_hint': '¡Vuelve pronto, capitán!',
  'catalog.load_error': 'No se pudo cargar el catálogo. Inténtalo de nuevo en un momento.',
  'catalog.retry_aria': 'Volver a cargar el catálogo',
  'catalog.retry': 'Reintentar',
  'cat.displays': 'Cajas de sobres',
  'cat.boosters': 'Boosters',
  'cat.lots': 'Full sets',
  'cat.case': 'Caja (Case)',
  'cat.decks': 'Mazos preconstruidos',
  'cat.binder': 'Carpetas',
  'cat.sleeves': 'Fundas',
  'cat.acrylic-protectors': 'Protectores acrílicos',
  'cat.storage-boxes': 'Cajas de almacenamiento',
  'nav.accessories': 'Accesorios',
  'nav.binder': 'Carpetas',
  'nav.sleeves': 'Fundas',
  'nav.acrylic-protectors': 'Protectores acrílicos',
  'nav.storage-boxes': 'Cajas de almacenamiento',
  'category.coming_soon': 'Próximamente',
  'category.coming_soon_hint': '¡Esta categoría llegará pronto. Mantente atento, capitán!',
  'cart.title': 'Tu carrito',
  'cart.empty': 'Tu carrito está vacío',
  'cart.explore': 'Descubre nuestros tesoros',
  'cart.clear': 'Vaciar carrito',
  'cart.crosssell_title': 'También te puede interesar',
  'cart.crosssell_subtitle': 'Completa tu pedido en 1 clic',
  'cart.crosssell_add': 'Añadir',
  'cart.crosssell_coming_soon': 'Próximamente',
  'cart.summary': 'Resumen',
  'cart.subtotal': 'Subtotal',
  'cart.shipping': 'Envío',
  'cart.shipping_next': 'Calculado en el siguiente paso',
  'cart.total': 'Total',
  'cart.checkout': 'Pedir',
  'cart.drawer_empty': 'Tu carrito está vacío',
  'cart.drawer_hint': '¡Explora nuestros tesoros para llenarlo!',
  'cart.view_full': 'Ver carrito completo',
  'checkout.title': 'Pedido',
  'checkout.empty': 'Carrito vacío',
  'checkout.back': 'Volver al carrito',
  'checkout.info': 'Información',
  'checkout.firstname': 'Nombre',
  'checkout.lastname': 'Apellido',
  'checkout.email': 'Email',
  'checkout.address': 'Dirección',
  'checkout.zip': 'Código postal',
  'checkout.city': 'Ciudad',
  'checkout.country': 'País',
  'checkout.shipping': 'Método de envío',
  'checkout.shipping_eu': '🇪🇺 Envío en la UE',
  'checkout.payment': 'Pago seguro',
  'checkout.payment_hint': 'El pago se configurará aquí',
  'checkout.pay': 'Proceder al pago',
  'checkout.days': 'días',
  'checkout.redirecting': 'Redirigiendo...',
  'checkout.stripe_redirect_hint': 'Serás redirigido a Stripe para completar el pago de forma segura. La dirección de envío y el email se recogerán durante el pago.',
  'checkout.success_title': '¡Pedido confirmado!',
  'checkout.success_desc': 'Gracias por tu compra. Tu pedido ha sido registrado y recibirás un email de confirmación.',
  'checkout.success_orders': 'Ver mis pedidos',
  'checkout.success_continue': 'Seguir comprando',
  'checkout.order_number': 'Número de pedido',
  'checkout.berry_earned': 'ganados',
  'trust.verified': 'Cartas verificadas',
  'trust.verified_desc': 'Autenticidad garantizada',
  'trust.refund': 'Garantía de devolución',
  'trust.refund_desc': 'Devolución en 14 días',
  'trust.shipping': 'Envío en la UE',
  'trust.shipping_desc': 'Envío rápido 🇪🇺',
  'detail.trust1': 'Cartas verificadas y autenticadas',
  'detail.trust2': 'Devolución si no estás satisfecho (14 días)',
  'detail.trust3': 'Envío UE — Mondial Relay, Chronopost, La Poste, FedEx',
  'detail.notify_back': 'Avisarme cuando esté disponible',
  'binder.subtitle': 'Disponible en 9 bolsillos, 12 bolsillos y toploader.',
  'binder.available': 'Disponible',
  'binder.coming_soon': 'Próximamente',
  'binder.out_of_stock': 'Agotado',
  'binder.notify_cta': 'Avisarme cuando esté disponible',
  'binder.notify_desc': 'Esta carpeta estará disponible pronto. Activa la alerta para ser notificado.',
  'binder.desc_official': 'Carpeta oficial para la edición',
  'binder.desc_formats': 'Formatos disponibles: 9 bolsillos, 12 bolsillos, toploader.',
  'binder.desc_protect': 'Diseñado para organizar y proteger tu colección.',
  'binder.desc_matching': 'Diseño acorde a la edición.',
  'binder.format_9pkt': '9 bolsillos',
  'binder.format_12pkt': '12 bolsillos',
  'binder.format_topload': 'Toploader',
  'binder.title_prefix': 'Carpeta',
  'binder.trust1': 'Compatible con One Piece TCG',
  'binder.trust2': 'Devolución en 14 días',
  'binder.trust3': 'Envío UE',
  'binder.view_product': 'Ver carpeta',
  'binder.subtitle_short': '9 / 12 bolsillos · Toploader',
  'notify.title': 'Avisarme',
  'notify.subtitle': 'Recibir alerta por email',
  'notify.desc': 'Este producto está agotado. Deja tu email para ser notificado cuando vuelva.',
  'notify.submit': 'Avisarme cuando esté disponible',
  'notify.success_title': '¡Anotado!',
  'notify.success_desc': 'Te avisaremos en cuanto el producto esté disponible.',
  'notify.error': 'Se produjo un error. Inténtalo de nuevo.',
  'footer.desc': 'Tu tienda de confianza para One Piece TCG. Cartas verificadas, envío rápido en Europa.',
  'footer.nav': 'Navegación',
  'footer.info': 'Información',
  'footer.cgv': 'Condiciones de venta',
  'footer.refund_policy': 'Política de devoluciones',
  'footer.privacy': 'Política de privacidad',
  'footer.contact': 'Contacto',
  'footer.shipped_from': 'Envío desde Francia 🇫🇷',
  'footer.rights': '© 2026 Sabaody Island. Todos los derechos reservados.',
  'footer.payment_methods': 'Métodos de pago',
  'footer.shipping_partners': 'Socios de envío',
  'shop.title': 'Tienda',
  'shop.browse_categories': 'Explorar por categoría',
  'product.description_heading': 'Descripción',
  'shop.desc': 'Todas nuestras cartas verificadas de One Piece TCG',
  'locale.switch': 'Idioma',
  'bubble.title': 'Explora nuestros tesoros',
  'bubble.swipe_hint': 'Desliza →',
  'notfound.title': 'Página no encontrada',
  'notfound.desc': '¡Oops! Esta página no existe.',
  'notfound.back': 'Volver al inicio',
  'notify.email_placeholder': 'tu@email.com',
  'loyalty.points': 'Berry',
  'loyalty.no_img': 'Sin imagen',
  'misc.no_img': 'Sin imagen',
  'nav.prev': 'Anterior',
  'nav.next': 'Siguiente',

  // Language names
  'lang.Japonais': 'Japonés',
  'lang.Anglais': 'Inglés',
  'lang.Français': 'Francés',
  'lang.JP': 'Japonés',
  'lang.EN': 'Inglés',
  'lang.FR': 'Francés',
  'lang.9-Pocket': '9 fundas',
  'lang.12-Pocket': '12 fundas',
  'lang.Toploader': 'Toploader',

  // Condition values
  'condition.Scellé': 'Sellado',
  'condition.Neuf': 'Nuevo',
  'condition.NM': 'NM',
  'condition.EX': 'EX',
  'condition.GD': 'GD',
  'condition.LP': 'LP',
  'condition.MP': 'MP',

  // Badge values
  'badge_val.Nouveau': 'Nuevo',
  'badge_val.Best-seller': 'Más vendido',
  'badge_val.Restock': 'Reposición',
  'badge_val.Perfect fit': 'Perfect fit',
  'badge_val.Full set': 'Full set',
  'badge_val.Lot': 'Lote',
  'badge_val.Précommande': 'Preventa',
  'badge_val.Accessoire': 'Accesorio',

  // Sealed trust
  'sealed.chip': 'Sellado: sin tracking, sin pesaje',
  'sealed.badge': 'Sin tracking • Sin pesaje',
  'sealed.detail_title': 'Garantía de sellado',
  'sealed.detail_desc': 'Ninguna caja se rastrea ni se pesa. Es nuestra política de tienda.',
  'sealed.checkout': 'Sellado: sin tracking, sin pesaje — Política de confianza Sabaody Island.',

  // Deck filters
  'filter.deck_type': 'Tipo de mazo',
  'filter.availability': 'Disponibilidad',
  'filter.price': 'Precio',
  'deck_type.starter': 'Starter Deck',
  'deck_type.ultimate': 'Ultimate Deck',
  'deck_type.ultra': 'Ultra Deck',
  'deck_type.learn_together': 'Learn Together Deck',
  'deck_type.all': 'Todos los tipos',
  'availability.all': 'Todas',
  'availability.in_stock': 'En stock',
  'availability.preorder': 'Reserva',

  // Auth
  'auth.login': 'Iniciar sesión',
  'auth.signup': 'Crear cuenta',
  'auth.email': 'Email',
  'auth.password': 'Contraseña',
  'auth.forgot_password': '¿Olvidaste tu contraseña?',
  'auth.no_account': '¿No tienes cuenta?',
  'auth.has_account': '¿Ya tienes cuenta?',
  'auth.continue_google': 'Continuar con Google',
  'auth.continue_apple': 'Continuar con Apple',
  'auth.or': 'o',
  'auth.error': 'Error',
  'auth.password_min': '12 caracteres mínimo',
  'auth.password_policy': 'La contraseña debe tener al menos 12 caracteres, con al menos una mayúscula, una minúscula, un número y un carácter especial.',
  'auth.first_name': 'Nombre',
  'auth.last_name': 'Apellido',
  'auth.phone': 'Teléfono (opcional)',
  'auth.address_line1': 'Dirección',
  'auth.address_line2': 'Línea 2 (opcional)',
  'auth.postal_code': 'Código postal',
  'auth.city': 'Ciudad',
  'auth.country': 'País',
  'auth.shipping_info': 'Dirección de envío',
  'auth.check_email': 'Revisa tu email',
  'auth.check_email_desc': 'Se ha enviado un enlace de confirmación. Haz clic para activar tu cuenta.',
  'auth.back_to_login': 'Volver al inicio de sesión',
  'auth.confirmed_title': 'Email confirmado ✅',
  'auth.confirmed_desc': '¡Gracias! Tu cuenta Sabaody Island ya está activada. Puedes iniciar sesión o seguir comprando.',
  'auth.confirmed_shop': 'Seguir comprando',
  'auth.confirmed_error_title': 'Enlace inválido o caducado',
  'auth.confirmed_error_desc': 'Este enlace de confirmación ya no es válido. Regístrate de nuevo o contacta con soporte.',
  'auth.confirmed_resend': 'Volver a registrarse',
  'auth.confirming': 'Verificando…',
  'auth.forgot_desc': 'Introduce tu email para recibir un enlace de restablecimiento.',
  'auth.send_reset': 'Enviar enlace',
  'auth.reset_email_sent': 'Se ha enviado un email de restablecimiento.',
  'auth.reset_password': 'Nueva contraseña',
  'auth.new_password': 'Nueva contraseña',
  'auth.update_password': 'Actualizar',
  'auth.password_updated': '¡Contraseña actualizada!',
  'auth.login_or_signup': 'Iniciar sesión / Crear cuenta',
  'auth.newsletter_optin': 'Suscribirme al boletín',
  'auth.newsletter_berry': '+25 Berry',
  'auth.newsletter_bonus_note': 'Bonus acreditado tras crear la cuenta.',
  'auth.newsletter_privacy': 'Consulta nuestra',
  'auth.newsletter_privacy_link': 'política de privacidad',
  'auth.has_referral': 'Tengo un código de referido',
  'auth.referral_placeholder': 'SAB-XXXX',
  'auth.referral_applied': 'Código aplicado ✅',
  'account.loyalty': 'Fidelidad',
  'orders.title': 'Mis pedidos',
  'orders.empty': 'Sin pedidos aún',
  'orders.order': 'Pedido',
  'orders.not_found': 'Pedido no encontrado',
  'orders.items': 'Artículos',
  'orders.shipping': 'Envío',
  'orders.address': 'Dirección',
  'orders.track': 'Seguir mi pedido',
  'orders.track_copy': 'Copiar número de seguimiento',
  'orders.track_copied': '¡Copiado!',
  'orders.track_hint': 'Introduce tu número de seguimiento y código postal para ver el estado de tu paquete.',
  'orders.track_open_tab': 'Abrir seguimiento en nueva pestaña',
  'orders.track_no_tracking': 'El seguimiento estará disponible cuando tu pedido sea enviado.',
  'orders.track_loading': 'Cargando seguimiento…',
  'orders.track_postal': 'Código postal',
  'order_status.draft': 'Borrador',
  'order_status.pending_payment': 'Pago pendiente',
  'order_status.paid': 'Pagado',
  'order_status.processing': 'En preparación',
  'order_status.in_transit': 'En tránsito',
  'order_status.delivered': 'Entregado',
  'order_status.dispute': 'Disputa',
  'order_status.return_requested': 'Devolución',
  'order_status.fulfilled': 'Enviado',
  'order_status.cancelled': 'Cancelado',
  'order_status.refunded': 'Reembolsado',
  'addresses.title': 'Mis direcciones',
  'addresses.add': 'Añadir dirección',
  'addresses.edit': 'Editar dirección',
  'addresses.empty': 'Sin direcciones guardadas',
  'addresses.full_name': 'Nombre completo',
  'addresses.line1': 'Línea de dirección 1',
  'addresses.line2': 'Línea de dirección 2 (opcional)',
  'addresses.phone': 'Teléfono (opcional)',
  'addresses.set_default': 'Dirección predeterminada',
  'addresses.save': 'Guardar',
  'addresses.fill_required': 'Rellena los campos obligatorios',

  // Guest checkout
  'checkout.contact_title': 'Contacto',
  'checkout.logged_in_as': 'Conectado como',
  'checkout.guest_email_label': 'Email para recibir el seguimiento',
  'checkout.create_account_cta': 'Crear una cuenta (opcional)',
  'checkout.address_title': 'Dirección de envío',
  'checkout.guest_create_account_title': 'Crea una cuenta para seguir tus pedidos',
  'checkout.guest_create_account_desc': 'Sigue tus pedidos, gana Berry con cada compra y desbloquea recompensas.',

  // Settings
  'settings.title': 'Ajustes',
  'settings.change_password': 'Cambiar contraseña',
  'settings.current_password': 'Contraseña actual',
  'settings.new_password': 'Nueva contraseña',
  'settings.confirm_password': 'Confirmar nueva contraseña',
  'settings.save_password': 'Actualizar contraseña',
   'settings.passwords_mismatch': 'Las contraseñas no coinciden',
   'settings.passwords_mismatch_new': 'Las nuevas contraseñas no coinciden',
   'settings.current_password_wrong': 'La contraseña actual es incorrecta',
   'settings.password_updated_success': 'Su contraseña ha sido actualizada correctamente',
  'settings.change_email': 'Cambiar dirección de email',
  'settings.new_email': 'Nueva dirección de email',
  'settings.save_email': 'Actualizar email',
  'settings.email_change_sent': 'Se ha enviado un email de confirmación a tu nueva dirección. Revisa tu bandeja de entrada para confirmar el cambio.',
   'settings.email_change_error': 'No se pudo cambiar la dirección de email',
   'settings.email_change_sent_hint': 'Haz clic en el enlace del email enviado a tu nueva dirección para finalizar el cambio.',
   'settings.email_change_logout_hint': 'Has sido desconectado por seguridad. Confirma el cambio a través del email enviado a tu nueva dirección y luego inicia sesión con ella.',
  'settings.security': 'Seguridad de la cuenta',

  'auth.email_changed_title': 'Email actualizado ✉️',
  'auth.email_changed_desc': 'Tu dirección de email ha sido actualizada. Ahora puedes iniciar sesión con tu nueva dirección.',
  'auth.email_changed_error_title': 'Enlace expirado o inválido',
  'auth.email_changed_error_desc': 'El enlace de confirmación ha expirado o es inválido. Inténtalo de nuevo desde la configuración de tu cuenta.',
  'auth.email_change_expired_title': 'Enlace expirado',
  'auth.email_change_expired_desc': 'Este enlace de confirmación ha expirado. Puedes iniciar sesión con tu email anterior y solicitar el cambio de nuevo.',
  'settings.email_change_warning': 'Se cerrará tu sesión inmediatamente. Se enviará un email de confirmación a la nueva dirección.',

  // Stock hold
  'hold.created': 'Stock reservado durante 15 minutos',
  'hold.failed': 'Stock insuficiente — no se puede reservar',
  'hold.expired': 'Tu reserva ha expirado. Inténtalo de nuevo.',
  'hold.cancelled': 'Pago cancelado — el stock ha sido liberado.',
  'hold.item_error': 'Cantidad solicitada no disponible para {sku} ({available} disponible)',
  'hold.retry_cta': 'Reintentar',

  // SEO
  'seo.home_title': 'Sabaody Island — One Piece TCG Europa | Sellados Verificados',
  'seo.home_desc': 'Tu tienda de confianza de One Piece TCG en Europa. Displays, sobres y barajas sellados y verificados. No pesados. Envío rápido en toda la UE.',
  'seo.shop_title': 'Tienda One Piece TCG — Displays, Sobres, Barajas | Sabaody',
  'seo.shop_desc': 'Explora todas las categorías de One Piece TCG: displays, sobres, lotes, barajas preconstruidas y accesorios. Productos sellados, envío rápido en Europa.',
  'seo.cart_title': 'Carrito — Sabaody Island',
  'seo.cart_desc': 'Revisa los artículos One Piece TCG de tu carrito antes del pago seguro en Sabaody Island.',
  'seo.account_title': 'Mi cuenta — Sabaody Island',
  'seo.account_desc': 'Gestiona tu cuenta Sabaody Island: pedidos, direcciones, Berry y fidelidad One Piece TCG.',
  'seo.checkout_title': 'Pago seguro — Sabaody Island',
  'seo.checkout_desc': 'Finaliza tu pedido One Piece TCG de forma segura con envío rápido en Europa.',
  'seo.category_desc': 'Compra {category} de One Piece TCG sellados. Productos verificados, no pesados, precios competitivos. Envío rápido en Europa.',
  // Per-category SEO
  'seo.cat.displays_title': 'Displays One Piece TCG — Booster Box selladas | Sabaody',
  'seo.cat.displays_desc': 'Compra displays (booster box) One Piece TCG selladas y verificadas. JP, EN, FR disponibles. Envío rápido en Europa.',
  'seo.cat.boosters_title': 'Sobres One Piece TCG — Packs sellados individuales | Sabaody',
  'seo.cat.boosters_desc': 'Sobres de One Piece TCG individuales, sellados y sin rastreo. Todas las ediciones OP y EB. Envío rápido en la UE.',
  'seo.cat.case_title': 'Cases One Piece TCG — Cajas de displays selladas | Sabaody',
  'seo.cat.case_desc': 'Cajas completas (case) de displays One Piece TCG selladas. Ideal para coleccionistas y revendedores. Envío seguro en Europa.',
  'seo.cat.binder_title': 'Álbumes One Piece TCG — Almacenamiento y colección | Sabaody',
  'seo.cat.binder_desc': 'Álbumes y portfolios para tu colección One Piece TCG. Protege tus cartas con estilo. Envío en Europa.',
  'seo.cat.acrylic-protectors_title': 'Protectores Acrílicos One Piece TCG | Sabaody',
  'seo.cat.acrylic-protectors_desc': 'Estuches acrílicos para proteger tus displays y booster box One Piece TCG. Calidad premium, envío en Europa.',
  'seo.cat.decks_title': 'Barajas Preconstruidas One Piece TCG | Sabaody',
  'seo.cat.decks_desc': 'Barajas preconstruidas One Piece TCG selladas, listas para jugar. Todas las ediciones disponibles. Envío rápido en Europa.',
  'seo.cat.lots_title': 'Full Sets One Piece TCG — Colecciones completas | Sabaody',
  'seo.cat.lots_desc': 'Full sets One Piece TCG: colecciones completas pre-ordenadas para coleccionar fácilmente. Envío en Europa.',
  'seo.cat.displays_intro': 'En <strong>Sabaody Island</strong>, encuentra <strong>displays One Piece TCG (booster boxes)</strong> para <strong>abrir sobres</strong>, <strong>coleccionar</strong> o preparar un <strong>deck competitivo</strong>.<br/><ul><li><strong>Ediciones</strong>: OP, EB, PRB</li><li><strong>Idiomas</strong>: EN / FR / JPN (según disponibilidad)</li><li><strong>Sellados</strong>: <strong>sin tracking / sin pesada</strong></li><li><strong>Stock</strong>: actualizado en tiempo real</li></ul>Busques OP15, EB03… o simplemente una <strong>booster box One Piece en Europa</strong>, aquí lo tienes: selección clara, variantes por idioma y opciones de envío en Europa.<br/>Rumbo a Sabaody 🏴\u200d☠️',
  'seo.cat.boosters_intro': '¿Te apetece abrir algunos sobres sin comprar un display?<br/><br/>Aquí puedes comprar <strong>sobres One Piece TCG</strong> por unidad. Elige edición e idioma <strong>EN / FR / JPN</strong> según disponibilidad y completa tu colección a tu ritmo.<br/><br/>Los productos sellados son <strong>sin tracking / sin pesada</strong>, y el stock se actualiza continuamente.<br/><br/>Ideal para probar una expansión, mejorar un deck o simplemente darte un capricho.<br/><br/>Bienvenido a Sabaody 🏴\u200d☠️',
  'seo.cat.case_intro': 'Para coleccionistas y aperturas XXL, descubre <strong>cases One Piece TCG</strong>.<br/><br/>Elige <strong>EN / FR / JPN</strong> cuando esté disponible.<br/><br/>Como siempre: sellados <strong>sin tracking / sin pesada</strong> y stock en tiempo real.<br/><br/>Ideal para abrir a lo grande, asegurar stock sellado o ir a por una edición concreta.<br/><br/>Bienvenido a Sabaody 🏴\u200d☠️',
  'seo.cat.decks_intro': 'Los <strong>decks preconstruidos One Piece TCG</strong> son perfectos para jugar rápido y aprender.<br/><br/>Elige <strong>EN / FR / JPN</strong> según disponibilidad.<br/><br/>Seas principiante o competitivo, ¡es la forma más fácil de empezar!<br/><br/>Bienvenido a Sabaody 🏴\u200d☠️',
  'seo.cat.lots_intro': '¿Quieres coleccionar sin comprar displays?<br/><br/>Nuestros <strong>full sets One Piece TCG</strong> son para ti.<br/><br/>Vienen <strong>pre-ordenados</strong> para empezar una colección y llegar a un <strong>master set</strong> más fácilmente.<br/><br/>Perfecto para ahorrar tiempo, completar una expansión o regalar un lote "listo para usar".<br/><br/>Bienvenido a Sabaody 🏴\u200d☠️',
  'seo.cat.binder_intro': '¿Necesitas guardar tu colección de forma ordenada y hacerla crecer con tranquilidad?<br/><br/>Descubre nuestras <strong>carpetas One Piece TCG</strong> pensadas tanto para coleccionistas como para jugadores.<br/><br/>Habrá varios formatos según tus necesidades: 9 bolsillos, 12 bolsillos y formato Top Loader para proteger tus cartas más valiosas.<br/><br/>¡Cada carpeta está personalizada por edición para organizar tu colección como un profesional!<br/><br/>Bienvenido a Sabaody 🏴\u200d☠️',
  'seo.cat.sleeves_intro': '¿Necesitas proteger tu deck y tus cartas favoritas?<br/><br/>Descubre nuestras <strong>fundas One Piece TCG</strong> para evitar el desgaste.<br/><br/>Encontrarás fundas de tamaño estándar, buenos acabados y una protección fiable para el día a día.<br/><br/>Imprescindible para jugar cómodo y mantener las cartas en perfecto estado.<br/><br/>Bienvenido a Sabaody 🏴\u200d☠️',
  'seo.cat.acrylic-protectors_intro': '¿Quieres lucir tus displays y decks sellados como auténticos tesoros?<br/><br/>Descubre nuestros <strong>protectores acrílicos</strong> para exponer, asegurar y preservar tus piezas importantes.<br/><br/>Es la opción premium para proteger de golpes, polvo y manipulación, manteniendo un aspecto limpio y "collector".<br/><br/>Ese detalle a medida que hace que tu colección brille.<br/><br/>Bienvenido a Sabaody 🏴\u200d☠️',
  'seo.cat.storage-boxes_title': 'Cajas de Almacenamiento One Piece TCG | Sabaody',
  'seo.cat.storage-boxes_desc': 'Deck boxes, cajas de clasificación y almacenamiento para tus cartas One Piece TCG. Organización y protección diaria. Envío en Europa.',
  'seo.cat.storage-boxes_intro': '¿Necesitas guardar, clasificar o transportar tus cartas?<br/><br/>Descubre nuestras <strong>cajas de almacenamiento One Piece TCG</strong>: deck boxes, cajas de clasificación y soluciones de almacenaje para organizar tu colección en el día a día.<br/><br/>Busques una deck box magnética, una caja de clasificación por expansión o almacenamiento a largo plazo, aquí encontrarás lo que necesitas.<br/><br/>Bienvenido a Sabaody 🏴\u200d☠️',
  'reviews.title': 'Opiniones de clientes',
  'reviews.section_title': 'Opiniones de clientes',
  'reviews.section_subtitle': 'Transparencia, calidad y servicio — tu opinión cuenta.',
  'reviews.store_rating': 'Valoración de la tienda',
  'reviews.based_on': 'Basado en {count} opiniones',
  'reviews.no_reviews': 'Sin opiniones por el momento.',
  'reviews.verified': 'Compra verificada',
  'reviews.see_all': 'Ver todas las opiniones',
  'reviews.leave_review': 'Dejar una opinión',
  'reviews.page_title': 'Opiniones de clientes — Sabaody Island',
  'reviews.page_desc': 'Lee las opiniones de nuestros clientes. Transparencia y calidad garantizadas.',
  'reviews.form_title': 'Dejar una opinión',
  'reviews.form_name': 'Tu nombre',
  'reviews.form_name_placeholder': 'Ej: Luffy',
  'reviews.form_country': 'País',
  'reviews.form_comment': 'Tu opinión',
  'reviews.form_comment_placeholder': 'Comparte tu experiencia (mín. 10 caracteres)...',
  'reviews.form_submit': 'Enviar mi opinión',
  'reviews.form_success': '¡Gracias por tu opinión! Será visible tras la moderación.',
  'reviews.form_error': 'Ha ocurrido un error. Inténtalo de nuevo.',
  'reviews.form_rate_limit': 'Ya has enviado una opinión recientemente. Inténtalo en 24h.',
  'reviews.form_login_required': 'Inicia sesión para dejar una opinión.',
  'reviews.login_to_review': 'Inicia sesión para opinar sobre tu pedido',
  'reviews.login_to_review_desc': 'Solo los clientes con un pedido pueden dejar una opinión.',
  'reviews.login_cta': 'Iniciar sesión',
  'reviews.no_eligible_orders': 'Sin pedidos elegibles',
  'reviews.no_eligible_orders_desc': 'Solo los clientes que han recibido un pedido pueden dejar una opinión.',
  'reviews.all_orders_reviewed': '¡Todas las opiniones enviadas!',
  'reviews.all_orders_reviewed_desc': 'Ya has dejado una opinión para todos tus pedidos elegibles.',
  'reviews.form_title_order': 'Opina sobre tu pedido',
  'reviews.select_order': 'Pedido',
  'reviews.select_order_placeholder': 'Selecciona un pedido…',
  'seo.product.booster_box': '{setCode} Booster Box ({lang}) — {productName} | Sabaody Island',
  'seo.product.booster_pack': '{setCode} Sobre ({lang}) — {productName} | Sabaody Island',
  'seo.product.case': '{setCode} Case ({lang}) — {productName} | Sabaody Island',
  'seo.product.deck': '{setCode} Baraja — {productName} | Sabaody Island',
  'seo.product.default': '{name} | Sabaody Island',
  'seo.product.op15_adventure_kamis_island_booster_box_title': 'OP15 Booster Box (JPN/EN/FR) — Adventure on Kami\'s Island | Sabaody Island',
  'seo.product.op15_adventure_kamis_island_booster_box_desc': 'Booster Box OP15 "Adventure on Kami\'s Island" en JPN/EN/FR (según disponibilidad). Sellado sin tracking, stock UE, envío en Europa.',
  'seo.product_desc.booster_box': 'Compra la Booster Box {setCode} {productName} ({lang}), sellada y verificada. No pesada. Envío rápido en Europa.',
  'seo.product_desc.booster_pack': 'Sobre individual {setCode} {productName} ({lang}), sellado y sin rastreo. Envío rápido en la UE.',
  'seo.product_desc.case': 'Case completo {setCode} {productName} ({lang}) sellado. Ideal para coleccionistas y revendedores. Envío seguro en Europa.',
  'seo.product_desc.deck': 'Baraja preconstruida {setCode} {productName}, sellada y lista para jugar. Envío rápido en Europa.',
  'seo.loyalty_title': 'Programa de fidelidad One Piece TCG — Berry & Recompensas | Sabaody Island',
  'seo.loyalty_desc': 'Gana Berry con cada compra y canjéalos por sobres gratis, envío gratuito y recompensas exclusivas.',
  'seo.refund_title': 'Política de reembolso — Sabaody Island',
  'seo.refund_desc': 'Consulta nuestra política de reembolso y devoluciones. Devolución en 14 días para productos sellados sin abrir.',
  'seo.privacy_title': 'Política de privacidad — Sabaody Island',
  'seo.privacy_desc': 'Descubre cómo Sabaody Island protege tus datos personales.',
  'seo.terms_title': 'Términos y condiciones — Sabaody Island',
  'seo.terms_desc': 'Consulta los términos y condiciones de la tienda Sabaody Island.',
  'seo.product_title': '{name} | Sabaody Island',

  // Admin
  'admin.orders': 'Pedidos',
  'admin.inventory': 'Inventario',
  'admin.catalog': 'Catálogo',
  'admin.newsletter': 'Newsletter',
  'admin.social_rewards': 'Redes sociales',
  'admin.reviews': 'Opiniones',
  'admin.email_center': 'Centro de correo',
  'admin.new_member_banner': 'Banner nuevos miembros',

  'social.follow_us': 'Síguenos',
  'social.follow_desc': 'Síguenos en redes sociales y gana',
  'social.per_platform': 'por plataforma!',
  'social.handle_instruction': 'Introduce tu nombre de usuario para que podamos verificar que nos sigues.',
  'social.available': 'Disponible',
  'social.pending': 'Pendiente',
  'social.approved': 'Aprobado ✅',
  'social.rejected': 'Rechazado',
  'social.request_berry': 'Solicitar mis +5 Berry',
  'social.request_submitted': 'Tu solicitud está pendiente de revisión.',
  'social.request_error': 'Error al enviar la solicitud.',
  'social.handle_prompt': 'Introduce tu nombre de usuario para que podamos verificar que nos sigues.',
  'social.handle_label': 'Tu nombre de usuario en esta plataforma',
  'social.handle_placeholder': 'minombre',
  'social.cancel': 'Cancelar',
  'social.submit_request': 'Enviar solicitud',
  'social.login_required': 'Inicia sesión para ganar Berry',
  'social.newsletter_label': 'Suscribirse al boletín',
  'social.newsletter_updated': 'Preferencia de boletín actualizada',
  'social.newsletter_active': 'Boletín activado ✅',
  'social.newsletter_unsubscribe_hint': 'Para cancelar la suscripción, usa el enlace al final de los correos del boletín.',

  // Referral
  'referral.title': 'Referidos',
  'referral.desc': '+50 Berry para ti y +50 Berry para tu amigo/a después de su 1er pedido pagado de 50€ o más.',
  'referral.copy': 'Copiar',
  'referral.share': 'Compartir enlace',
  'referral.copied': '¡Código copiado!',
  'referral.link_copied': '¡Enlace copiado!',
  'referral.share_text': '¡Únete a Sabaody Island con mi código de referido y gana 50 Berry!',
  'referral.edit_code': 'Editar mi código',
  'referral.save': 'Guardar',
  'referral.code_updated': '¡Código actualizado!',
  'referral.code_locked': 'Código ya modificado',
  'referral.already_changed': 'Ya has cambiado tu código una vez.',
  'referral.invalid_format': 'Formato inválido (4-16 caracteres, letras/números/guiones).',
  'referral.code_taken': 'Este código ya está en uso.',
  'referral.reward_rule': 'para ti y tu amigo/a tras su 1er pedido pagado ≥ 50€',
  'referral.pending': 'Pendientes',
  'referral.activated': 'Activados',
  'referral.total_earned': 'Total ganado',

  // Accessibility
  'a11y.skip_to_content': 'Ir al contenido',
  'a11y.close': 'Cerrar',
  'a11y.remove_item': 'Eliminar artículo',
  'a11y.decrease_qty': 'Disminuir cantidad',
  'a11y.increase_qty': 'Aumentar cantidad',
  'a11y.show_password': 'Mostrar contraseña',
  'a11y.hide_password': 'Ocultar contraseña',
  'a11y.copy': 'Copiar',
  'a11y.share': 'Compartir',
  'a11y.cancel': 'Cancelar',
  'a11y.search': 'Buscar',
  'a11y.cart_drawer': 'Carrito',
  'a11y.main_navigation': 'Navegación principal',
  'a11y.open_menu': 'Abrir menú',
  'a11y.close_menu': 'Cerrar menú',
  'a11y.mobile_menu': 'Menú de navegación',
};


// ─── IT (Italian) ────────────────────────────────────────
const it: TranslationDict = {
  'nav.shop': 'Shop',
  'nav.displays': 'Box di buste',
  'nav.boosters': 'Boosters',
  'nav.lots': 'Full set',
  'nav.case': 'Cartone (Case)',
  'nav.decks': 'Mazzi',
  'nav.loyalty': 'Fedeltà',
  'nav.promo': 'Offerte',
  'promo.title': 'Offerte',
  'promo.intro': 'Offerte del momento selezionate da Sabaody Island. Prezzi e disponibilità sono aggiornati in tempo reale.',
  'promo.empty': 'Nessuna offerta al momento',
  'promo.empty_hint': 'Torna presto, nuove offerte in arrivo!',
  'promo.browse_shop': 'Vai allo shop',
  'seo.promo_title': 'Offerte One Piece TCG | Sabaody Island',
  'seo.promo_desc': 'Scopri le offerte del momento sui prodotti sigillati One Piece TCG su Sabaody Island.',
  'nav.search': 'Cerca',
  'nav.cart': 'Carrello',
  'nav.menu': 'Menu',
  'search.placeholder': 'Cerca una carta, set, personaggio...',
  'search.card_placeholder': 'Cerca una carta...',
  'hero.badge': 'One Piece TCG — Carte verificate',
  'hero.title': 'Sabaody Island',
  'hero.subtitle': 'Il riferimento One Piece TCG in Europa',
  'hero.subtitle2': '',
  'hero.cta': 'Acquista ora',
  'hero.new': 'Novità / Riassortimenti',
  'section.new': 'Novità',
  'section.new_desc': 'Appena arrivate sull\'isola',
  'section.restock': 'Riassortimento',
  'section.restock_desc': 'Tornate disponibili, non perderle!',
  'section.bestsellers': 'Più venduti',
  'section.bestsellers_desc': 'I preferiti dell\'equipaggio',
  'section.deals': '🔥 Offerte speciali',
  'section.deals_desc': 'Scopri le nostre migliori offerte. Stock limitato, capitano!',
  'section.view_all': 'Vedi tutto',
  'loyalty.title': 'Programma fedeltà 🏴‍☠️',
  'loyalty.subtitle': 'Ogni acquisto ti avvicina al tesoro. Guadagna punti, sali di grado, sblocca premi.',
  'loyalty.how': 'Come funziona?',
  'loyalty.step1': 'Acquista',
  'loyalty.step1_desc': 'Guadagna Berry a ogni ordine',
  'loyalty.step2': 'Accumula',
  'loyalty.step2_desc': 'I tuoi Berry vengono aggiunti automaticamente',
  'loyalty.step3': 'Approfitta',
  'loyalty.step3_desc': 'Riscatta i tuoi Berry per premi',
  'loyalty.tiers': 'Gradi',
  'loyalty.your_current_rank': 'Il tuo rango attuale',
  'loyalty.rewards': 'Premi',
  'loyalty.cta': 'Inizia a raccogliere punti',
  'loyalty.teaser_title': 'Programma fedeltà 🏴‍☠️',
  'loyalty.teaser_desc': 'Guadagna punti con ogni acquisto e sblocca premi esclusivi. Unisciti alla ciurma!',
  'loyalty.teaser_cta': 'Scopri il programma fedeltà',
  'loyalty.earn_rule': '1 € speso = 1 Berry',
  'loyalty.reward_250': 'Consegna gratuita',
  'loyalty.reward_500': '3 buste regalo',
  'loyalty.reward_1000': '6 buste regalo',
  'loyalty.reward_2000': '1 Display in regalo',
  'loyalty.reward_5000': '2 Display in regalo',
  'loyalty.reward_10000': '3 Display in regalo',
  'loyalty.perk_5off': '-5 % sui box di buste',
  'loyalty.perk_10off': '-10 % sui box di buste',
  'loyalty.perk_early': 'Accesso anticipato alle novità',
  'loyalty.rules_title': 'Regole di riscatto',
  'loyalty.rule_one_per_order': 'Un solo premio per ordine',
  'loyalty.rule_no_expiry': 'I tuoi punti non scadono mai',
  'loyalty.rule_account_required': 'Account necessario per accumulare e riscattare punti',
  'loyalty.rule_next_order': 'I premi possono essere utilizzati al prossimo ordine',
  'loyalty.checkout_earn': 'Guadagnerai {n} punti con questo ordine',
  'tier.moussaillon': 'Mozzo',
  'tier.pirate': 'Pirata',
  'tier.captain': 'Capitano',
  'tier.yonko': 'Yonko',
  'tier.tenryubito': 'Tenryubito',
  'tier.roi_des_pirates': 'Re dei Pirati',
  'account.title': 'Il mio account',
  'account.rank': 'Il tuo rango',
  'account.rank_current': 'Rango attuale',
  'account.berry_current': 'Berry attuali',
  'account.next_rank': 'Prossimo rango',
  'account.points_to_next': 'Ancora {count} Berry prima del rango superiore!',
  'account.max_rank_reached': 'Hai raggiunto il rango più alto.',
  'account.balance': 'Berry disponibili',
  'account.lifetime_earned': 'Berry guadagnati in totale',
  'account.history': 'Cronologia',
  'account.berry_cta': 'Estratto Berry',
  'account.berry_cta_subtitle': 'Consulta i tuoi guadagni, utilizzi e premi.',
  'account.no_transactions': 'Nessuna transazione',
  'account.redeem': 'Riscatta',
  'account.redeem_success': 'Premio riscattato!',
  'account.redeem_next_order': 'Sarà applicato al prossimo ordine',
  'account.redeem_error': 'Errore',
  'account.logout': 'Esci',
  'account.loading': 'Caricamento...',
  'berry_history.title': 'Movimenti Berry',
  'berry_history.empty': 'Nessun movimento Berry al momento.',
  'berry_history.filter_all': 'Tutti',
  'berry_history.filter_earnings': 'Guadagni',
  'berry_history.filter_spendings': 'Utilizzi',
  'berry_history.filter_rewards': 'Premi',
  'berry_history.filter_orders': 'Ordini',
  'berry_history.see_more': 'Mostra altro',
  'berry_history.label_welcome_bonus': 'Bonus di benvenuto',
  'berry_history.label_newsletter': 'Bonus newsletter',
  'berry_history.label_social_instagram': 'Social media — Instagram',
  'berry_history.label_social_facebook': 'Social media — Facebook',
  'berry_history.label_social_tiktok': 'Social media — TikTok',
  'berry_history.label_social': 'Social media —',
  'berry_history.label_review': 'Recensione approvata',
  'berry_history.label_referral_out': 'Referral — amico qualificato',
  'berry_history.label_referral_in': 'Bonus referral — benvenuto',
  'berry_history.label_free_delivery': 'Spedizione gratuita',
  'berry_history.label_reward_used': 'Premio utilizzato',
  'berry_history.label_reward_generic': 'Premio',
  'berry_history.label_order_earn': 'Ordine',
  'berry_history.order_suffix': 'Ordine',
  'checkout.loyalty_title': 'Punti fedeltà',
  'checkout.loyalty_balance': 'Il tuo saldo',
  'checkout.free_shipping_toggle': 'Usa 250 Berry: Consegna gratuita',
  'checkout.free_shipping_helper': 'Disponibile perché hai 250+ Berry. Usalo ora o conservalo.',
  'checkout.free_shipping_applied': 'Consegna gratuita (250 Berry)',
  'checkout.free_shipping_remaining': 'Dopo l\'uso',
  'checkout.rewards_title': 'Premi Berry',
  'checkout.reward_none': 'Non usare un premio',
  'checkout.reward_free_shipping': 'Consegna gratuita',
  'checkout.reward_free_boosters_3': '3 buste regalo aggiunte all\'ordine',
  'checkout.reward_free_boosters_6': '6 buste regalo aggiunte all\'ordine',
  'checkout.reward_free_display': '1 Display regalo aggiunta all\'ordine',
  'checkout.reward_free_displays_2': '2 Display regalo aggiunte all\'ordine',
  'checkout.reward_free_displays_3': '3 Display regalo aggiunte all\'ordine',
  'checkout.reward_insufficient': 'Saldo Berry insufficiente',
  'checkout.reward_needs_display': 'Il carrello deve contenere almeno una Display',
  'checkout.reward_pick_display': 'Scegli la Display da scontare:',
  'checkout.reward_after_use': 'Dopo l\'uso',
  'checkout.reward_gift': '(regalo)',
  'checkout.reward_display_discount_label': 'Sconto Display -10 %',
  'checkout.reward_select_display': 'Seleziona la Display da scontare',
  'checkout.reward_discount_per_unit': '-{amount} su 1 unità',
  'checkout.gift_pick_edition': 'Scegli le tue edizioni',
  'checkout.gift_available': 'Disponibile',
  'checkout.gift_out_of_stock': 'Esaurito',
  'checkout.gift_no_stock': 'Stock insufficiente per questa edizione',
  'checkout.gift_incomplete': 'Seleziona tutte le edizioni per il tuo regalo',
  'checkout.gift_remaining': 'Ancora {count} da scegliere',
  'loyalty.reward_available': 'Ricompensa disponibile: Consegna gratuita (prossimo ordine)',
  'loyalty.reward_use_at_checkout': 'Usa al checkout',
  'stock.in_stock': 'Disponibile',
  'stock.preorder': 'Preordine',
  'stock.sold_out': 'Esaurito',
  'stock.out_of_stock': 'Non disponibile',
  'stock.preorder_available': 'Preordina',
  'stock.available': 'disponibile',
  'stock.available_plural': 'disponibili',
  'stock.limited': 'Quantità limitata',
  'stock.shipped_24_48': 'Disponibile — Spedizione in 24–48h',
  'stock.out_label': 'Non disponibile',
  'stock.eta': 'Consegna stimata:',
  'stock.eta_on': 'Consegna stimata il',
  'badge.new': 'Nuovo',
  'badge.restock': 'Riassortimento',
  'badge.bestseller': 'Più venduto',
  'badge.sealed': 'Sigillato',
  'product.from': 'Da',
  'price.coming_soon': 'Disponibile a breve',
  'product.languages': 'lingue',
  'product.choose_language': 'Scegli la lingua',
  'product.choose_binder_type': 'Scegli il tipo di raccoglitore',
  'product.add_to_cart': 'Aggiungi al carrello',
  'product.preorder': 'Preordina',
  'product.notify': 'Avvisami',
  'product.not_found': 'Prodotto non trovato',
  'product.back_to_shop': 'Pagina precedente',
  'product.you_may_like': 'Potrebbe piacerti anche',
  'product.click_to_unzoom': 'Clicca per ridurre lo zoom',
  'product.sorted': 'Preordinato numericamente',
  'product.complete_collection': 'Collezione completa (dettagli su richiesta)',
  'product.lot_contents': 'Contenuto del lotto',
  'product.total_cards': 'Totale carte',
  'product.super_rare': 'Super Rare',
  'product.uncommon': 'Uncommon',
  'product.common': 'Common',
  'product.limited_per_order': 'Limitato a {n} per ordine',
  'product.state': 'Condizione:',
  'product.sorted_nm_jp': 'Preordinato • NM/M • Giapponese',
  'benefit.pre_sorted': 'Preordinato in ordine numerico',
  'benefit.pack_fresh': 'Condizione appena aperto',
  'benefit.no_display': 'Senza comprare un display',
  'filter.all': 'Tutto',
  'filter.filters': 'Filtri',
  'filter.reset': 'Ripristina',
  'filter.set': 'Set / Espansione',
  'filter.rarity': 'Rarità',
  'filter.language': 'Lingua',
  'filter.color': 'Colore',
  'filter.all_option': 'Tutti',
  'sort.label': 'Ordina per',
  'sort.newest': 'Novità',
  'sort.price_asc': 'Prezzo: crescente',
  'sort.price_desc': 'Prezzo: decrescente',
  'sort.popular': 'Popolarità',
  'sort.release_desc': 'Data di uscita: più recente → più vecchia',
  'sort.release_asc': 'Data di uscita: più vecchia → più recente',
  'sort.edition_az': 'Edizione: A → Z',
  'sort.edition_za': 'Edizione: Z → A',
  'sort.code_desc': 'Codice: decrescente',
  'sort.code_asc': 'Codice: crescente',
  'sort.stock_first': 'Disponibili prima',
  'sort.out_of_stock_first': 'Esauriti prima',
  'sort.edition_op_first': 'Edizione: OP prima',
  'sort.edition_prb_first': 'Edizione: PRB prima',
  'sort.edition_eb_first': 'Edizione: EB prima',
  'sort.st_asc': 'Numero ST: ST01 → ST29',
  'sort.st_desc': 'Numero ST: ST29 → ST01',
  'sort.apply': 'Applica',
  'sort.reset': 'Reimposta',
  'results': 'risultato',
  'results_plural': 'risultati',
  'no_results': 'Nessun risultato trovato',
  'no_results_hint': 'Prova a modificare i filtri',
  'category.back': 'Pagina precedente',
  'category.products': 'prodotto',
  'category.products_plural': 'prodotti',
  'category.empty': 'Nessun prodotto in questa categoria',
  'category.empty_hint': 'Torna presto, capitano!',
  'catalog.load_error': 'Impossibile caricare il catalogo. Riprova tra un momento.',
  'catalog.retry_aria': 'Ricarica il catalogo',
  'catalog.retry': 'Riprova',
  'cat.displays': 'Box di buste',
  'cat.boosters': 'Boosters',
  'cat.lots': 'Full set',
  'cat.case': 'Cartone (Case)',
  'cat.decks': 'Mazzi pre-costruiti',
  'cat.binder': 'Raccoglitori',
  'cat.sleeves': 'Bustine',
  'cat.acrylic-protectors': 'Protettori acrilici',
  'cat.storage-boxes': 'Scatole di stoccaggio',
  'nav.accessories': 'Accessori',
  'nav.binder': 'Raccoglitori',
  'nav.sleeves': 'Bustine',
  'nav.acrylic-protectors': 'Protettori acrilici',
  'nav.storage-boxes': 'Scatole di stoccaggio',
  'category.coming_soon': 'Disponibile a breve',
  'category.coming_soon_hint': 'Questa categoria arriverà presto. Resta sintonizzato, capitano!',
  'cart.title': 'Il tuo carrello',
  'cart.empty': 'Il tuo carrello è vuoto',
  'cart.explore': 'Scopri i nostri tesori',
  'cart.clear': 'Svuota carrello',
  'cart.crosssell_title': 'Potrebbe piacerti anche',
  'cart.crosssell_subtitle': 'Completa il tuo ordine in 1 clic',
  'cart.crosssell_add': 'Aggiungi',
  'cart.crosssell_coming_soon': 'In arrivo',
  'cart.summary': 'Riepilogo',
  'cart.subtotal': 'Subtotale',
  'cart.shipping': 'Spedizione',
  'cart.shipping_next': 'Calcolata al passaggio successivo',
  'cart.total': 'Totale',
  'cart.checkout': 'Ordina',
  'cart.drawer_empty': 'Il tuo carrello è vuoto',
  'cart.drawer_hint': 'Esplora i nostri tesori per riempirlo!',
  'cart.view_full': 'Vedi carrello completo',
  'checkout.title': 'Ordine',
  'checkout.empty': 'Carrello vuoto',
  'checkout.back': 'Torna al carrello',
  'checkout.info': 'Informazioni',
  'checkout.firstname': 'Nome',
  'checkout.lastname': 'Cognome',
  'checkout.email': 'Email',
  'checkout.address': 'Indirizzo',
  'checkout.zip': 'CAP',
  'checkout.city': 'Città',
  'checkout.country': 'Paese',
  'checkout.shipping': 'Metodo di spedizione',
  'checkout.shipping_eu': '🇪🇺 Consegna UE',
  'checkout.payment': 'Pagamento sicuro',
  'checkout.payment_hint': 'Il pagamento verrà configurato qui',
  'checkout.pay': 'Procedere al pagamento',
  'checkout.days': 'giorni',
  'checkout.redirecting': 'Reindirizzamento...',
  'checkout.stripe_redirect_hint': 'Verrai reindirizzato a Stripe per completare il pagamento in sicurezza. Indirizzo di spedizione e email saranno raccolti durante il pagamento.',
  'checkout.success_title': 'Ordine confermato!',
  'checkout.success_desc': 'Grazie per il tuo acquisto. Il tuo ordine è stato registrato e riceverai un\'email di conferma.',
  'checkout.success_orders': 'Vedi i miei ordini',
  'checkout.success_continue': 'Continua lo shopping',
  'checkout.order_number': 'Numero ordine',
  'checkout.berry_earned': 'guadagnati',
  'trust.verified': 'Carte verificate',
  'trust.verified_desc': 'Autenticità garantita',
  'trust.refund': 'Garanzia soddisfatti o rimborsati',
  'trust.refund_desc': 'Reso entro 14 giorni',
  'trust.shipping': 'Consegna UE',
  'trust.shipping_desc': 'Spedizione veloce 🇪🇺',
  'detail.trust1': 'Carte verificate e autenticate',
  'detail.trust2': 'Rimborso se non soddisfatto (14 giorni)',
  'detail.trust3': 'Consegna UE — Mondial Relay, Chronopost, La Poste, FedEx',
  'detail.notify_back': 'Avvisami quando torna disponibile',
  'binder.subtitle': 'Disponibile nei formati 9 tasche, 12 tasche e toploader.',
  'binder.available': 'Disponibile',
  'binder.coming_soon': 'Disponibile a breve',
  'binder.out_of_stock': 'Esaurito',
  'binder.notify_cta': 'Avvisami quando torna disponibile',
  'binder.notify_desc': 'Questo raccoglitore sarà presto disponibile. Attiva l\'avviso per essere notificato.',
  'binder.desc_official': 'Raccoglitore ufficiale per l\'edizione',
  'binder.desc_formats': 'Formati disponibili: 9 tasche, 12 tasche, toploader.',
  'binder.desc_protect': 'Progettato per organizzare e proteggere la tua collezione.',
  'binder.desc_matching': 'Design abbinato all\'edizione.',
  'binder.format_9pkt': '9 tasche',
  'binder.format_12pkt': '12 tasche',
  'binder.format_topload': 'Toploader',
  'binder.title_prefix': 'Raccoglitore',
  'binder.trust1': 'Compatibile con One Piece TCG',
  'binder.trust2': 'Rimborso entro 14 giorni',
  'binder.trust3': 'Consegna UE',
  'binder.view_product': 'Vedi raccoglitore',
  'binder.subtitle_short': '9 / 12 tasche · Toploader',
  'notify.title': 'Avvisami',
  'notify.subtitle': 'Ricevi un avviso via email',
  'notify.desc': 'Questo prodotto è al momento esaurito. Lascia la tua email per essere avvisato del suo ritorno.',
  'notify.submit': 'Avvisami quando disponibile',
  'notify.success_title': 'Annotato!',
  'notify.success_desc': 'Sarai avvisato non appena il prodotto sarà disponibile.',
  'notify.error': 'Si è verificato un errore. Riprova.',
  'footer.desc': 'Il tuo shop di fiducia per One Piece TCG. Carte verificate, spedizione veloce in Europa.',
  'footer.nav': 'Navigazione',
  'footer.info': 'Informazioni',
  'footer.cgv': 'Condizioni di vendita',
  'footer.refund_policy': 'Politica di rimborso',
  'footer.privacy': 'Politica sulla privacy',
  'footer.contact': 'Contatto',
  'footer.shipped_from': 'Spedizione dalla Francia 🇫🇷',
  'footer.rights': '© 2026 Sabaody Island. Tutti i diritti riservati.',
  'footer.payment_methods': 'Metodi di pagamento',
  'footer.shipping_partners': 'Partner di spedizione',
  'shop.title': 'Shop',
  'shop.browse_categories': 'Sfoglia per categoria',
  'product.description_heading': 'Descrizione',
  'shop.desc': 'Tutte le nostre carte One Piece TCG verificate',
  'locale.switch': 'Lingua',
  'bubble.title': 'Esplora i nostri tesori',
  'bubble.swipe_hint': 'Scorri →',
  'notfound.title': 'Pagina non trovata',
  'notfound.desc': 'Oops! Questa pagina non esiste.',
  'notfound.back': 'Torna alla home',
  'notify.email_placeholder': 'tua@email.it',
  'loyalty.points': 'Berry',
  'loyalty.no_img': 'Nessuna immagine',
  'misc.no_img': 'Nessuna immagine',
  'nav.prev': 'Precedente',
  'nav.next': 'Successivo',

  // Language names
  'lang.Japonais': 'Giapponese',
  'lang.Anglais': 'Inglese',
  'lang.Français': 'Francese',
  'lang.JP': 'Giapponese',
  'lang.EN': 'Inglese',
  'lang.FR': 'Francese',
  'lang.9-Pocket': '9 tasche',
  'lang.12-Pocket': '12 tasche',
  'lang.Toploader': 'Toploader',

  // Condition values
  'condition.Scellé': 'Sigillato',
  'condition.Neuf': 'Nuovo',
  'condition.NM': 'NM',
  'condition.EX': 'EX',
  'condition.GD': 'GD',
  'condition.LP': 'LP',
  'condition.MP': 'MP',

  // Badge values
  'badge_val.Nouveau': 'Novità',
  'badge_val.Best-seller': 'Più venduto',
  'badge_val.Restock': 'Riassortimento',
  'badge_val.Perfect fit': 'Perfect fit',
  'badge_val.Full set': 'Full set',
  'badge_val.Lot': 'Set completo',
  'badge_val.Précommande': 'Preordine',
  'badge_val.Accessoire': 'Accessorio',

  // Sealed trust
  'sealed.chip': 'Sigillato: non tracciato, non pesato',
  'sealed.badge': 'Non tracciato • Non pesato',
  'sealed.detail_title': 'Garanzia sigillato',
  'sealed.detail_desc': 'Nessuna box viene tracciata o pesata. È la nostra politica di negozio.',
  'sealed.checkout': 'Sigillato: non tracciato, non pesato — Politica di fiducia Sabaody Island.',

  // Deck filters
  'filter.deck_type': 'Tipo di mazzo',
  'filter.availability': 'Disponibilità',
  'filter.price': 'Prezzo',
  'deck_type.starter': 'Starter Deck',
  'deck_type.ultimate': 'Ultimate Deck',
  'deck_type.ultra': 'Ultra Deck',
  'deck_type.learn_together': 'Learn Together Deck',
  'deck_type.all': 'Tutti i tipi',
  'availability.all': 'Tutte',
  'availability.in_stock': 'Disponibile',
  'availability.preorder': 'Preordine',

  // Auth
  'auth.login': 'Accedi',
  'auth.signup': 'Crea account',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.forgot_password': 'Password dimenticata?',
  'auth.no_account': 'Non hai un account?',
  'auth.has_account': 'Hai già un account?',
  'auth.continue_google': 'Continua con Google',
  'auth.continue_apple': 'Continua con Apple',
  'auth.or': 'o',
  'auth.error': 'Errore',
  'auth.password_min': 'Minimo 12 caratteri',
  'auth.password_policy': 'La password deve contenere almeno 12 caratteri, con almeno una lettera maiuscola, una minuscola, un numero e un carattere speciale.',
  'auth.first_name': 'Nome',
  'auth.last_name': 'Cognome',
  'auth.phone': 'Telefono (opzionale)',
  'auth.address_line1': 'Indirizzo',
  'auth.address_line2': 'Indirizzo aggiuntivo (opzionale)',
  'auth.postal_code': 'Codice postale',
  'auth.city': 'Città',
  'auth.country': 'Paese',
  'auth.shipping_info': 'Indirizzo di spedizione',
  'auth.check_email': 'Controlla la tua email',
  'auth.check_email_desc': 'È stato inviato un link di conferma. Cliccalo per attivare il tuo account.',
  'auth.back_to_login': 'Torna al login',
  'auth.confirmed_title': 'Email confermata ✅',
  'auth.confirmed_desc': 'Grazie! Il tuo account Sabaody Island è ora attivo. Puoi accedere o continuare a fare shopping.',
  'auth.confirmed_shop': 'Continua lo shopping',
  'auth.confirmed_error_title': 'Link non valido o scaduto',
  'auth.confirmed_error_desc': 'Questo link di conferma non è più valido. Registrati di nuovo o contatta il supporto.',
  'auth.confirmed_resend': 'Riprova la registrazione',
  'auth.confirming': 'Verifica in corso…',
  'auth.forgot_desc': 'Inserisci la tua email per ricevere un link di reimpostazione.',
  'auth.send_reset': 'Invia link',
  'auth.reset_email_sent': 'È stata inviata un\'email di reimpostazione.',
  'auth.reset_password': 'Nuova password',
  'auth.new_password': 'Nuova password',
  'auth.update_password': 'Aggiorna',
  'auth.password_updated': 'Password aggiornata!',
  'auth.login_or_signup': 'Accedi / Crea account',
  'auth.newsletter_optin': 'Iscrivimi alla newsletter',
  'auth.newsletter_berry': '+25 Berry',
  'auth.newsletter_bonus_note': 'Bonus accreditato dopo la creazione dell\'account.',
  'auth.newsletter_privacy': 'Consulta la nostra',
  'auth.newsletter_privacy_link': 'informativa sulla privacy',
  'auth.has_referral': 'Ho un codice referral',
  'auth.referral_placeholder': 'SAB-XXXX',
  'auth.referral_applied': 'Codice applicato ✅',
  'account.loyalty': 'Fedeltà',
  'orders.title': 'I miei ordini',
  'orders.empty': 'Nessun ordine per ora',
  'orders.order': 'Ordine',
  'orders.not_found': 'Ordine non trovato',
  'orders.items': 'Articoli',
  'orders.shipping': 'Spedizione',
  'orders.address': 'Indirizzo',
  'orders.track': 'Traccia il mio ordine',
  'orders.track_copy': 'Copia numero di tracciamento',
  'orders.track_copied': 'Copiato!',
  'orders.track_hint': 'Inserisci il numero di tracciamento e il codice postale per vedere lo stato del tuo pacco.',
  'orders.track_open_tab': 'Apri tracciamento in una nuova scheda',
  'orders.track_no_tracking': 'Il tracciamento sarà disponibile quando il tuo ordine sarà spedito.',
  'orders.track_loading': 'Caricamento tracciamento…',
  'orders.track_postal': 'Codice postale',
  'order_status.draft': 'Bozza',
  'order_status.pending_payment': 'In attesa di pagamento',
  'order_status.paid': 'Pagato',
  'order_status.processing': 'In preparazione',
  'order_status.in_transit': 'In transito',
  'order_status.delivered': 'Consegnato',
  'order_status.dispute': 'Contestazione',
  'order_status.return_requested': 'Reso richiesto',
  'order_status.fulfilled': 'Spedito',
  'order_status.cancelled': 'Annullato',
  'order_status.refunded': 'Rimborsato',
  'addresses.title': 'I miei indirizzi',
  'addresses.add': 'Aggiungi indirizzo',
  'addresses.edit': 'Modifica indirizzo',
  'addresses.empty': 'Nessun indirizzo salvato',
  'addresses.full_name': 'Nome completo',
  'addresses.line1': 'Indirizzo riga 1',
  'addresses.line2': 'Indirizzo riga 2 (opzionale)',
  'addresses.phone': 'Telefono (opzionale)',
  'addresses.set_default': 'Indirizzo predefinito',
  'addresses.save': 'Salva',
  'addresses.fill_required': 'Compila i campi obbligatori',

  // Guest checkout
  'checkout.contact_title': 'Contatto',
  'checkout.logged_in_as': 'Connesso come',
  'checkout.guest_email_label': 'Email per ricevere il tracking',
  'checkout.create_account_cta': 'Crea un account (opzionale)',
  'checkout.address_title': 'Indirizzo di spedizione',
  'checkout.guest_create_account_title': 'Crea un account per seguire i tuoi ordini',
  'checkout.guest_create_account_desc': 'Segui i tuoi ordini, guadagna Berry a ogni acquisto e sblocca premi.',

  // Settings
  'settings.title': 'Impostazioni',
  'settings.change_password': 'Cambia password',
  'settings.current_password': 'Password attuale',
  'settings.new_password': 'Nuova password',
  'settings.confirm_password': 'Conferma nuova password',
  'settings.save_password': 'Aggiorna password',
   'settings.passwords_mismatch': 'Le password non corrispondono',
   'settings.passwords_mismatch_new': 'Le nuove password non corrispondono',
   'settings.current_password_wrong': 'La password attuale non è corretta',
   'settings.password_updated_success': 'La password è stata aggiornata con successo',
  'settings.change_email': 'Cambia indirizzo email',
  'settings.new_email': 'Nuovo indirizzo email',
  'settings.save_email': 'Aggiorna email',
  'settings.email_change_sent': 'Un\'email di conferma è stata inviata al tuo nuovo indirizzo. Controlla la tua casella di posta per confermare la modifica.',
   'settings.email_change_error': 'Impossibile cambiare l\'indirizzo email',
   'settings.email_change_sent_hint': 'Clicca sul link nell\'email inviata al tuo nuovo indirizzo per completare la modifica.',
   'settings.email_change_logout_hint': 'Sei stato disconnesso per sicurezza. Conferma la modifica tramite l\'email inviata al tuo nuovo indirizzo, poi accedi con quello.',
  'settings.security': 'Sicurezza dell\'account',

  'auth.email_changed_title': 'Email aggiornata ✉️',
  'auth.email_changed_desc': 'Il tuo indirizzo email è stato aggiornato. Ora puoi accedere con il tuo nuovo indirizzo.',
  'auth.email_changed_error_title': 'Link scaduto o non valido',
  'auth.email_changed_error_desc': 'Il link di conferma è scaduto o non è valido. Riprova dalle impostazioni del tuo account.',
  'auth.email_change_expired_title': 'Link scaduto',
  'auth.email_change_expired_desc': 'Questo link di conferma è scaduto. Puoi accedere con la tua vecchia email e richiedere di nuovo il cambio.',
  'settings.email_change_warning': 'Sarai disconnesso immediatamente. Un\'email di conferma sarà inviata al nuovo indirizzo.',

  // Stock hold
  'hold.created': 'Stock riservato per 15 minuti',
  'hold.failed': 'Stock insufficiente — impossibile riservare',
  'hold.expired': 'La tua prenotazione è scaduta. Riprova.',
  'hold.cancelled': 'Pagamento annullato — lo stock è stato rilasciato.',
  'hold.item_error': 'Quantità richiesta non disponibile per {sku} ({available} disponibile)',
  'hold.retry_cta': 'Riprova',

  // SEO
  'seo.home_title': 'Sabaody Island — One Piece TCG Europa | Sigillati Garantiti',
  'seo.home_desc': 'Il tuo negozio di fiducia per One Piece TCG in Europa. Display, buste e mazzi sigillati e verificati. Non pesati. Spedizione rapida in tutta l\'UE.',
  'seo.shop_title': 'Negozio One Piece TCG — Display, Buste, Mazzi | Sabaody',
  'seo.shop_desc': 'Sfoglia tutte le categorie One Piece TCG: display, buste, lotti, mazzi precostituiti e accessori. Prodotti sigillati, consegna rapida in Europa.',
  'seo.cart_title': 'Carrello — Sabaody Island',
  'seo.cart_desc': 'Controlla gli articoli One Piece TCG nel carrello prima del pagamento sicuro su Sabaody Island.',
  'seo.account_title': 'Il mio account — Sabaody Island',
  'seo.account_desc': 'Gestisci il tuo account Sabaody Island: ordini, indirizzi, Berry e fedeltà One Piece TCG.',
  'seo.checkout_title': 'Pagamento sicuro — Sabaody Island',
  'seo.checkout_desc': 'Completa il tuo ordine One Piece TCG in modo sicuro con spedizione rapida in Europa.',
  'seo.category_desc': 'Acquista {category} One Piece TCG sigillati. Prodotti verificati, non pesati, prezzi competitivi. Spedizione rapida in Europa.',
  // Per-category SEO
  'seo.cat.displays_title': 'Display One Piece TCG — Booster Box sigillate | Sabaody',
  'seo.cat.displays_desc': 'Acquista display (booster box) One Piece TCG sigillate e verificate. JP, EN, FR disponibili. Spedizione rapida in Europa.',
  'seo.cat.boosters_title': 'Buste One Piece TCG — Pack sigillati singoli | Sabaody',
  'seo.cat.boosters_desc': 'Buste One Piece TCG singole, sigillate e non tracciate. Tutte le edizioni OP e EB disponibili. Spedizione rapida in UE.',
  'seo.cat.case_title': 'Case One Piece TCG — Cartoni di display sigillati | Sabaody',
  'seo.cat.case_desc': 'Cartoni completi (case) di display One Piece TCG sigillati. Ideale per collezionisti e rivenditori. Spedizione sicura in Europa.',
  'seo.cat.binder_title': 'Raccoglitori One Piece TCG | Sabaody',
  'seo.cat.binder_desc': 'Raccoglitori e portfolio per la tua collezione One Piece TCG. Proteggi le tue carte con stile. Spedizione in Europa.',
  'seo.cat.acrylic-protectors_title': 'Protezioni Acriliche One Piece TCG | Sabaody',
  'seo.cat.acrylic-protectors_desc': 'Custodie acriliche per proteggere i tuoi display e booster box One Piece TCG. Qualità premium, spedizione in Europa.',
  'seo.cat.decks_title': 'Mazzi Precostituiti One Piece TCG | Sabaody',
  'seo.cat.decks_desc': 'Mazzi precostituiti One Piece TCG sigillati, pronti da giocare. Tutte le edizioni disponibili. Spedizione rapida in Europa.',
  'seo.cat.lots_title': 'Full Set One Piece TCG — Collezioni complete | Sabaody',
  'seo.cat.lots_desc': 'Full set One Piece TCG: collezioni complete pre-ordinate per collezionare facilmente. Spedizione in Europa.',
  'seo.cat.displays_intro': 'Su <strong>Sabaody Island</strong>, trovi <strong>display One Piece TCG (booster box)</strong> per <strong>aprire</strong>, <strong>collezionare</strong> o preparare un <strong>deck competitivo</strong>.<br/><ul><li><strong>Edizioni</strong>: OP, EB, PRB</li><li><strong>Lingue</strong>: EN / FR / JPN (in base alla disponibilità)</li><li><strong>Sigillati</strong>: <strong>non tracciati / non pesati</strong></li><li><strong>Stock</strong>: aggiornato in tempo reale</li></ul>Che tu stia cercando OP15, EB03… o una <strong>booster box One Piece in Europa</strong>, sei nel posto giusto: selezione chiara, varianti per lingua e opzioni di spedizione UE.<br/>Salpa per Sabaody 🏴\u200d☠️',
  'seo.cat.boosters_intro': 'Hai voglia di aprire qualche pack senza prendere un display?<br/><br/>Qui puoi acquistare <strong>booster One Piece TCG</strong> singolarmente. Scegli edizione e lingua <strong>EN / FR / JPN</strong> in base alla disponibilità e completa la collezione al tuo ritmo.<br/><br/>I prodotti sigillati sono <strong>non tracciati / non pesati</strong>, e lo stock viene aggiornato continuamente.<br/><br/>Perfetto per provare un set, migliorare un deck o semplicemente concederti un piacere.<br/><br/>Benvenuto su Sabaody 🏴\u200d☠️',
  'seo.cat.case_intro': 'Per collezionisti e aperture XXL, scopri i <strong>case One Piece TCG</strong>.<br/><br/>Scegli <strong>EN / FR / JPN</strong> quando disponibile.<br/><br/>Come sempre: sigillati <strong>non tracciati / non pesati</strong>, stock in tempo reale.<br/><br/>Perfetto per aprire in grande, mettere da parte sigillati o puntare un set preciso.<br/><br/>Benvenuto su Sabaody 🏴\u200d☠️',
  'seo.cat.decks_intro': 'I <strong>deck preconfezionati One Piece TCG</strong> sono perfetti per giocare subito e imparare.<br/><br/>Scegli <strong>EN / FR / JPN</strong> in base alla disponibilità.<br/><br/>Che tu sia nuovo o competitivo, è il modo più semplice per iniziare!<br/><br/>Benvenuto su Sabaody 🏴\u200d☠️',
  'seo.cat.lots_intro': 'Vuoi collezionare senza comprare display?<br/><br/>I nostri <strong>full set One Piece TCG</strong> fanno per te.<br/><br/>Sono <strong>pre-ordinati</strong> per iniziare una collezione e puntare a un <strong>master set</strong> più facilmente.<br/><br/>Perfetto per risparmiare tempo, completare un set o regalare un bundle "chiavi in mano".<br/><br/>Benvenuto su Sabaody 🏴\u200d☠️',
  'seo.cat.binder_intro': 'Vuoi organizzare la tua collezione in modo ordinato e farla crescere con tranquillità?<br/><br/>Scopri i nostri <strong>raccoglitori One Piece TCG</strong> pensati per collezionisti e giocatori.<br/><br/>Saranno disponibili diversi formati: 9 tasche, 12 tasche e formato Top Loader per proteggere le tue carte più preziose.<br/><br/>Ogni raccoglitore è personalizzato per edizione, così puoi organizzare la collezione come un professionista!<br/><br/>Benvenuto su Sabaody 🏴\u200d☠️',
  'seo.cat.sleeves_intro': 'Vuoi proteggere il tuo deck e le tue carte preferite?<br/><br/>Scopri le nostre <strong>bustine One Piece TCG</strong> per evitare l\'usura.<br/><br/>Troverai bustine formato standard, finiture piacevoli e una protezione affidabile ogni giorno.<br/><br/>Indispensabili per giocare comodamente e mantenere le carte in perfette condizioni.<br/><br/>Benvenuto su Sabaody 🏴\u200d☠️',
  'seo.cat.acrylic-protectors_intro': 'Vuoi mettere in mostra i tuoi display e deck sigillati come veri tesori?<br/><br/>Scopri i nostri <strong>protettori acrilici</strong> per esporre, mettere al sicuro e preservare i pezzi importanti.<br/><br/>È l\'opzione premium contro urti, polvere e manipolazioni, mantenendo un look pulito e "collector".<br/><br/>Il dettaglio su misura che fa brillare la tua collezione.<br/><br/>Benvenuto su Sabaody 🏴\u200d☠️',
  'seo.cat.storage-boxes_title': 'Scatole di Stoccaggio One Piece TCG | Sabaody',
  'seo.cat.storage-boxes_desc': 'Deck box, scatole di smistamento e soluzioni di stoccaggio per le tue carte One Piece TCG. Organizzazione e protezione quotidiana. Spedizione in Europa.',
  'seo.cat.storage-boxes_intro': 'Hai bisogno di riporre, ordinare o trasportare le tue carte?<br/><br/>Scopri le nostre <strong>scatole di stoccaggio One Piece TCG</strong>: deck box, scatole di smistamento e soluzioni di archiviazione per organizzare la tua collezione ogni giorno.<br/><br/>Che tu stia cercando una deck box magnetica, una scatola per ordinare per espansione o uno stoccaggio a lungo termine, troverai ciò di cui hai bisogno.<br/><br/>Benvenuto su Sabaody 🏴\u200d☠️',
  'reviews.title': 'Recensioni clienti',
  'reviews.section_title': 'Recensioni dei clienti',
  'reviews.section_subtitle': 'Trasparenza, qualità e servizio — il tuo feedback conta.',
  'reviews.store_rating': 'Valutazione del negozio',
  'reviews.based_on': 'Basato su {count} recensioni',
  'reviews.no_reviews': 'Nessuna recensione per il momento.',
  'reviews.verified': 'Acquisto verificato',
  'reviews.see_all': 'Vedi tutte le recensioni',
  'reviews.leave_review': 'Lascia una recensione',
  'reviews.page_title': 'Recensioni clienti — Sabaody Island',
  'reviews.page_desc': 'Leggi le recensioni dei nostri clienti. Trasparenza e qualità garantite.',
  'reviews.form_title': 'Lascia una recensione',
  'reviews.form_name': 'Il tuo nome',
  'reviews.form_name_placeholder': 'Es: Luffy',
  'reviews.form_country': 'Paese',
  'reviews.form_comment': 'La tua recensione',
  'reviews.form_comment_placeholder': 'Condividi la tua esperienza (min. 10 caratteri)...',
  'reviews.form_submit': 'Invia la mia recensione',
  'reviews.form_success': 'Grazie per la tua recensione! Sarà visibile dopo la moderazione.',
  'reviews.form_error': 'Si è verificato un errore. Riprova.',
  'reviews.form_rate_limit': 'Hai già inviato una recensione di recente. Riprova tra 24 ore.',
  'reviews.form_login_required': 'Accedi per lasciare una recensione.',
  'reviews.login_to_review': 'Accedi per recensire il tuo ordine',
  'reviews.login_to_review_desc': 'Solo i clienti che hanno effettuato un ordine possono lasciare una recensione.',
  'reviews.login_cta': 'Accedi',
  'reviews.no_eligible_orders': 'Nessun ordine idoneo',
  'reviews.no_eligible_orders_desc': 'Solo i clienti che hanno ricevuto un ordine possono lasciare una recensione.',
  'reviews.all_orders_reviewed': 'Tutte le recensioni inviate!',
  'reviews.all_orders_reviewed_desc': 'Hai già recensito tutti i tuoi ordini idonei.',
  'reviews.form_title_order': 'Recensisci il tuo ordine',
  'reviews.select_order': 'Ordine',
  'reviews.select_order_placeholder': 'Seleziona un ordine…',
  'seo.product.booster_box': '{setCode} Booster Box ({lang}) — {productName} | Sabaody Island',
  'seo.product.booster_pack': '{setCode} Busta ({lang}) — {productName} | Sabaody Island',
  'seo.product.case': '{setCode} Case ({lang}) — {productName} | Sabaody Island',
  'seo.product.deck': '{setCode} Mazzo — {productName} | Sabaody Island',
  'seo.product.default': '{name} | Sabaody Island',
  'seo.product.op15_adventure_kamis_island_booster_box_title': 'OP15 Booster Box (JPN/EN/FR) — Adventure on Kami\'s Island | Sabaody Island',
  'seo.product.op15_adventure_kamis_island_booster_box_desc': 'Booster Box OP15 "Adventure on Kami\'s Island" in JPN/EN/FR (in base alla disponibilità). Sigillato non tracciato, stock UE, spedizione UE.',
  'seo.product_desc.booster_box': 'Acquista la Booster Box {setCode} {productName} ({lang}), sigillata e verificata. Non pesata. Spedizione rapida in Europa.',
  'seo.product_desc.booster_pack': 'Busta singola {setCode} {productName} ({lang}), sigillata e non tracciata. Spedizione rapida in UE.',
  'seo.product_desc.case': 'Case completo {setCode} {productName} ({lang}) sigillato. Ideale per collezionisti e rivenditori. Spedizione sicura in Europa.',
  'seo.product_desc.deck': 'Mazzo precostituito {setCode} {productName}, sigillato e pronto da giocare. Spedizione rapida in Europa.',
  'seo.loyalty_title': 'Programma fedeltà One Piece TCG — Berry & Premi | Sabaody Island',
  'seo.loyalty_desc': 'Guadagna Berry ad ogni acquisto e scambiali con buste gratuite, spedizione gratuita e premi esclusivi.',
  'seo.refund_title': 'Politica di rimborso — Sabaody Island',
  'seo.refund_desc': 'Consulta la nostra politica di rimborso e reso. Reso entro 14 giorni per prodotti sigillati non aperti.',
  'seo.privacy_title': 'Informativa sulla privacy — Sabaody Island',
  'seo.privacy_desc': 'Scopri come Sabaody Island protegge i tuoi dati personali.',
  'seo.terms_title': 'Condizioni generali — Sabaody Island',
  'seo.terms_desc': 'Consulta le nostre condizioni generali di vendita del negozio Sabaody Island.',
  'seo.product_title': '{name} | Sabaody Island',

  // Admin
  'admin.orders': 'Ordini',
  'admin.inventory': 'Inventario',
  'admin.catalog': 'Catalogo',
  'admin.newsletter': 'Newsletter',
  'admin.social_rewards': 'Social rewards',
  'admin.reviews': 'Recensioni',
  'admin.email_center': 'Centro email',
  'admin.new_member_banner': 'Banner nuovi membri',

  'social.follow_us': 'Seguici',
  'social.follow_desc': 'Seguici sui social e guadagna',
  'social.per_platform': 'per piattaforma!',
  'social.handle_instruction': 'Inserisci il tuo nome utente per verificare che ci segui.',
  'social.available': 'Disponibile',
  'social.pending': 'In attesa',
  'social.approved': 'Approvato ✅',
  'social.rejected': 'Rifiutato',
  'social.request_berry': 'Richiedi i miei +5 Berry',
  'social.request_submitted': 'La tua richiesta è in attesa di revisione.',
  'social.request_error': 'Errore nell\'invio della richiesta.',
  'social.handle_prompt': 'Inserisci il tuo nome utente per verificare che ci segui.',
  'social.handle_label': 'Il tuo nome utente su questa piattaforma',
  'social.handle_placeholder': 'mionome',
  'social.cancel': 'Annulla',
  'social.submit_request': 'Invia richiesta',
  'social.login_required': 'Accedi per guadagnare Berry',
  'social.newsletter_label': 'Iscriviti alla newsletter',
  'social.newsletter_updated': 'Preferenza newsletter aggiornata',
  'social.newsletter_active': 'Newsletter attiva ✅',
  'social.newsletter_unsubscribe_hint': 'Per annullare l\'iscrizione, usa il link in fondo alle email della newsletter.',

  // Referral
  'referral.title': 'Inviti',
  'referral.desc': '+50 Berry per te e +50 Berry per il tuo amico/a dopo il suo 1° ordine pagato di almeno 50€.',
  'referral.copy': 'Copia',
  'referral.share': 'Condividi link',
  'referral.copied': 'Codice copiato!',
  'referral.link_copied': 'Link copiato!',
  'referral.share_text': 'Unisciti a Sabaody Island con il mio codice invito e guadagna 50 Berry!',
  'referral.edit_code': 'Modifica il mio codice',
  'referral.save': 'Salva',
  'referral.code_updated': 'Codice aggiornato!',
  'referral.code_locked': 'Codice già modificato',
  'referral.already_changed': 'Hai già modificato il tuo codice una volta.',
  'referral.invalid_format': 'Formato non valido (4-16 caratteri, lettere/numeri/trattini).',
  'referral.code_taken': 'Questo codice è già in uso.',
  'referral.reward_rule': 'per te e il tuo amico/a dopo il suo 1° ordine pagato ≥ 50€',
  'referral.pending': 'In attesa',
  'referral.activated': 'Attivati',
  'referral.total_earned': 'Totale guadagnato',

  // Accessibility
  'a11y.skip_to_content': 'Vai al contenuto',
  'a11y.close': 'Chiudi',
  'a11y.remove_item': 'Rimuovi articolo',
  'a11y.decrease_qty': 'Diminuisci quantità',
  'a11y.increase_qty': 'Aumenta quantità',
  'a11y.show_password': 'Mostra password',
  'a11y.hide_password': 'Nascondi password',
  'a11y.copy': 'Copia',
  'a11y.share': 'Condividi',
  'a11y.cancel': 'Annulla',
  'a11y.search': 'Cerca',
  'a11y.cart_drawer': 'Carrello',
  'a11y.main_navigation': 'Navigazione principale',
  'a11y.open_menu': 'Apri menu',
  'a11y.close_menu': 'Chiudi menu',
  'a11y.mobile_menu': 'Menu di navigazione',
};


// ─── Export ──────────────────────────────────────────────
export const translations: Record<Locale, TranslationDict> = {
  fr,
  'en-gb': enGb,
  de,
  es,
  it,
};

export type TranslationKey = string;
